import React, { useState, useEffect, useCallback, useRef, useContext, createContext } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useNavigate, Link, NavLink, useParams, useLocation } from 'react-router-dom';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ReCAPTCHA from 'react-google-recaptcha';
import { DeviceContext, useDevice } from './context/DeviceContext.js';
import { Modal } from './components/Modal';
import { MikrotikModelOptions } from './components/MikrotikModelOptions.js';
import { PppoeManagementPage } from './components/PppoeManagementPage.js'; 
import { useAuthStore } from './store/authStore.js';
import { ownerApi } from './api/ownerApi.js';
import AutoConfigTab from './components/AutoConfigTab'; // NEW
import { LoadingDisplay } from './components/utils'; // NEW
import { DeviceSettingsPage } from './components/DeviceSettingsPage.js';
import { ModifyAutoConfigTab } from './components/ModifyAutoConfigTab.js'; 
import { HotspotSettingsTab } from './components/HotspotSettingsTab.js'; // Add this import
import { BridgeInterfaces } from './components/BridgeInterfaces.js'; // Add this import
import { createPortal } from 'react-dom';
import { adminApi } from './api/adminApi.js';
import { 
    UploadIcon, ReportIcon, SalesLogIcon, SettingsIcon, EditIcon, AddIcon,
    BlockIcon, CheckCircleIcon, PrintIcon, DeleteIcon, WifiIcon, MenuIcon, 
    CloseIcon, LogoutIcon, DashboardIcon, HierarchyIcon, MonitorIcon, 
    UsersIcon, ProfileIcon, VendoIcon, VoucherIcon, SalesIcon, ImageIcon, WandIcon, RefreshIcon,
    BackupIcon
} from './components/icons.js';
import {
  Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Filler, Title, Tooltip, Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import HotspotLogsPage from "./components/HotspotLogsPage.js";
import EnterpriseSalesReportPage from './components/EnterpriseSalesReport.js';
ChartJS.register(
  CategoryScale, LinearScale, LineElement, PointElement, Filler, Title, Tooltip, Legend
);
export const SidebarContext = createContext(null);
export const useSidebar = () => useContext(SidebarContext);
const Spinner = () => (
    <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

const ActionPortalMenu = ({ isOpen, anchorRect, onClose, children }) => {
    if (!isOpen || !anchorRect) return null;

    const style = {
        position: 'fixed',
        top: anchorRect.bottom + window.scrollY + 5,
        left: Math.max(10, anchorRect.left - 150),
        zIndex: 9999,
    };

    return createPortal(
        <>
            <div className="fixed inset-0 z-[9998]" onClick={onClose} />
            <div style={style} className="w-48 bg-white rounded-xl shadow-2xl ring-1 ring-black ring-opacity-5 py-1 border border-gray-100 animate-in fade-in zoom-in-95 duration-100 overflow-visible">
                {children}
            </div>
        </>,
        document.body
    );
};

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};
// --- ICONS ---



const axiosInstance = axios.create({ baseURL: '/api' });
axiosInstance.interceptors.request.use((config) => {
    const token = useAuthStore.getState().token;
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    return config;
}, (error) => Promise.reject(error));
const authApi = { login: async (username, password, recaptchaToken) => (await axiosInstance.post('/auth/login', { username, password, recaptchaToken })).data, 
register: async (username, password, recaptchaToken) => (await axiosInstance.post('/auth/register', { username, password, recaptchaToken })).data,
forgotPassword: async (email) => (await axiosInstance.post('/auth/forgot-password', { email })).data,
resetPassword: async (token, newPassword) => (await axiosInstance.post('/auth/reset-password', { token, newPassword })).data,
publicConfig: async () => (await axiosInstance.get('/config/public')).data
};
//const authApi = { login: async (username, password) => (await axiosInstance.post('/auth/login', { username, password })).data };

const useRecaptchaEnabled = () => {
    const [enabled, setEnabled] = useState(true);

    useEffect(() => {
        let active = true;
        authApi.publicConfig()
            .then((config) => {
                if (active) setEnabled(config.recaptchaEnabled !== false);
            })
            .catch(() => {
                if (active) setEnabled(true);
            });
        return () => { active = false; };
    }, []);

    return enabled;
};


const mikrotikApi = {
    getStatus: async (mikrotikId) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/status`)).data,
    getSales: async (mikrotikId) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/sales`)).data,
    getUserStats: async (mikrotikId) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/user-stats`)).data,
    getHardwareStats: async (mikrotikId) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/hardware-stats`)).data,
    getLiveDailySales: async (mikrotikId) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/live-daily-sales`)).data,
    getActiveUsers: async (mikrotikId) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/hotspot/users/active`)).data,
    getBannedUsers: async (mikrotikId) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/hotspot/users/banned`)).data,
    kickUser: async (mikrotikId, userId) => (await axiosInstance.post(`/mikrotik/${mikrotikId}/hotspot/users/kick`, { userId })).data,
    bypassUser: (mikrotikId, userId, macAddress) =>
  axiosInstance.post(`/mikrotik/${mikrotikId}/hotspot/users/bypass`, { userId, macAddress })
    .then(r => r.data),
     getBypassedUsers: async (mikrotikId) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/hotspot/users/bypassed`)).data,
    unbypassUser: async (mikrotikId, bindingId) => (await axiosInstance.post(`/mikrotik/${mikrotikId}/hotspot/users/unbypass`, { bindingId })).data,
    banUser: async (mikrotikId, userId) => (await axiosInstance.post(`/mikrotik/${mikrotikId}/hotspot/users/ban`, { userId })).data,
    unbanUser: async (mikrotikId, bindingId) => (await axiosInstance.post(`/mikrotik/${mikrotikId}/hotspot/users/unban`, { bindingId })).data,
    syncVouchers: async (mikrotikId) => (await axiosInstance.post(`/mikrotik/${mikrotikId}/sync-vouchers`)).data,
    //getHotspotUsers: async (mikrotikId) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/hotspot/users`)).data,
    deleteHotspotUser: async (mikrotikId, nameToDelete) => (await axiosInstance.post(`/mikrotik/${mikrotikId}/hotspot/users/delete`, { userId: nameToDelete })).data,
    getDefaultProfileRate: async (mikrotikId) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/hotspot/profile/default`)).data,
    updateDefaultProfileRate: async (mikrotikId, rates) => (await axiosInstance.put(`/mikrotik/${mikrotikId}/hotspot/profile/default`, rates)).data,
    getHotspotUsers: async (mikrotikId, page = 1) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/hotspot/users?page=${page}`)).data,
    // --- NEW BACKUP METHODS ---
    getBackups: async (mikrotikId) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/backups`)).data,
    createBackup: async (mikrotikId, comment) => (await axiosInstance.post(`/mikrotik/${mikrotikId}/backups`, { comment })).data,
    restoreBackup: async (mikrotikId, backupId) => (await axiosInstance.post(`/mikrotik/${mikrotikId}/backups/${backupId}/restore`)).data,
    deleteBackup: async (mikrotikId, backupId) => (await axiosInstance.delete(`/mikrotik/${mikrotikId}/backups/${backupId}`)).data,
    getUnauthenticatedHosts: async (mikrotikId) => (await axiosInstance.get(`/mikrotik/${mikrotikId}/hotspot/hosts/unauthenticated`)).data,
    bypassHostByMac: async (mikrotikId, macAddress) => (await axiosInstance.post(`/mikrotik/${mikrotikId}/hotspot/hosts/bypass`, { macAddress })).data,
    // New Profile methods needed for the implementation
    // Ensure this exact name is used
    getVouchers: async (mikrotikId, page = 1, batchName = '') => {
        // Ensure batchName is passed to the backend 
        const url = `/owner/mikrotiks/${mikrotikId}/vouchers?page=${page}&batchName=${batchName}`;
        return (await axiosInstance.get(url)).data;
    },
    getUserProfiles: async (mikrotikId) => 
        (await axiosInstance.get(`/owner/mikrotiks/${mikrotikId}/user-profiles`)).data,
    
    createUserProfile: async (mikrotikId, data) => 
        (await axiosInstance.post(`/owner/mikrotiks/${mikrotikId}/user-profiles`, data)).data,
    
    deleteUserProfile: async (profileId) => 
        (await axiosInstance.delete(`/owner/user-profiles/${profileId}`)).data,
    updateUserProfile: async (profileId, data) =>
  (await axiosInstance.put(`/owner/user-profiles/${profileId}`, data)).data,
    syncUserProfiles: async (mikrotikId, strict = false) =>
  (await axiosInstance.post(`/owner/mikrotiks/${mikrotikId}/user-profiles/sync`, { strict })).data,


};

const pingerApi = {
    pingNow: async (data) => (await axiosInstance.post('/pinger/ping', data)).data,
};

const salesReportApi = {
    getReport: async (mikrotikId, period) => (await axiosInstance.get('/sales-report', {
        params: { mikrotikId, ...(period ? { period } : {}) },
    })).data,
    syncReport: async (mikrotikId) => (await axiosInstance.post('/sales-report/sync', { mikrotikId })).data,
};

const dashboardApi = {
    getStats: async (mikrotikId) => (await axiosInstance.get(`/dashboard/stats?mikrotikId=${mikrotikId}`)).data,
    getSalesStats: async (mikrotikId) => (await axiosInstance.get(`/dashboard/sales-stats?mikrotikId=${mikrotikId}`)).data,
    syncStats: async (mikrotikId) => (await axiosInstance.post('/dashboard/sync-stats', { mikrotikId })).data,
};

const vendoApi = {
    syncSales: async (mikrotikId) => (await axiosInstance.post('/vendo/sync-sales', { mikrotikId })).data,
    getSales: async (sortBy = 'createdAt', mikrotikId, searchTerm = '', page = 1) => (await axiosInstance.get(`/vendo/sales?sortBy=${sortBy}&mikrotikId=${mikrotikId}&searchTerm=${searchTerm}&page=${page}`)).data,
    getUptime: async (vendoId) => (await axiosInstance.get(`/vendo/${vendoId}/uptime`)).data,
};


// --- CORE LAYOUT COMPONENTS ---
// App.js

const BackupsPage = () => {
    const { user } = useAuthStore();
    const [mikrotiks, setMikrotiks] = useState([]);
    const [selectedMikrotikId, setSelectedMikrotikId] = useState('');
    const [backups, setBackups] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const { register, handleSubmit, reset } = useForm();

    const api = user.role === 'admin' ? adminApi : ownerApi;
    const deviceApi = user.role === 'admin' ? adminApi.getAllMikrotiks : ownerApi.getMikrotiks;

    useEffect(() => {
        setIsLoading(true);
        deviceApi().then(devices => {
            setMikrotiks(devices || []);
            if (devices && devices.length > 0) setSelectedMikrotikId(devices[0].id);
        }).catch(() => toast.error("Failed to load device list."))
          .finally(() => setIsLoading(false));
    }, [user.role]);

    const fetchBackups = useCallback(() => {
        if (!selectedMikrotikId) return;
        setIsLoading(true);
        mikrotikApi.getBackups(selectedMikrotikId)
            .then(setBackups)
            .catch(() => toast.error("Failed to load backups."))
            .finally(() => setIsLoading(false));
    }, [selectedMikrotikId]);

    useEffect(() => { fetchBackups(); }, [fetchBackups]);

    const handleCreate = async (data) => {
    if (!selectedMikrotikId) return;

    // Find the device object to check its current status
    const targetDevice = mikrotiks.find(m => m.id.toString() === selectedMikrotikId.toString());

    // Check if the device is offline
    if (!targetDevice || !targetDevice.isOnline) {
        toast.error("Cannot create backup: The device is currently offline.");
        return;
    }

    setIsCreating(true);
    toast.promise(mikrotikApi.createBackup(selectedMikrotikId, data.comment), {
        loading: 'Creating backup...',
        success: () => { 
            fetchBackups(); 
            reset(); 
            return 'Backup created successfully!'; 
        },
        error: (err) => err.response?.data?.message || 'Backup failed.'
    }).finally(() => setIsCreating(false));
};

    const handleRestore = async (backup) => {
        if (window.confirm(`WARNING: Restore backup from ${new Date(backup.createdAt).toLocaleString()} and REBOOT?`)) {
            toast.promise(mikrotikApi.restoreBackup(selectedMikrotikId, backup.id), {
                loading: 'Restoring...', success: 'Restore sent! Rebooting...', error: (err) => err.response?.data?.message || 'Restore failed.'
            });
        }
    };

    const handleDelete = async (backup) => {
        if (window.confirm('Delete this backup?')) {
            toast.promise(mikrotikApi.deleteBackup(selectedMikrotikId, backup.id), {
                loading: 'Deleting...', success: () => { fetchBackups(); return 'Deleted.'; }, error: 'Failed to delete.'
            });
        }
    };

    return (
        <div>
            <h1 className="text-2xl font-bold mb-6">System Backups</h1>
            <div className="flex gap-4 p-4 bg-white rounded-lg shadow-md mb-6">
                <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-600 mb-1">Select Device</label>
                    <select value={selectedMikrotikId} onChange={e => setSelectedMikrotikId(e.target.value)} className="input-class">
                        {mikrotiks.map(m => <option key={m.id} value={m.id}>{m.deviceName}</option>)}
                    </select>
                </div>
            </div>
            {selectedMikrotikId && (
                <>
                    <form onSubmit={handleSubmit(handleCreate)} className="bg-gray-50 p-4 rounded-lg mb-6 flex gap-4 items-end">
                        <div className="flex-grow">
                            <label className="text-sm font-medium text-gray-700">New Backup Comment</label>
                            <input type="text" placeholder="e.g. Before changes" className="input-class w-full" {...register('comment')} />
                        </div>
                        <button type="submit" disabled={isCreating} className="btn-primary h-10">{isCreating ? 'Creating...' : 'Create Backup'}</button>
                    </form>
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50"><tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comment</th><th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th></tr></thead>
                            <tbody className="divide-y divide-gray-200">
                                {backups.map(b => (
                                    <tr key={b.id}>
                                        <td className="px-6 py-4 text-sm text-gray-900">{new Date(b.createdAt).toLocaleString()}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500">{b.comment || '-'}</td>
                                        <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                                            <button onClick={() => handleRestore(b)} className="text-blue-600 hover:text-blue-900">Restore</button>
                                            <button onClick={() => handleDelete(b)} className="text-red-600 hover:text-red-900">Delete</button>
                                        </td>
                                    </tr>
                                ))}
                                {backups.length === 0 && <tr><td colSpan="3" className="px-6 py-4 text-center text-gray-500">No backups found.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

const BackupsTab = () => {
    // This is a wrapper to use the Backups page inside the device manager
    return <BackupsPage />;
};

const TelegramTab = () => {
    // 1. All hooks are now called unconditionally at the top
    const { device, api } = useDevice();
    const { register, handleSubmit, reset, watch, formState: { isSubmitting, isDirty } } = useForm({
        defaultValues: {
            enabled: false,
            botToken: '',
            chatId: '',
            model: ''
        }
    });
    const [isLoading, setIsLoading] = useState(true);
    const isEnabled = watch('enabled');

    useEffect(() => {
        // Only run the effect if the data from the context is ready
        if (!api || !device) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        api.getTelegramSettings(device.id)
            .then(data => {
                reset({
                    enabled: data.telegramEnabled,
                    botToken: data.telegramBotToken,
                    chatId: data.telegramChatId,
                    model: ''
                });
            })
            .catch(() => toast.error('Failed to load current Telegram settings.'))
            .finally(() => setIsLoading(false));
    }, [device, api, reset]);

    const onSubmit = async (data) => {
        if (!api || !device) return toast.error("Cannot save settings, device data not available.");

        const settings = {
            telegramEnabled: data.enabled,
            telegramBotToken: data.botToken,
            telegramChatId: data.chatId,
            model: data.model
        };
        await toast.promise(
            api.updateTelegramSettings(device.id, settings),
            {
                loading: 'Saving settings and pushing script to MikroTik...',
                success: (res) => {
                    reset(data); // Resets the dirty state
                    return res.message;
                },
                error: (err) => err.response?.data?.message || 'Failed to save settings.',
            }
        );
    };

    // 2. Conditional returns now happen AFTER all hooks have been called
    if (!device) {
        return <LoadingDisplay message="Loading device data..." />;
    }

    if (isLoading) return <LoadingDisplay message="Loading Telegram settings..." />;

    // 3. The final component render
    return (
        <div className="max-w-lg">
            <h2 className="text-xl font-semibold mb-4">Telegram Sales Alerts</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-gray-50 p-6 rounded-lg shadow-inner">
                <div className="flex items-center justify-between">
                    <label htmlFor="enabled" className="text-lg font-medium text-gray-800">Enable Telegram Alerts</label>
                    <div className="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
                        <input type="checkbox" id="enabled" className="toggle-checkbox absolute block w-7 h-7 rounded-full bg-white border-4 appearance-none cursor-pointer" {...register('enabled')} />
                        <label htmlFor="enabled" className="toggle-label block overflow-hidden h-7 rounded-full bg-gray-300 cursor-pointer"></label>
                    </div>
                </div>

                <div className={`space-y-4 ${!isEnabled && 'opacity-50'}`}>
                    <div>
                        <label htmlFor="botToken" className="block text-sm font-bold text-gray-700 mb-1">Bot Token</label>
                        <input id="botToken" type="text" placeholder="Enter your Telegram bot token" className="input-class w-full" {...register('botToken', { required: isEnabled })} disabled={!isEnabled} />
                    </div>
                    <div>
                        <label htmlFor="chatId" className="block text-sm font-bold text-gray-700 mb-1">Chat ID</label>
                        <input id="chatId" type="text" placeholder="Enter your user or group Chat ID" className="input-class w-full" {...register('chatId', { required: isEnabled })} disabled={!isEnabled} />
                    </div>
                    <div>
                        <label htmlFor="model" className="block text-sm font-bold text-gray-700 mb-1">Device Model</label>
                        <select id="model" className="input-class w-full" {...register('model', { required: isEnabled })} disabled={!isEnabled}>
                            <MikrotikModelOptions placeholder="-- Select model --" />
                        </select>
                        <p className="text-xs text-gray-500 mt-1">This is required to set the correct script path.</p>
                    </div>
                </div>
                
                <div className="text-right">
                    <button type="submit" className="btn-primary" disabled={isSubmitting || !isDirty}>
                        {isSubmitting ? 'Saving...' : 'Save and Push Script'}
                    </button>
                </div>
            </form>
        </div>
    );
};

const ForgotPasswordPage = () => {
    const { register, handleSubmit, formState: { isSubmitting } } = useForm();
    const [message, setMessage] = useState('');

    const onSubmit = async (data) => {
        try {
            const res = await authApi.forgotPassword(data.email);
            setMessage(res.message);
            toast.success('Check your email!');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Request failed.');
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
            <div className="w-full max-w-sm bg-white shadow-lg rounded-lg p-8 border border-gray-200">
                <h1 className="text-2xl font-bold text-center mb-4">Reset Password</h1>
                {message ? (
                    <div className="bg-green-100 text-green-700 p-4 rounded text-center">
                        <p>{message}</p>
                        <Link to="/login" className="block mt-4 text-blue-600 hover:underline">Back to Login</Link>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <p className="text-gray-600 text-sm mb-4 text-center">Enter your email address and we'll send you a link to reset your password.</p>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-bold mb-2">Email Address</label>
                            <input type="email" className="input-class w-full" {...register('email', { required: true })} />
                        </div>
                        <button className="btn-primary w-full" disabled={isSubmitting}>
                            {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                        </button>
                        <div className="mt-4 text-center">
                            <Link to="/login" className="text-sm text-blue-600 hover:underline">Back to Login</Link>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

const ResetPasswordPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm();
    const password = watch("password");

    const onSubmit = async (data) => {
        try {
            await authApi.resetPassword(token, data.password);
            toast.success("Password reset successful! Please login.");
            navigate('/login');
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to reset password.");
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
            <div className="w-full max-w-sm bg-white shadow-lg rounded-lg p-8 border border-gray-200">
                <h1 className="text-2xl font-bold text-center mb-6">Set New Password</h1>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2">New Password</label>
                        <input type="password" className="input-class w-full" {...register('password', { required: "Password is required", minLength: { value: 6, message: "Min 6 chars" } })} />
                        {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
                    </div>
                    <div className="mb-6">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Confirm Password</label>
                        <input type="password" className="input-class w-full" {...register('confirmPassword', { required: true, validate: val => val === password || "Passwords do not match" })} />
                        {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>}
                    </div>
                    <button className="btn-primary w-full" disabled={isSubmitting}>
                        {isSubmitting ? 'Resetting...' : 'Reset Password'}
                    </button>
                </form>
            </div>
        </div>
    );
};
const VendoSalesPage = () => {
    const { user } = useAuthStore();
    const [sales, setSales] = useState([]);
    const [mikrotiks, setMikrotiks] = useState([]);
    const [selectedMikrotik, setSelectedMikrotik] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [sortBy, setSortBy] = useState('createdAt');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    useEffect(() => {
        const fetchDevices = user.role === 'admin' ? adminApi.getAllMikrotiks : ownerApi.getMikrotiks;
        fetchDevices().then(devices => {
            setMikrotiks(devices);
            if (devices && devices.length > 0) {
                setSelectedMikrotik(devices[0].id);
            } else {
                setIsLoading(false);
            }
        }).catch(err => {
            toast.error("Could not load device list.");
            setIsLoading(false);
        });
    }, [user.role]);

    const fetchSales = useCallback(() => {
        if (!selectedMikrotik) {
            setSales([]);
            setTotalPages(0);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        vendoApi.getSales(sortBy, selectedMikrotik, debouncedSearchTerm, currentPage)
            .then(data => {
                setSales(data.sales || []);
                setTotalPages(data.totalPages || 0);
            })
            .catch(err => toast.error("Failed to load sales data."))
            .finally(() => setIsLoading(false));
    }, [sortBy, selectedMikrotik, debouncedSearchTerm, currentPage]);

    useEffect(() => {
        fetchSales();
    }, [fetchSales]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, selectedMikrotik, sortBy]);

    const handleSync = async () => {
        if (!selectedMikrotik) {
            toast.error("Please select a MikroTik device to sync.");
            return;
        }
        setIsSyncing(true);
        try {
            const result = await toast.promise(
                vendoApi.syncSales(selectedMikrotik),
                {
                    loading: 'Syncing sales from MikroTik...',
                    success: (res) => res.message || 'Sync complete!',
                    error: (err) => err.response?.data?.message || 'Sync failed.',
                }
            );
            toast.success(`Synced ${result.newUsersAdded} new user sales.`);
            fetchSales();
        } finally {
            setIsSyncing(false);
        }
    };

    return (
        <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Vendo Sales Log</h1>
            <div className="flex flex-col sm:flex-row flex-wrap items-center gap-4 p-4 bg-white rounded-lg shadow-md mb-6">
                <div className="flex flex-col">
                    <label htmlFor="device-select-log" className="mb-1 text-sm font-medium text-gray-600">Device</label>
                    <select 
                        id="device-select-log"
                        value={selectedMikrotik} 
                        onChange={e => setSelectedMikrotik(e.target.value)}
                        className="bg-gray-50 border border-gray-300 text-gray-900 rounded-md w-full sm:w-auto p-2"
                    >
                        <option value="">-- Select a Device --</option>
                        {mikrotiks.map(m => <option key={m.id} value={m.id}>{m.deviceName}</option>)}
                    </select>
                </div>
                <button 
                    onClick={handleSync} 
                    disabled={isSyncing || !selectedMikrotik}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md w-full sm:w-auto self-end disabled:opacity-50"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181-3.181m0-4.992v4.992m0 0h-4.992m4.993 0-3.181 3.181m2.985-2.985h-4.992m0 0v4.992m0-4.992 3.181 3.181" /></svg>
                    {isSyncing ? 'Syncing...' : 'Refresh & Sync Data'}
                </button>
                <div className="flex-grow"></div>
                <input
                    type="text"
                    placeholder="Search by Code, Vendo, or MAC..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="bg-gray-50 border border-gray-300 text-gray-900 rounded-md w-full sm:w-64 p-2 self-end"
                />
                <div className="flex flex-col">
                    <label htmlFor="sort-by-log" className="mb-1 text-sm font-medium text-gray-600">Sort By</label>
                    <select 
                        id="sort-by-log"
                        value={sortBy} 
                        onChange={e => setSortBy(e.target.value)}
                        className="bg-gray-50 border border-gray-300 text-gray-900 rounded-md w-full sm:w-auto p-2"
                    >
                        <option value="createdAt">Sort by Date</option>
                        <option value="vendoName">Sort by Vendo Name</option>
                        <option value="price">Sort by Price</option>
                    </select>
                </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md overflow-x-auto">
                {isLoading ? <p className="p-8 text-center text-gray-500">Loading sales...</p> : (
                <table className="min-w-full">
                    <thead className="bg-gray-50"><tr>
                        <th className="px-4 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Code</th>
                        <th className="px-4 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendo Name</th>
                        <th className="px-4 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                        <th className="px-4 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Validity</th>
                        <th className="px-4 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MAC Address</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-200">
                        {sales.map(sale => (
                            <tr key={sale.id}>
                                <td className="px-4 py-4 sm:px-6 whitespace-nowrap text-sm text-gray-600">{new Date(sale.createdAt).toISOString().slice(0, 16).replace('T', ' ')}</td>
                                <td className="px-4 py-4 sm:px-6 whitespace-nowrap font-mono">{sale.code}</td>
                                <td className="px-4 py-4 sm:px-6 whitespace-nowrap">{sale.vendoName}</td>
                                <td className="px-4 py-4 sm:px-6 whitespace-nowrap">₱{sale.price.toFixed(2)}</td>
                                <td className="px-4 py-4 sm:px-6 whitespace-nowrap">{sale.validity}</td>
                                <td className="px-4 py-4 sm:px-6 whitespace-nowrap font-mono">{sale.macAddress}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                )}
            </div>
            <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />
        </div>
    );
};
const SidebarNavLink = ({ to, icon, children }) => {
    const { setSidebarOpen } = useSidebar(); // Access context here

    return (
        <NavLink
            to={to}
            onClick={() => {
                if (window.innerWidth < 768) setSidebarOpen(false);
            }}
            className={({ isActive }) =>
                `group flex min-h-9 items-center gap-2.5 rounded-lg border px-3 py-2 text-[13px] font-medium transition-all ${
                    isActive
                        ? 'border-white/10 bg-white/10 text-white shadow-sm'
                        : 'border-transparent text-gray-400 hover:bg-white/[0.06] hover:text-white'
                }`
            }
        >
            {icon}
            {children}
        </NavLink>
    );
};

const StatCard = ({ title, value, icon, color, onClick, actionLabel }) => {
    // Color mapping for a professional light theme
    const colorStyles = {
        'border-blue-500': { bg: 'bg-blue-50', text: 'text-blue-600' },
        'border-green-500': { bg: 'bg-emerald-50', text: 'text-emerald-600' },
        'border-yellow-500': { bg: 'bg-amber-50', text: 'text-amber-600' },
        'border-red-500': { bg: 'bg-red-50', text: 'text-red-600' },
        'border-cyan-500': { bg: 'bg-cyan-50', text: 'text-cyan-600' },
        'border-purple-500': { bg: 'bg-violet-50', text: 'text-violet-600' },
    };
    const styles = colorStyles[color] || { bg: 'bg-gray-100', text: 'text-gray-600' };

    const Component = onClick ? 'button' : 'div';

    return (
        <Component
            {...(onClick ? { type: 'button', onClick, 'aria-label': actionLabel || `View ${title}` } : {})}
            className={`w-full rounded-xl border border-slate-200 bg-white p-3.5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${onClick ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/30' : ''}`}
        >
            <div className="flex items-center gap-3">
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${styles.bg} ${styles.text}`}>
                    {icon}
                </div>
                <div className="min-w-0">
                    <p className="truncate text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">{title}</p>
                    <p className="mt-0.5 text-xl font-bold tracking-tight text-slate-950">{value}</p>
                </div>
                {onClick && <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-blue-600">View list</span>}
            </div>
        </Component>
    );
};

const MainDashboard = () => {
    const { user } = useAuthStore();
    const [stats, setStats] = useState(null);
    const [mikrotiks, setMikrotiks] = useState([]);
    const [selectedMikrotik, setSelectedMikrotik] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const api = user.role === 'admin' ? adminApi : ownerApi;

    useEffect(() => {
        const fetchDevices = user.role === 'admin' ? adminApi.getAllMikrotiks : ownerApi.getMikrotiks;
        fetchDevices().then(devices => {
            setMikrotiks(devices);
            if (devices && devices.length > 0) {
                setSelectedMikrotik(devices[0].id);
            } else {
                setIsLoading(false);
            }
        }).catch(err => {
            toast.error("Could not load device list.");
            setIsLoading(false);
        });
    }, [user.role]);

    const fetchStats = useCallback(async () => {
        if (!selectedMikrotik) {
            setStats(null);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);

        try {
            // Render database-backed sales immediately. Router hardware and user
            // statistics can take several seconds over a tunnel, so load them
            // without holding the entire dashboard spinner open.
            const salesData = await dashboardApi.getSalesStats(selectedMikrotik);
            setStats({ ...salesData, liveStatsLoading: true });
            setIsLoading(false);

            try {
                const liveData = await dashboardApi.getStats(selectedMikrotik);
                setStats(prevStats => ({
                    ...(prevStats || {}),
                    ...liveData,
                    liveStatsLoading: false,
                }));
            } catch (liveError) {
                console.error("Live Dashboard Stats Error:", liveError);
                setStats(prevStats => ({
                    ...(prevStats || {}),
                    liveStatsLoading: false,
                    liveStatsError: true,
                }));
            }

            // Refresh router counters in the background without blocking the UI.
            dashboardApi.syncStats(selectedMikrotik).then(freshSales => {
                setStats(prevStats => ({
                    ...(prevStats || {}),
                    salesDaily: freshSales.salesDaily,
                    salesWeekly: freshSales.salesWeekly,
                    salesMonthly: freshSales.salesMonthly,
                    salesYearly: freshSales.salesYearly,
                }));
            }).catch(error => {
                console.error("Initial sales sync failed:", error);
            });

        } catch (error) {
            console.error("Dashboard Load Error:", error);
            // Only show error toast if we don't have any data yet
            setStats(prev => {
                if (!prev) toast.error("Failed to load dashboard statistics.");
                return prev;
            });
        } finally {
            setIsLoading(false);
        }
    }, [selectedMikrotik]);

    useEffect(() => {
        if (selectedMikrotik) {
            fetchStats();
        }
    }, [selectedMikrotik, fetchStats]);

    useEffect(() => {
        if (!selectedMikrotik || !stats) return;
        const intervalId = setInterval(async () => {
            try {
                const hardwareData = await mikrotikApi.getHardwareStats(selectedMikrotik);
                setStats(prevStats => ({ ...(prevStats || {}), systemStats: hardwareData }));
            } catch (error) {
                console.error("Failed to poll for hardware stats:", error);
            }
        }, 5000);
        return () => clearInterval(intervalId);
    }, [selectedMikrotik, stats]);

    useEffect(() => {
        if (!selectedMikrotik || !stats) return;
        const intervalId = setInterval(async () => {
            try {
                const userStatsData = await mikrotikApi.getUserStats(selectedMikrotik);
                setStats(prevStats => ({
                    ...(prevStats || {}),
                    ...userStatsData,
                    liveStatsError: false,
                }));
            } catch (error) {
                console.error("Failed to poll for user stats:", error);
            }
        }, 5000);
        return () => clearInterval(intervalId);
    }, [selectedMikrotik, stats]);

    useEffect(() => {
        if (!selectedMikrotik || !stats) return;
        const intervalId = setInterval(async () => {
            try {
                const salesData = await dashboardApi.syncStats(selectedMikrotik);
                setStats(prevStats => ({
                    ...(prevStats || {}),
                    salesDaily: salesData.salesDaily,
                    salesWeekly: salesData.salesWeekly,
                    salesMonthly: salesData.salesMonthly,
                    salesYearly: salesData.salesYearly,
                }));
            } catch (error) {
                console.error("Failed to poll for sales data:", error);
            }
        }, 10000);

        return () => clearInterval(intervalId);
    }, [selectedMikrotik, stats]);

    const handleResetSales = async () => {
        if (!selectedMikrotik) return;
        if (window.confirm(`WARNING:\n\nYou are about to reset ALL historical sales data for this device.\n\nTHIS WILL PERMANENTLY DELETE ALL TRANSACTION LOGS for this MikroTik.\n\nAre you sure you want to continue?`)) {
            await toast.promise(
                api.resetOverallSales(selectedMikrotik),
                {
                    loading: 'Resetting all sales data & transaction logs...',
                    success: (res) => {
                        fetchStats(); 
                        return res.message || 'Sales and logs reset successfully!';
                    },
                    error: (err) => err.response?.data?.message || 'Failed to reset sales.',
                }
            );
        }
    };

    const formatSales = value => `₱${Number(value || 0).toLocaleString('en-PH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

    const dashboardControls = (
        <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0 flex-1">
                <label htmlFor="device-select-dashboard" className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Reporting device
                </label>
                <select
                    id="device-select-dashboard"
                    value={selectedMikrotik}
                    onChange={event => setSelectedMikrotik(event.target.value)}
                    className="w-full sm:max-w-xs"
                    disabled={mikrotiks.length === 0}
                >
                    {mikrotiks.length > 0 ? (
                        mikrotiks.map(mikrotik => <option key={mikrotik.id} value={mikrotik.id}>{mikrotik.deviceName}</option>)
                    ) : (
                        <option>No devices found</option>
                    )}
                </select>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={fetchStats}
                    disabled={!selectedMikrotik || isLoading}
                    className="btn-secondary"
                    title="Refresh dashboard data"
                >
                    <RefreshIcon /> Refresh
                </button>
                <button
                    type="button"
                    onClick={handleResetSales}
                    disabled={!selectedMikrotik}
                    className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 shadow-sm transition-colors hover:bg-red-50 disabled:pointer-events-none disabled:opacity-50"
                    title="Reset all sales data for this device"
                >
                    <DeleteIcon /> Reset sales
                </button>
            </div>
        </section>
    );

    if (isLoading) {
        return <LoadingDisplay message="Loading Dashboard..." />;
    }

    if (!stats) {
        return (
            <div className="space-y-4">
                {dashboardControls}
                <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
                    <p className="text-sm font-semibold text-slate-700">No dashboard data available</p>
                    <p className="mt-1 text-xs text-slate-500">The selected device may be offline or unreachable.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {dashboardControls}

            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-sm font-bold text-slate-900">Sales performance</h2>
                    <p className="text-[11px] text-slate-500">Stored reporting data for the selected device</p>
                </div>
                <span className="badge badge-success">Database online</span>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard title="Daily Sales" value={formatSales(stats.salesDaily)} icon={<SalesIcon/>} color="border-blue-500" />
                <StatCard title="Weekly Sales" value={formatSales(stats.salesWeekly)} icon={<SalesIcon/>} color="border-green-500" />
                <StatCard title="Monthly Sales" value={formatSales(stats.salesMonthly)} icon={<SalesIcon/>} color="border-yellow-500" />
                <StatCard title="Yearly Sales" value={formatSales(stats.salesYearly)} icon={<SalesIcon/>} color="border-red-500" />

                {stats.liveStatsLoading ? (
                    <div className="col-span-full flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs text-blue-700">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
                        Connecting to the MikroTik for live network metrics…
                    </div>
                ) : stats.liveStatsError ? (
                    <div className="col-span-full flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <p className="text-xs font-bold text-amber-900">Live router metrics unavailable</p>
                            <p className="mt-0.5 text-[11px] text-amber-700">Sales data remains available. Check the tunnel, router status, and API credentials.</p>
                        </div>
                        <button type="button" onClick={fetchStats} className="inline-flex items-center gap-1.5 self-start text-xs font-semibold text-amber-800 hover:text-amber-950">
                            <RefreshIcon /> Retry connection
                        </button>
                    </div>
                ) : (
                    <>
                        <StatCard title="Hotspot Active" value={stats.hotspotActive} icon={<WifiIcon className="w-6 h-6"/>} color="border-cyan-500" />
                        <StatCard title="Hotspot Total Users" value={stats.hotspotTotal} icon={<WifiIcon className="w-6 h-6"/>} color="border-cyan-500" />
                        <StatCard title="PPPoE Active" value={stats.pppoeActive} icon={<UsersIcon/>} color="border-purple-500" />
                        <StatCard title="PPPoE Total Users" value={stats.pppoeTotal} icon={<UsersIcon/>} color="border-purple-500" />
                    </>
                )}
            </div>

            {stats.systemStats && !stats.liveStatsError && (
                <SystemStatsPanel stats={stats.systemStats} />
            )}
        </div>
    );
};
const CustomizationPage = () => {
    const [activeTab, setActiveTab] = useState('themes');

    return (
        <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Customization</h1>
            <div className="bg-white rounded-lg shadow-md">
                <div className="border-b border-gray-200">
                    <nav className="flex space-x-4 p-4" aria-label="Tabs">
                        <button onClick={() => setActiveTab('themes')} className={`px-3 py-2 font-medium text-sm rounded-md ${activeTab === 'themes' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}>
                            Portal Themes
                        </button>
                        <button onClick={() => setActiveTab('logos')} className={`px-3 py-2 font-medium text-sm rounded-md ${activeTab === 'logos' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}>
                            Upload Logos
                        </button>
                        {/* --- NEW: Sounds Tab Button --- */}
                        <button onClick={() => setActiveTab('sounds')} className={`px-3 py-2 font-medium text-sm rounded-md ${activeTab === 'sounds' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}>
                            Upload Sounds
                        </button>
                        {/* --- NEW: GCash Tab Button --- */}
                        <button onClick={() => setActiveTab('gcash')} className={`px-3 py-2 font-medium text-sm rounded-md ${activeTab === 'gcash' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}>
                            GCASH Portal
                        </button>
                    </nav>
                </div>
                <div className="p-4 sm:p-6">
                    {activeTab === 'themes' && <ThemesTab />}
                    {activeTab === 'logos' && <LogosTab />}
                    {/* --- NEW: Sounds Tab Content --- */}
                    {activeTab === 'sounds' && <SoundsTab />}
                    {activeTab === 'gcash' && <GcashTab />}
                </div>
            </div>
        </div>
    );
};
const ThemesTab = () => {
    const { user } = useAuthStore();
    const isAdmin = user.role === 'admin';
    const api = isAdmin ? adminApi : ownerApi;

    const [themes, setThemes] = useState([]);
    const [mikrotiks, setMikrotiks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeployModalOpen, setDeployModalOpen] = useState(false);
    const [selectedTheme, setSelectedTheme] = useState(null);

    const [isPreviewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState('');
    
    const [isEditModalOpen, setEditModalOpen] = useState(false);

    const { register: registerUpload, handleSubmit: handleUploadSubmit, reset: resetUploadForm, formState: { isSubmitting: isUploading } } = useForm();
    const { register: registerDeploy, handleSubmit: handleDeploySubmit, reset: resetDeployForm } = useForm();
    const { register: registerEdit, handleSubmit: handleEditSubmit, reset: resetEditForm, formState: { isSubmitting: isUpdating } } = useForm();

    const fetchThemes = useCallback(() => {
        return api.getThemes().then(setThemes).catch(() => toast.error("Could not load themes."));
    }, [api]);

    useEffect(() => {
        setIsLoading(true);
        const promises = [fetchThemes()];
        const deviceApi = isAdmin ? adminApi.getAllMikrotiks : ownerApi.getMikrotiks;
        promises.push(deviceApi().then(setMikrotiks));
        
        Promise.all(promises).finally(() => setIsLoading(false));
    }, [isAdmin, fetchThemes]);
    
    const getThemeCardColor = (themeName) => {
        const name = themeName.toLowerCase();
        if (name.includes('red')) return 'bg-red-100 hover:bg-red-200';
        if (name.includes('gray') || name.includes('light')) return 'bg-slate-100 hover:bg-slate-200';
        if (name.includes('modern')) return 'bg-blue-100 hover:bg-blue-200';
        if (name.includes('dark')) return 'bg-gray-700 text-white hover:bg-gray-600';
        if (name.includes('green')) return 'bg-green-100 hover:bg-green-200';
        if (name.includes('yellow')) return 'bg-yellow-100 hover:bg-yellow-200';
        return 'bg-white hover:bg-gray-50';
    };

    const handlePreview = (theme) => {
        const cacheBuster = new Date().getTime();
        const apiUrl = user.role === 'admin' ? '/api/admin' : '/api/owner';
        setPreviewImageUrl(`/api/themes/previews/${theme.folderName}.png?t=${cacheBuster}`);
        setSelectedTheme(theme);
        setPreviewModalOpen(true);
    };

    // --- FIX: Moved these handler functions before the return statement ---
    const onUploadSubmit = async (data) => {
        const formData = new FormData();
        formData.append('name', data.name);
        formData.append('themeZip', data.themeZip[0]);
        formData.append('previewImage', data.previewImage[0]);

        await toast.promise(adminApi.uploadTheme(formData), {
            loading: 'Uploading theme...',
            success: (res) => {
                resetUploadForm();
                fetchThemes();
                return res.message;
            },
            error: (err) => err.response?.data?.message || 'Failed to upload theme.',
        });
    };
    
    const handleOpenEditModal = (theme) => {
        setSelectedTheme(theme);
        resetEditForm({ name: theme.name });
        setEditModalOpen(true);
    };

    const onUpdateSubmit = async (data) => {
        if (!selectedTheme) return;

        const formData = new FormData();
        formData.append('name', data.name);
        if (data.previewImage && data.previewImage[0]) {
            formData.append('previewImage', data.previewImage[0]);
        }

        await toast.promise(adminApi.updateTheme(selectedTheme.id, formData), {
            loading: 'Updating theme...',
            success: (res) => {
                setEditModalOpen(false);
                fetchThemes();
                return res.message;
            },
            error: (err) => err.response?.data?.message || 'Failed to update theme.',
        });
    };

    const handleOpenDeployModal = (theme) => {
        setSelectedTheme(theme);
        resetDeployForm();
        setDeployModalOpen(true);
    };

    const handleDeleteTheme = async (theme) => {
        if (window.confirm(`Are you sure you want to permanently delete the theme "${theme.name}"?`)) {
            await toast.promise(adminApi.deleteTheme(theme.id), {
                loading: 'Deleting theme...',
                success: (res) => {
                    fetchThemes();
                    return res.message;
                },
                error: (err) => err.response?.data?.message || 'Failed to delete theme.',
            });
        }
    };

    const onDeploySubmit = async (data) => {
        if (!selectedTheme) return;
        const { mikrotikId, model } = data;
        await toast.promise(api.deployTheme(mikrotikId, { themeId: selectedTheme.id, model }), {
            loading: `Deploying '${selectedTheme.name}'...`,
            success: (res) => {
                setDeployModalOpen(false);
                return res.message;
            },
            error: (err) => err.response?.data?.message || 'Failed to deploy theme.',
        });
    };

    if (isLoading) return <LoadingDisplay message="Loading themes..." />;

    return (
        <div>
            {isAdmin && (
                <details className="bg-white p-4 rounded-lg shadow-md mb-8 open:ring-2 open:ring-blue-500 transition-shadow">
                    <summary className="font-semibold text-lg cursor-pointer">Upload New Theme</summary>
                    <div className="mt-4 border-t pt-4">
                        <form onSubmit={handleUploadSubmit(onUploadSubmit)} className="space-y-4 max-w-lg">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Theme Name</label>
                                <input {...registerUpload('name', { required: true })} placeholder="e.g., Modern Dark Theme" className="input-class w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Theme (.zip file)</label>
                                <input type="file" {...registerUpload('themeZip', { required: true })} accept=".zip" className="input-class w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Preview Image (.png, .jpg, .gif)</label>
                                <input type="file" {...registerUpload('previewImage', { required: true })} accept="image/png, image/jpeg, image/gif" className="input-class w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                            </div>
                            <div className="text-right">
                                <button type="submit" className="btn-primary" disabled={isUploading}>{isUploading ? 'Uploading...' : 'Upload Theme'}</button>
                            </div>
                        </form>
                    </div>
                </details>
            )}

            <div>
                <h2 className="text-xl font-semibold mb-4 text-gray-800">Available Themes</h2>
                {themes.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {themes.map(theme => {
                            const cardColor = getThemeCardColor(theme.name);
                            return (
                                <div key={theme.id} className={`${cardColor} rounded-lg shadow-md flex flex-col justify-between overflow-hidden transition-colors duration-300`}>
                                    <div className="p-5">
                                        <h3 className="font-bold text-lg">{theme.name}</h3>
                                    </div>
                                    <div className="bg-black bg-opacity-5 px-5 py-3 flex items-center justify-end gap-2">
                                        {isAdmin && (
                                            <>
                                                <button onClick={() => handleOpenEditModal(theme)} className="p-2 text-blue-600 hover:text-blue-800" title="Edit Theme">
                                                    <EditIcon />
                                                </button>
                                                <button onClick={() => handleDeleteTheme(theme)} className="p-2 text-red-500 hover:text-red-700" title="Delete Theme">
                                                    <DeleteIcon />
                                                </button>
                                            </>
                                        )}
                                        <button onClick={() => handlePreview(theme)} className="btn-secondary text-sm font-semibold">
                                            Preview
                                        </button>
                                        <button onClick={() => handleOpenDeployModal(theme)} className="btn-primary text-sm font-semibold">
                                            Deploy
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                     <div className="text-center py-10 bg-white rounded-lg shadow-md">
                        <p className="text-gray-500">No themes have been uploaded by the administrator.</p>
                    </div>
                )}
            </div>

            <ImagePreviewModal 
                isOpen={isPreviewModalOpen} 
                onClose={() => setPreviewModalOpen(false)} 
                imageUrl={previewImageUrl}
                themeName={selectedTheme?.name}
            />

            <Modal isOpen={isEditModalOpen} onClose={() => setEditModalOpen(false)} title={`Edit Theme: ${selectedTheme?.name}`}>
                <form onSubmit={handleEditSubmit(onUpdateSubmit)}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Theme Name</label>
                            <input {...registerEdit('name', { required: true })} className="input-class w-full" />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Re-upload Preview Image (Optional)</label>
                            <input type="file" {...registerEdit('previewImage')} accept="image/png, image/jpeg, image/gif" className="input-class w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                            <p className="text-xs text-gray-500 mt-1">If you upload a new image, it will replace the old one.</p>
                        </div>
                    </div>
                    <div className="flex justify-end gap-4 mt-6">
                        <button type="button" onClick={() => setEditModalOpen(false)} className="btn-secondary">Cancel</button>
                        <button type="submit" className="btn-primary" disabled={isUpdating}>{isUpdating ? 'Saving...' : 'Save Changes'}</button>
                    </div>
                </form>
            </Modal>

            <Modal isOpen={isDeployModalOpen} onClose={() => setDeployModalOpen(false)} title={`Deploy Theme: ${selectedTheme?.name}`}>
                <form onSubmit={handleDeploySubmit(onDeploySubmit)}>
                    <p className="text-gray-600 mb-4">Select a device and its model to deploy this theme to. Protected files (logos, config.js, etc.) will not be overwritten.</p>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">MikroTik Device</label>
                            <select {...registerDeploy('mikrotikId', { required: true })} className="input-class w-full">
                                <option value="">-- Select a device --</option>
                                {mikrotiks.map(m => <option key={m.id} value={m.id}>{m.deviceName}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Device Model</label>
                            <select {...registerDeploy('model', { required: true })} className="input-class w-full">
                                <MikrotikModelOptions placeholder="-- Select model --" />
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-end gap-4 mt-6">
                        <button type="button" onClick={() => setDeployModalOpen(false)} className="btn-secondary">Cancel</button>
                        <button type="submit" className="btn-primary">Deploy Theme</button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

// --- NEW: SoundsTab Component ---

const GcashTab = () => {
    const { user } = useAuthStore();
    const isAdmin = user.role === 'admin';
    const api = isAdmin ? adminApi : ownerApi;

    const [mikrotiks, setMikrotiks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedMikrotik, setSelectedMikrotik] = useState('');
    const [selectedModel, setSelectedModel] = useState('');
    const [gcashUserId, setGcashUserId] = useState('');
    const [isFetchingId, setIsFetchingId] = useState(false);

    const { register, handleSubmit, formState: { isSubmitting: isDeploying } } = useForm();
    const { register: registerId, handleSubmit: handleIdSubmit, setValue, formState: { isSubmitting: isSavingId } } = useForm();

    useEffect(() => {
        const deviceApi = isAdmin ? adminApi.getAllMikrotiks : ownerApi.getMikrotiks;
        deviceApi().then(setMikrotiks).catch(() => toast.error("Could not load device list.")).finally(() => setIsLoading(false));
    }, [isAdmin]);
    
    useEffect(() => {
        if (selectedMikrotik && selectedModel) {
            setIsFetchingId(true);
            api.getGcashUserId(selectedMikrotik, selectedModel)
                .then(data => {
                    setGcashUserId(data.userId);
                    setValue('userId', data.userId); // Set value for the form
                })
                .catch(err => {
                    toast.error(err.response?.data?.message || "Failed to fetch GCash User ID.");
                    setGcashUserId('');
                    setValue('userId', '');
                })
                .finally(() => setIsFetchingId(false));
        } else {
            setGcashUserId('');
            setValue('userId', '');
        }
    }, [selectedMikrotik, selectedModel, api, setValue]);

    const onDeployScript = async (data) => {
        await toast.promise(
            api.runGcashScript(data.mikrotikId),
            {
                loading: 'Deploying GCash script to MikroTik...',
                success: (res) => res.message || 'Script deployed successfully!',
                error: (err) => err.response?.data?.message || 'Failed to deploy script.',
            }
        );
    };
    
    const onSaveUserId = async (data) => {
        await toast.promise(
            api.updateGcashUserId(selectedMikrotik, { model: selectedModel, userId: data.userId }),
            {
                loading: 'Saving User ID to login.html...',
                success: (res) => {
                    setGcashUserId(data.userId); // Update state on success
                    return res.message || 'User ID saved successfully!';
                },
                error: (err) => err.response?.data?.message || 'Failed to save User ID.',
            }
        );
    };

    if (isLoading) return <LoadingDisplay message="Loading devices..." />;
    
    return (
        <div className="space-y-8">
            {/* Script Deployment Section */}
            <div className="max-w-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Deploy GCash Walled Garden</h3>
                <p className="text-gray-700 mb-4 text-sm">This will run a script on your MikroTik to configure the firewall, schedulers, and DNS for the GCash payment portal. Run this once per device.</p>
                <form onSubmit={handleSubmit(onDeployScript)} className="space-y-4">
                    <div>
                        <label htmlFor="gcash-mikrotikId-deploy" className="block text-gray-700 text-sm font-bold mb-2">Target MikroTik Device</label>
                        <select id="gcash-mikrotikId-deploy" className="input-class w-full" {...register('mikrotikId', { required: true })}>
                            <option value="">-- Select a device --</option>
                            {mikrotiks.map(m => <option key={m.id} value={m.id}>{m.deviceName}</option>)}
                        </select>
                    </div>
                     <div className="flex justify-end gap-4 mt-2">
                        <button type="submit" className="btn-primary" disabled={isDeploying}>{isDeploying ? 'Deploying...' : 'Deploy Scripts'}</button>
                    </div>
                </form>
            </div>

            {/* User ID Management Section */}
            <div className="max-w-lg border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Manage GCash User ID</h3>
                <p className="text-gray-700 mb-4 text-sm">Fetch and update the GCash User ID used in the `login.html` file of the selected device.</p>
                <form onSubmit={handleIdSubmit(onSaveUserId)} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="gcash-mikrotikId-manage" className="block text-gray-700 text-sm font-bold mb-2">Target Device</label>
                            <select id="gcash-mikrotikId-manage" className="input-class w-full" value={selectedMikrotik} onChange={e => setSelectedMikrotik(e.target.value)} required>
                                <option value="">-- Select a device --</option>
                                {mikrotiks.map(m => <option key={m.id} value={m.id}>{m.deviceName}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="gcash-model-manage" className="block text-gray-700 text-sm font-bold mb-2">Device Model</label>
                            <select id="gcash-model-manage" className="input-class w-full" value={selectedModel} onChange={e => setSelectedModel(e.target.value)} required>
                                <MikrotikModelOptions placeholder="-- Select Model --" />
                            </select>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="gcash-userId" className="block text-gray-700 text-sm font-bold mb-2">GCash User ID</label>
                        <div className="relative">
                            <input id="gcash-userId" type="text" className="input-class w-full" {...registerId('userId', { required: true })} disabled={isFetchingId || !selectedMikrotik || !selectedModel} placeholder={isFetchingId ? 'Fetching...' : 'Select device and model first'}/>
                            {isFetchingId && <div className="absolute inset-y-0 right-0 pr-3 flex items-center"><Spinner/></div>}
                        </div>
                    </div>
                     <div className="flex justify-end gap-4 mt-2">
                        <button type="submit" className="btn-primary" disabled={isSavingId || isFetchingId || !gcashUserId}>{isSavingId ? 'Saving...' : 'Save User ID'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const SoundsTab = () => {
    const { user } = useAuthStore();
    const isAdmin = user.role === 'admin';
    const api = isAdmin ? adminApi : ownerApi;

    const [mikrotiks, setMikrotiks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const { register, handleSubmit, reset, watch, formState: { isSubmitting } } = useForm();
    const soundFile = watch("soundFile");

    useEffect(() => {
        const deviceApi = isAdmin ? adminApi.getAllMikrotiks : ownerApi.getMikrotiks;
        deviceApi().then(setMikrotiks).catch(() => toast.error("Could not load device list.")).finally(() => setIsLoading(false));
    }, [isAdmin]);

    const onSoundUpload = async (data) => {
        const { mikrotikId, model } = data;
        if (!mikrotikId) {
            toast.error("Please select a MikroTik device.");
            return;
        }

        const formData = new FormData();
        formData.append('model', model);

        if (data.soundFile && data.soundFile[0]) {
            if (data.soundFile[0].size > 250 * 1024) {
                toast.error("Sound file exceeds the 250KB size limit.");
                return;
            }
            formData.append('soundFile', data.soundFile[0]);
        } else {
            toast.error("Please select an MP3 file to upload.");
            return;
        }

        await toast.promise(
            api.uploadSound(mikrotikId, formData),
            {
                loading: 'Uploading sound file...',
                success: (res) => {
                    reset();
                    return res.message || 'Sound file uploaded successfully!';
                },
                error: (err) => err.response?.data?.message || 'Failed to upload sound file.',
            }
        );
    };

    if (isLoading) return <LoadingDisplay message="Loading devices..." />;

    return (
        <div className="max-w-lg">
             <form onSubmit={handleSubmit(onSoundUpload)}>
                <p className="text-gray-700 mb-4 text-sm">Upload a new `insertcoinbg.mp3` file to a selected device. Max file size is 250KB.</p>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="sound-mikrotikId" className="block text-gray-700 text-sm font-bold mb-2">Target MikroTik Device</label>
                        <select id="sound-mikrotikId" className="input-class w-full" {...register('mikrotikId', { required: true })}>
                            <option value="">-- Select a device --</option>
                            {mikrotiks.map(m => <option key={m.id} value={m.id}>{m.deviceName}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="sound-model" className="block text-gray-700 text-sm font-bold mb-2">Device Model</label>
                        <select id="sound-model" className="input-class w-full" {...register('model', { required: true })}>
                            <MikrotikModelOptions placeholder="-- Select Model --" />
                        </select>
                    </div>
                    <div>
                        <label htmlFor="soundFile" className="block text-gray-700 text-sm font-bold mb-2">Insert Coin Sound (insertcoinbg.mp3)</label>
                        <input id="soundFile" type="file" accept=".mp3" className="input-class w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" {...register('soundFile', { required: true })} />
                        {soundFile && soundFile[0] && <p className="text-xs text-gray-500 mt-1">Selected: {soundFile[0].name} ({(soundFile[0].size / 1024).toFixed(2)} KB)</p>}
                    </div>
                </div>
                <div className="flex justify-end gap-4 mt-6">
                    <button type="submit" className="btn-primary" disabled={isSubmitting}>{isSubmitting ? 'Uploading...' : 'Upload Sound'}</button>
                </div>
            </form>
        </div>
    );
};

const LogosTab = () => {
    const { user } = useAuthStore();
    const isAdmin = user.role === 'admin';
    const api = isAdmin ? adminApi : ownerApi;

    const [mikrotiks, setMikrotiks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const { register, handleSubmit, reset, watch, formState: { isSubmitting } } = useForm();
    const logo1File = watch("logo1");
    const logo2File = watch("logo2");

    useEffect(() => {
        const deviceApi = isAdmin ? adminApi.getAllMikrotiks : ownerApi.getMikrotiks;
        deviceApi().then(setMikrotiks).catch(() => toast.error("Could not load device list.")).finally(() => setIsLoading(false));
    }, [isAdmin]);

    const onLogoUpload = async (data) => {
        const { mikrotikId, model } = data;
        if (!mikrotikId) {
            toast.error("Please select a MikroTik device.");
            return;
        }

        const formData = new FormData();
        formData.append('model', model);

        if (data.logo1 && data.logo1[0]) {
            if (data.logo1[0].size > 100 * 1024) {
                toast.error("Logo 1 exceeds the 100KB size limit.");
                return;
            }
            formData.append('logo1', data.logo1[0]);
        }
        if (data.logo2 && data.logo2[0]) {
            if (data.logo2[0].size > 100 * 1024) {
                toast.error("Logo 2 exceeds the 100KB size limit.");
                return;
            }
            formData.append('logo2', data.logo2[0]);
        }

        if (!formData.has('logo1') && !formData.has('logo2')) {
            toast.error("Please select at least one logo to upload.");
            return;
        }

        await toast.promise(
            api.uploadLogos(mikrotikId, formData),
            {
                loading: 'Uploading logos to MikroTik...',
                success: (res) => {
                    reset();
                    return res.message || 'Logos uploaded successfully!';
                },
                error: (err) => err.response?.data?.message || 'Failed to upload logos.',
            }
        );
    };

    if (isLoading) return <LoadingDisplay message="Loading devices..." />;

    return (
        <div className="max-w-lg">
             <form onSubmit={handleSubmit(onLogoUpload)}>
                <p className="text-gray-700 mb-4 text-sm">Upload new logos to replace the existing ones on a selected device. Max file size is 100KB.</p>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="mikrotikId" className="block text-gray-700 text-sm font-bold mb-2">Target MikroTik Device</label>
                        <select id="mikrotikId" className="input-class w-full" {...register('mikrotikId', { required: true })}>
                            <option value="">-- Select a device --</option>
                            {mikrotiks.map(m => <option key={m.id} value={m.id}>{m.deviceName}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="model" className="block text-gray-700 text-sm font-bold mb-2">Device Model</label>
                        <select id="model" className="input-class w-full" {...register('model', { required: true })}>
                            <MikrotikModelOptions placeholder="-- Select Model --" />
                        </select>
                    </div>
                    <div>
                        <label htmlFor="logo1" className="block text-gray-700 text-sm font-bold mb-2">Logo 1 (logo1.jpg)</label>
                        <input id="logo1" type="file" accept=".jpg,.jpeg" className="input-class w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" {...register('logo1')} />
                        {logo1File && logo1File[0] && <p className="text-xs text-gray-500 mt-1">Selected: {logo1File[0].name} ({(logo1File[0].size / 1024).toFixed(2)} KB)</p>}
                    </div>
                    <div>
                        <label htmlFor="logo2" className="block text-gray-700 text-sm font-bold mb-2">Logo 2 (logo2.PNG)</label>
                        <input id="logo2" type="file" accept=".png" className="input-class w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" {...register('logo2')} />
                        {logo2File && logo2File[0] && <p className="text-xs text-gray-500 mt-1">Selected: {logo2File[0].name} ({(logo2File[0].size / 1024).toFixed(2)} KB)</p>}
                    </div>
                </div>
                <div className="flex justify-end gap-4 mt-6">
                    <button type="submit" className="btn-primary" disabled={isSubmitting}>{isSubmitting ? 'Uploading...' : 'Upload Logos'}</button>
                </div>
            </form>
        </div>
    );
};

const OwnerForm = ({ editingOwner, onSubmit, onCancel }) => {
    const { register, handleSubmit, reset } = useForm();

    useEffect(() => {
        // Pre-populate the form when editing an existing owner
        if (editingOwner) {
            reset(editingOwner);
        } else {
            // Default maxVendos to 1 for new owners
            reset({ username: '', password: '', maxMikrotiks: 1, maxVendos: 1 });
        }
    }, [editingOwner, reset]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <p className="text-gray-500 mb-4 text-sm">For edits, leave the password blank to keep it unchanged.</p>
            <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Username</label>
                <input className="bg-gray-50 border border-gray-300 text-gray-900 rounded w-full py-2 px-3" {...register('username', { required: true })} />
            </div>
            <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Password</label>
                <input className="bg-gray-50 border border-gray-300 text-gray-900 rounded w-full py-2 px-3" type="password" {...register('password', { required: !editingOwner })} />
            </div>
            <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Max Mikrotik Devices</label>
                <input className="bg-gray-50 border border-gray-300 text-gray-900 rounded w-full py-2 px-3" type="number" {...register('maxMikrotiks', { required: true, valueAsNumber: true, min: 0 })} />
            </div>
            <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">Max Vendo Machines</label>
                <input 
                    className="bg-gray-50 border border-gray-300 text-gray-900 rounded w-full py-2 px-3" 
                    type="number" 
                    {...register('maxVendos', { required: true, valueAsNumber: true, min: 0 })} 
                />
            </div>
            <div className="flex justify-end gap-4">
                <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-md">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">Save</button>
            </div>
        </form>
    );
};

const PaginationControls = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between mt-4">
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Previous
            </button>
            <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
            </span>
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Next
            </button>
        </div>
    );
};



const PromoManagementPage = () => {
    // 1. All hooks are now called unconditionally at the top.
    const { user } = useAuthStore();
    const [vendoData, setVendoData] = useState({ vendos: [], totalPages: 0 });
    const [livePromos, setLivePromos] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedVendo, setSelectedVendo] = useState(null);
    const [currentSettings, setCurrentSettings] = useState(null);
    const [currentPromos, setCurrentPromos] = useState([]);
    const [editingPromoIndex, setEditingPromoIndex] = useState(null);
    const { register, handleSubmit, reset } = useForm();
    const MAX_PROMO_RATES = 7;

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    // Safely define api, allowing it to be null initially
    const api = user ? (user.role === 'admin' ? adminApi : ownerApi) : null;
    
    const fetchVendos = useCallback(() => {
        // Only run if the api object is ready
        if (!api) return;

        setIsLoading(true);
        api.getAllVendosWithPromos(debouncedSearchTerm, currentPage)
            .then(data => {
                setVendoData(data || { vendos: [], totalPages: 0 });
            })
            .catch(err => {
                toast.error("Failed to load Vendo list.");
                setVendoData({ vendos: [], totalPages: 0 });
            })
            .finally(() => setIsLoading(false));
    }, [api, debouncedSearchTerm, currentPage]);

    useEffect(() => {
        // Only run the fetch if the api object is ready
        if (api) {
            fetchVendos();
        }
    }, [api, fetchVendos]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm]);

    useEffect(() => {
        if (api && Array.isArray(vendoData.vendos) && vendoData.vendos.length > 0) {
            vendoData.vendos.forEach(vendo => {
                if (vendo.isOnline) {
                    api.getFirmwareSettings(vendo.id)
                        .then(settings => {
                            setLivePromos(prev => ({ ...prev, [vendo.id]: settings.promos || [] }));
                        })
                        .catch(err => {
                            console.error(`Could not fetch promos for ${vendo.vendoName}`, err);
                            setLivePromos(prev => ({ ...prev, [vendo.id]: [] }));
                        });
                }
            });
        }
    }, [vendoData.vendos, api]);

    const handleOpenModal = (vendo) => {
        setSelectedVendo(vendo);
        toast.promise(api.getFirmwareSettings(vendo.id), {
            loading: 'Loading current promos from firmware...',
            success: (settings) => {
                setCurrentSettings(settings);
                setCurrentPromos(settings.promos || []);
                setModalOpen(true);
                return "Current promos loaded.";
            },
            error: "Could not load promos from firmware."
        });
    };
    
    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedVendo(null);
        setCurrentPromos([]);
        setCurrentSettings(null);
        setEditingPromoIndex(null);
        reset();
    };

    const handlePromoSubmit = (data) => {
        const promoData = {
            name: data.name,
            price: parseInt(data.price, 10),
            minutes: parseInt(data.minutes, 10),
            validityDays: parseInt(data.validityDays, 10)
        };
        if (editingPromoIndex !== null) {
            const updatedPromos = [...currentPromos];
            updatedPromos[editingPromoIndex] = promoData;
            setCurrentPromos(updatedPromos);
            toast.success("Promo updated locally.");
        } else {
            if (currentPromos.length >= MAX_PROMO_RATES) {
                toast.error(`A maximum of ${MAX_PROMO_RATES} promos is allowed.`);
                return;
            }
            setCurrentPromos([...currentPromos, promoData]);
            toast.success("Promo added locally.");
        }
        setEditingPromoIndex(null);
        reset({ name: '', price: '', minutes: '', validityDays: '' });
    };

    const handleEditPromo = (promo, index) => {
        setEditingPromoIndex(index);
        reset(promo);
    };
    
    const handleDeletePromo = (indexToDelete) => {
        setCurrentPromos(currentPromos.filter((_, index) => index !== indexToDelete));
    };
    
    const handleSaveChanges = async () => {
        if (!selectedVendo || !currentSettings) return;
        
        const settingsToSave = { 
            ...currentSettings,
            promos: currentPromos
        };

        await toast.promise(
            api.setFirmwareSettings(selectedVendo.id, settingsToSave),
            {
                loading: 'Saving promos to firmware...',
                success: (res) => {
                    handleCloseModal();
                    fetchVendos();
                    return 'Promos saved successfully!';
                },
                error: (err) => err.response?.data?.message || 'Failed to save promos.',
            }
        );
    };

    // Conditional returns now happen AFTER all hooks have been called.
    if (!user) {
        return <LoadingDisplay message="Loading user data..." />;
    }

    if (isLoading) {
        return <LoadingDisplay message="Loading Vendo machines..." />;
    }

    // The final component render
    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Promo Rate Management</h1>
                
                {user?.role === 'admin' && (
                    <input
                        type="text"
                        placeholder="Search Vendo Name..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="input-class w-full sm:w-64"
                    />
                )}
            </div>

            {!isLoading && (!vendoData.vendos || vendoData.vendos.length === 0) && (
                <div className="text-center py-10 bg-white rounded-lg shadow-md">
                    <p className="text-gray-500">No Vendo machines found.</p>
                    <p className="text-sm text-gray-400 mt-2">Please add a Vendo machine under 'My Devices' to manage its promo rates.</p>
                </div>
            )}
            
            <div className="space-y-4">
                {Array.isArray(vendoData.vendos) && vendoData.vendos.map(vendo => (
                    <div key={vendo.id} className="bg-white rounded-lg shadow-md p-4">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="font-semibold text-lg text-gray-800">{vendo.vendoName}</p>
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${vendo.isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{vendo.isOnline ? 'Online' : 'Offline'}</span>
                            </div>
                            <button 
                                onClick={() => handleOpenModal(vendo)}
                                disabled={!vendo.isOnline}
                                className="btn-primary text-sm flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                <SettingsIcon /> Manage Promos
                            </button>
                        </div>
                        <div className="mt-4 pt-4 border-t">
                             <h4 className="text-sm font-semibold text-gray-600 mb-2">Current Promos:</h4>
                             {(livePromos[vendo.id] || vendo.promos)?.length > 0 ? (
                                <ul className="list-disc list-inside text-sm text-gray-500">
                                    {(livePromos[vendo.id] || vendo.promos).map((promo, i) => (
                                        <li key={i}>{promo.name} (P{promo.price} / {promo.minutes}m)</li>
                                    ))}
                                </ul>
                             ) : (
                                <p className="text-sm text-gray-400">No promos found.</p>
                             )}
                        </div>
                    </div>
                ))}
            </div>
            
            <PaginationControls
                currentPage={currentPage}
                totalPages={vendoData.totalPages}
                onPageChange={setCurrentPage}
            />

            <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={`Manage Promos for ${selectedVendo?.vendoName}`}>
                <div className="space-y-4">
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                        {currentPromos.map((promo, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm">{promo.name} (P{promo.price} / {promo.minutes}m / {promo.validityDays}d)</span>
                                <div className="flex items-center gap-2">
                                    {/* --- THIS IS THE UI IMPROVEMENT --- */}
                                    <button onClick={() => handleEditPromo(promo, index)} className="p-2 text-indigo-600 bg-indigo-100 hover:bg-indigo-200 rounded-full transition-colors" title="Edit Promo">
                                        <EditIcon />
                                    </button>
                                    <button onClick={() => handleDeletePromo(index)} className="p-2 text-red-600 bg-red-100 hover:bg-red-200 rounded-full transition-colors" title="Delete Promo">
                                        <DeleteIcon />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <form onSubmit={handleSubmit(handlePromoSubmit)} className="space-y-4 border-t pt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Name</label>
                            <input {...register('name', {required: true})} placeholder="e.g., 1 Hour Pass" className="input-class w-full" />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Price</label>
                                <input {...register('price', {required: true})} type="number" placeholder="e.g., 10" className="input-class w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Minutes</label>
                                <input {...register('minutes', {required: true})} type="number" placeholder="e.g., 60" className="input-class w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Validity (Days)</label>
                                <input {...register('validityDays', {required: true})} type="number" placeholder="e.g., 1" className="input-class w-full" />
                            </div>
                        </div>
                        <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2">
                            {editingPromoIndex !== null ? <><EditIcon /> Update Promo</> : <><AddIcon /> Add Promo</>}
                        </button>
                    </form>
                    <div className="flex justify-end gap-4 pt-4 border-t">
                        <button type="button" onClick={handleCloseModal} className="btn-secondary">Cancel</button>
                        <button type="button" onClick={handleSaveChanges} className="btn-success flex items-center gap-2">
                            <CheckCircleIcon /> Save All to Firmware
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};


const SalesReportPage = () => {
    const { user } = useAuthStore();
    const [reportData, setReportData] = useState(null);
    const [mikrotiks, setMikrotiks] = useState([]);
    const [selectedMikrotikId, setSelectedMikrotikId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const salesRequestIdRef = useRef(0);
    
    const api = user.role === 'admin' ? adminApi : ownerApi;
    const selectedDevice = mikrotiks.find(m => m.id.toString() === selectedMikrotikId.toString());
    
    const fetchDevices = useCallback(async () => {
        try {
            const deviceApi = user.role === 'admin' ? adminApi.getAllMikrotiks : ownerApi.getMikrotiks;
            const initialDevices = await deviceApi();
            if (initialDevices && initialDevices.length > 0) {
                setMikrotiks(initialDevices);
                setSelectedMikrotikId(currentId => {
                    const selectionStillExists = initialDevices.some(device => String(device.id) === String(currentId));
                    return selectionStillExists ? currentId : initialDevices[0].id;
                });
            } else {
                setMikrotiks([]);
                setSelectedMikrotikId('');
                setReportData([]);
                setIsLoading(false);
            }
        } catch (error) {
            toast.error("Could not load the device list.");
            setIsLoading(false);
        }
    }, [user.role]);

    useEffect(() => {
        fetchDevices();
    }, [fetchDevices]);

    const refreshSelectedDeviceStatus = useCallback(async ({ silent = false } = {}) => {
        if (!selectedMikrotikId) return null;
        if (!silent) setIsCheckingStatus(true);

        try {
            const updatedDevice = await pingerApi.pingNow({ type: 'mikrotik', id: selectedMikrotikId });
            setMikrotiks(currentDevices => currentDevices.map(device => (
                String(device.id) === String(updatedDevice.id) ? { ...device, ...updatedDevice } : device
            )));
            return updatedDevice;
        } catch (error) {
            setMikrotiks(currentDevices => currentDevices.map(device => (
                String(device.id) === String(selectedMikrotikId) ? { ...device, isOnline: false } : device
            )));
            return null;
        } finally {
            if (!silent) setIsCheckingStatus(false);
        }
    }, [selectedMikrotikId]);

    useEffect(() => {
        refreshSelectedDeviceStatus({ silent: true });
    }, [refreshSelectedDeviceStatus]);

    const fetchSales = useCallback(async () => {
        if (!selectedMikrotikId) {
            setReportData(null);
            setIsLoading(false);
            return;
        }

        const requestId = ++salesRequestIdRef.current;
        setIsLoading(true);
        try {
            const data = await salesReportApi.getReport(selectedMikrotikId);
            if (requestId === salesRequestIdRef.current) setReportData(data);
        } catch (error) {
            if (requestId === salesRequestIdRef.current && !error.message?.includes('offline')) {
                    toast.error("Failed to load sales report.");
            }
        } finally {
            if (requestId === salesRequestIdRef.current) setIsLoading(false);
        }
    }, [selectedMikrotikId]);

    useEffect(() => {
        setReportData(null);
        fetchSales();
    }, [fetchSales]);


    const handleSync = async () => {
        if (!selectedMikrotikId) {
            toast.error("Please select a device to sync.");
            return;
        }
        setIsSyncing(true);
        try {
            await toast.promise(
                salesReportApi.syncReport(selectedMikrotikId),
                {
                    loading: 'Syncing sales from MikroTik...',
                    success: (res) => res.message || 'Sync complete!',
                    error: (err) => {
                        const message = err.response?.data?.message || 'Sync failed.';
                        if (message.toLowerCase().includes('offline')) {
                            refreshSelectedDeviceStatus({ silent: true });
                            return "Device is offline. Could not sync.";
                        }
                        return message;
                    }
                }
            );
            await fetchSales();
        } finally {
            setIsSyncing(false);
        }
    };
    
    const handleResetVendoSales = async (vendo) => {
        if (!vendo) return;
        if (window.confirm(`WARNING:\n\nYou are about to reset all historical sales data for '${vendo.vendoName}'.\n\nTHIS WILL PERMANENTLY DELETE ALL TRANSACTION LOGS for this Vendo.\n\nAre you sure you want to continue?`)) {
            await toast.promise(
                api.resetVendoSales(vendo.id),
                {
                    loading: `Resetting sales & logs for '${vendo.vendoName}'...`,
                    success: (res) => {
                        fetchSales(); 
                        return res.message || 'Sales and logs reset successfully!';
                    },
                    error: (err) => err.response?.data?.message || 'Failed to reset sales.',
                }
            );
        }
    };

    const formatSales = value => `₱${Number(value || 0).toLocaleString('en-PH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

    const salesSummary = (reportData || []).reduce((summary, vendo) => ({
        daily: summary.daily + Number(vendo.salesDaily || 0),
        weekly: summary.weekly + Number(vendo.salesWeekly || 0),
        monthly: summary.monthly + Number(vendo.salesMonthly || 0),
        yearly: summary.yearly + Number(vendo.salesYearly || 0),
    }), { daily: 0, weekly: 0, monthly: 0, yearly: 0 });

    return (
        <div className="space-y-4">
            <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0 flex-1">
                    <label htmlFor="device-select-report" className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        Reporting device
                    </label>
                    <div className="flex items-center gap-2">
                        <select
                            id="device-select-report"
                            value={selectedMikrotikId}
                            onChange={event => setSelectedMikrotikId(event.target.value)}
                            className="w-full sm:max-w-xs"
                        >
                            <option value="">Select a device</option>
                            {mikrotiks.map(mikrotik => <option key={mikrotik.id} value={mikrotik.id}>{mikrotik.deviceName}</option>)}
                        </select>
                        {selectedDevice && (
                            <span className={`badge shrink-0 ${isCheckingStatus ? 'badge-info' : selectedDevice.isOnline ? 'badge-success' : 'badge-error'}`}>
                                <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${isCheckingStatus ? 'animate-pulse bg-blue-500' : selectedDevice.isOnline ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                {isCheckingStatus ? 'Checking' : selectedDevice.isOnline ? 'Online' : 'Offline'}
                            </span>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleSync}
                    disabled={isSyncing || !selectedDevice?.isOnline}
                    className="btn-primary w-full sm:w-auto"
                >
                    <span className={isSyncing ? 'animate-spin' : ''}><RefreshIcon /></span>
                    {isSyncing ? 'Syncing sales…' : 'Sync sales'}
                </button>
            </section>

            {selectedDevice && !selectedDevice.isOnline && (
                <div className="flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-xs font-bold text-amber-900">Live synchronization is unavailable</p>
                        <p className="mt-0.5 text-[11px] text-amber-700">Showing the latest sales stored in PostgreSQL. Restore the router connection to synchronize new transactions.</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => refreshSelectedDeviceStatus()}
                        disabled={isCheckingStatus}
                        className="inline-flex shrink-0 items-center gap-1.5 self-start text-xs font-semibold text-amber-800 hover:text-amber-950 disabled:opacity-50"
                    >
                        <span className={isCheckingStatus ? 'animate-spin' : ''}><RefreshIcon /></span>
                        Check connection
                    </button>
                </div>
            )}

            {isLoading ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Loading sales report">
                    {[0, 1, 2, 3].map(item => <div key={item} className="h-[74px] animate-pulse rounded-xl border border-slate-200 bg-white" />)}
                </div>
            ) : reportData && reportData.length > 0 ? (
                <>
                    <section>
                        <div className="mb-2 flex items-end justify-between">
                            <div>
                                <h2 className="text-sm font-bold text-slate-900">Portfolio summary</h2>
                                <p className="text-[11px] text-slate-500">Combined sales from {reportData.length} {reportData.length === 1 ? 'vendo' : 'vendos'}</p>
                            </div>
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Stored sales data</span>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                            <StatCard title="Daily Sales" value={formatSales(salesSummary.daily)} icon={<SalesIcon/>} color="border-blue-500" />
                            <StatCard title="Weekly Sales" value={formatSales(salesSummary.weekly)} icon={<SalesIcon/>} color="border-green-500" />
                            <StatCard title="Monthly Sales" value={formatSales(salesSummary.monthly)} icon={<SalesIcon/>} color="border-yellow-500" />
                            <StatCard title="Yearly Sales" value={formatSales(salesSummary.yearly)} icon={<SalesIcon/>} color="border-red-500" />
                        </div>
                    </section>

                    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50/70 px-4 py-3">
                            <div>
                                <h2 className="text-sm font-bold text-slate-900">Vendo breakdown</h2>
                                <p className="text-[11px] text-slate-500">Performance by individual machine</p>
                            </div>
                            <span className="badge badge-info">{reportData.length} active</span>
                        </div>
                        <div className="divide-y divide-slate-100">
                            {reportData.map((vendo, index) => (
                                <article key={vendo.id || index} className="px-4 py-3 transition-colors hover:bg-slate-50/60">
                                    <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
                                        <div className="flex min-w-[220px] flex-1 items-center gap-3">
                                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                                                <VendoIcon />
                                            </span>
                                            <div className="min-w-0">
                                                <h3 className="truncate text-sm font-bold text-slate-900">{vendo.vendoName}</h3>
                                                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Vendo sales</p>
                                            </div>
                                        </div>
                                        <div className="grid flex-[3] grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-4">
                                            {[
                                                ['Today', vendo.salesDaily],
                                                ['This week', vendo.salesWeekly],
                                                ['This month', vendo.salesMonthly],
                                                ['This year', vendo.salesYearly],
                                            ].map(([label, value]) => (
                                                <div key={label} className="bg-white px-3 py-2.5">
                                                    <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
                                                    <p className="mt-0.5 text-sm font-bold tabular-nums text-slate-900">{formatSales(value)}</p>
                                                </div>
                                            ))}
                                        </div>
                                <button
                                    type="button"
                                    onClick={() => handleResetVendoSales(vendo)}
                                    disabled={!selectedDevice?.isOnline}
                                    className="inline-flex min-h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-500 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:pointer-events-none disabled:opacity-40"
                                    title={`Reset sales for ${vendo.vendoName}`}
                                >
                                                <DeleteIcon/> Reset
                                </button>
                                    </div>
                                </article>
                            ))}
                            </div>
                    </section>
                </>
            ) : (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
                    <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><SalesIcon /></span>
                    <p className="mt-3 text-sm font-semibold text-slate-700">No sales data available</p>
                    <p className="mt-1 text-xs text-slate-500">{!selectedMikrotikId ? 'Select a reporting device to begin.' : 'No stored sales were found for this device.'}</p>
                </div>
            )}
        </div>
    );
};


const DeviceManagementNav = ({ mikrotikId }) => (
    <div className="pt-4 mt-4 border-t border-gray-700">
        <h3 className="px-3 text-xs font-semibold uppercase text-gray-400 mb-2">Manage Device</h3>
        <SidebarNavLink to={`/manage/device/${mikrotikId}/vendos`} icon={<VendoIcon />}>Vendo Machines</SidebarNavLink>
        <SidebarNavLink to={`/manage/device/${mikrotikId}/vouchers`} icon={<VoucherIcon />}>Vouchers</SidebarNavLink>
        <SidebarNavLink to={`/manage/device/${mikrotikId}/active-users`} icon={<UsersIcon />}>Active Users</SidebarNavLink>
        <SidebarNavLink to={`/manage/device/${mikrotikId}/hotspot-users`} icon={<UsersIcon />}>Hotspot Users</SidebarNavLink>
        <SidebarNavLink to={`/manage/device/${mikrotikId}/settings`} icon={<SettingsIcon />}>Settings</SidebarNavLink>
        <SidebarNavLink to={`/manage/device/${mikrotikId}/hotspot-coins-log`} icon={<SalesLogIcon />}>Transaction Logs</SidebarNavLink>
        <SidebarNavLink to={`/manage/device/${mikrotikId}/telegram`} icon={<ProfileIcon />}>Telegram Alerts</SidebarNavLink>
        <SidebarNavLink to={`/manage/device/${mikrotikId}/auto-config`} icon={<WandIcon />}>Auto-Config</SidebarNavLink>
        <SidebarNavLink to={`/manage/device/${mikrotikId}/backups`} icon={<BackupIcon />}>Backups</SidebarNavLink>    
    </div>
);

const SidebarNav = ({ isSidebarOpen, setSidebarOpen }) => {
    const { user, logout, originalAdminToken, returnToAdmin } = useAuthStore();
    const navigate = useNavigate();
    const { mikrotikId } = useParams();
    const handleLogout = () => {
        logout();
        navigate('/login');
    };
    const handleReturnToAdmin = () => {
        returnToAdmin();
        navigate('/admin/dashboard');
    };
    return (
        <>
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
                    onClick={() => setSidebarOpen(false)}
                ></div>
            )}
            <aside className={`app-sidebar fixed inset-y-0 left-0 z-30 flex w-[232px] shrink-0 transform flex-col text-white transition-transform duration-200 ease-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0`}>
                <div className="flex h-16 items-center justify-between border-b border-white/10 px-4">
                    <Link to="/" className="flex items-center gap-2.5">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white p-1 shadow-sm">
                            <img src="/logo.png" alt="MoziTech" className="h-full w-full object-contain" />
                        </span>
                        <span>
                            <span className="block text-[15px] font-bold tracking-tight">MoziFi</span>
                            <span className="block text-[8px] font-semibold uppercase tracking-[0.18em] text-gray-500">Operations</span>
                        </span>
                    </Link>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="md:hidden text-gray-400 hover:text-white p-1"
                        aria-label="Close sidebar"
                    >
                        <CloseIcon />
                    </button>
                </div>

                {/* --- THIS IS THE FIX --- */}
                <nav className="flex-grow space-y-1 overflow-y-auto px-3 py-4">
                    {user?.role === 'admin' && (
                        <>
                            <SidebarNavLink to="/admin/dashboard" icon={<DashboardIcon />}>Dashboard</SidebarNavLink>
                            <SidebarNavLink to="/admin/global-monitor" icon={<MonitorIcon />}>Global Monitor</SidebarNavLink>
                            <SidebarNavLink to="/admin/promo-rates" icon={<SettingsIcon />}>Promo Rates</SidebarNavLink>
                            <SidebarNavLink to="/admin/pppoe" icon={<UsersIcon />}>PPPoE Management</SidebarNavLink>
                            <SidebarNavLink to="/admin/sales-report" icon={<ReportIcon />}>Sales Report</SidebarNavLink>
                            <SidebarNavLink to="/admin/backups" icon={<SalesLogIcon />}>Backups</SidebarNavLink>
                            <SidebarNavLink to="/admin/profile" icon={<ProfileIcon />}>My Profile</SidebarNavLink>
                            <SidebarNavLink to="/admin/customization" icon={<ImageIcon />}>Customization</SidebarNavLink>
                            <SidebarNavLink to="/admin/enterprise-sales" icon={<ReportIcon />}>
                Enterprise Analytics
            </SidebarNavLink>
                        </>
                    )}
                    {user?.role === 'vendo-owner' && (
                        <>
                            <SidebarNavLink to="/owner/stats-dashboard" icon={<DashboardIcon />}>Dashboard</SidebarNavLink>
                            <SidebarNavLink to="/owner/dashboard" icon={<DashboardIcon />}>My Devices</SidebarNavLink>
                            <SidebarNavLink to="/owner/promo-rates" icon={<SettingsIcon />}>Promo Rates</SidebarNavLink>
                            <SidebarNavLink to="/owner/pppoe" icon={<UsersIcon />}>PPPoE Management</SidebarNavLink>
                            <SidebarNavLink to="/owner/sales-report" icon={<ReportIcon />}>Sales Report</SidebarNavLink>
                            <SidebarNavLink to="/owner/backups" icon={<SalesLogIcon />}>Backups</SidebarNavLink>
                            <SidebarNavLink to="/owner/customization" icon={<ImageIcon />}>Customization</SidebarNavLink>
                            <SidebarNavLink to="/owner/profile" icon={<ProfileIcon />}>My Profile</SidebarNavLink>
                            <SidebarNavLink to="/owner/enterprise-sales" icon={<ReportIcon />}>
                Enterprise Analytics
            </SidebarNavLink>
                        </>
                    )}
                    {mikrotikId && <DeviceManagementNav mikrotikId={mikrotikId} />}
                </nav>

                <div className="mt-auto border-t border-white/10 p-3">
    <div className="px-3 py-2 overflow-hidden">
        {/* Compact font size (text-sm), truncation, and tooltip on hover */}
        <p 
            className="font-semibold text-sm truncate max-w-[180px] text-gray-100" 
            title={user?.username}
        >
            {user?.username}
        </p>
        {/* Even smaller font size (text-[10px]) for the role label */}
        <p className="text-[10px] uppercase tracking-wider text-gray-400 truncate">
            {user?.role === 'vendo-owner' ? 'Owner' : 'Administrator'}
        </p>
    </div>
    {originalAdminToken && (
                        <button
                            onClick={handleReturnToAdmin}
                            className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-white bg-indigo-600 transition-all hover:bg-indigo-500 shadow-md"
                        >
                            <ProfileIcon className="w-4 h-4" />
                            Return to Admin
                        </button>
                    )}
    <button
        onClick={handleLogout}
        className="mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium text-gray-300 transition-all hover:bg-red-600 hover:text-white"
    >
        <LogoutIcon className="w-4 h-4" />
        Logout
    </button>
</div>
            </aside>
        </>
    );
};


const pageTitles = {
    dashboard: 'Dashboard',
    'stats-dashboard': 'Operations Dashboard',
    'global-monitor': 'Network Monitor',
    'promo-rates': 'Promo Rates',
    pppoe: 'PPPoE Management',
    'sales-report': 'Sales Report',
    'enterprise-sales': 'Financial Analytics',
    backups: 'Backups',
    customization: 'Customization',
    profile: 'My Profile',
    vendos: 'Vendo Machines',
    vouchers: 'Vouchers',
    'active-users': 'Active Users',
    'hotspot-users': 'Hotspot Users',
    settings: 'Device Settings',
    telegram: 'Telegram Alerts',
    'auto-config': 'Auto Configuration',
    'hotspot-coins-log': 'Transaction Logs',
};

const Header = ({ setSidebarOpen }) => {
    const { user } = useAuthStore();
    const location = useLocation();
    const segment = location.pathname
        .split('/')
        .filter(Boolean)
        .reverse()
        .find(pathSegment => pageTitles[pathSegment]);
    const title = pageTitles[segment] || 'MoziFi Console';

    return (
        <header className="app-header sticky top-0 z-20 flex h-14 items-center justify-between gap-4 border-b px-4 sm:px-5">
            <div className="flex min-w-0 items-center gap-3">
                <button onClick={() => setSidebarOpen(true)} className="-ml-1 rounded-lg p-2 text-slate-600 hover:bg-slate-100 md:hidden">
                    <MenuIcon />
                    <span className="sr-only">Open sidebar</span>
                </button>
                <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">{title}</p>
                    <p className="hidden text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400 sm:block">MoziTech IT Solutions</p>
                </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                <span className="max-w-[150px] truncate text-xs font-semibold text-slate-700">{user?.username}</span>
                <span className="hidden text-[9px] font-bold uppercase tracking-wider text-slate-400 sm:inline">{user?.role === 'admin' ? 'Admin' : 'Owner'}</span>
            </div>
        </header>
    );
};

const Footer = () => {
 return (
    <footer className="mt-auto flex flex-col justify-between gap-1 border-t border-slate-200 bg-white px-4 py-2.5 text-[10px] text-slate-400 sm:flex-row sm:px-5">
      <p>MoziFi Management Console · v2.0</p>
      <p>© {new Date().getFullYear()} MoziTech IT Solutions</p>
    </footer>
  );
};

const MainLayout = () => {
    const [isSidebarOpen, setSidebarOpen] = useState(false);

    return (
        <SidebarContext.Provider value={{ isSidebarOpen, setSidebarOpen }}>
            <div className="mozifi-app relative flex min-h-screen w-full">
                <SidebarNav isSidebarOpen={isSidebarOpen} setSidebarOpen={setSidebarOpen} />
                <div className="flex flex-1 flex-col overflow-hidden">
                    <Header setSidebarOpen={setSidebarOpen} />
                    <main className="flex-1 p-3 sm:p-4 lg:p-5">
                        <div className="app-content"><Outlet /></div>
                    </main>
                    <Footer />
                </div>

                {/* Floating Action Button for Mobile */}
                {!isSidebarOpen && (
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="fixed bottom-5 right-5 z-50 rounded-xl bg-blue-600 p-3 text-white shadow-xl md:hidden"
                        aria-label="Open Navigation"
                    >
                        <MenuIcon className="w-6 h-6" />
                    </button>
                )}
            </div>
        </SidebarContext.Provider>
    );
};

const PrivateRoute = ({ allowedRoles }) => {
    const { user } = useAuthStore();
    if (!user) return <Navigate to="/login" replace />;
    if (allowedRoles && !allowedRoles.includes(user.role)) {
        return <Navigate to="/" replace />;
    }
    return <Outlet />;
};

const HomeRedirect = () => {
    const { user } = useAuthStore();
    if (!user) return <Navigate to="/login" replace />;
    return user.role === 'admin' ? <Navigate to="/admin/dashboard" replace /> : <Navigate to="/owner/dashboard" replace />;
};


// --- AUTH PAGE ---
const LoginPage = () => {
    const { register, handleSubmit } = useForm();
    const navigate = useNavigate();
    const { login, user } = useAuthStore();
    const [error, setError] = useState('');
    const recaptchaRef = useRef(null);
    const recaptchaEnabled = useRecaptchaEnabled();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('verified') === 'true') {
            toast.success("Email verified successfully! You can now login.");
        }
    }, []);;

    const onSubmit = async (data) => {
        const token = recaptchaEnabled ? recaptchaRef.current?.getValue() : null;
        if (recaptchaEnabled && !token) {
            setError('Please verify you are not a robot.');
            return;
        }
        
        // Clear previous errors before attempting login
        setError(''); 

        try {
            const response = await authApi.login(data.username, data.password, token);
            login(response.token, response.user);
            navigate('/');
        } catch (err) {
            // --- FIX: Read the specific message from the backend response ---
            const backendMessage = err.response?.data?.message;
            setError(backendMessage || 'Invalid credentials or server error.');
            
            recaptchaRef.current?.reset();
        }
    };

    return (
        <div className="auth-shell flex min-h-screen items-center justify-center p-4">
            <div className="w-full max-w-[380px]">
                <div className="mb-4 text-center text-white">
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-blue-300">MoziTech IT Solutions</p>
                    <h1 className="mt-2 text-2xl font-bold tracking-tight">Network operations, simplified.</h1>
                </div>
                <div className="auth-card rounded-2xl px-6 py-6 sm:px-7">
                    <div className="mb-5 flex items-center gap-3">
                        <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm">
                            <img src="/logo.png" alt="MoziTech" className="h-full w-full object-contain" />
                        </span>
                        <div>
                            <h1 className="text-xl font-bold tracking-tight text-slate-950">Welcome to MoziFi</h1>
                            <p className="text-xs text-slate-500">Sign in to your management console</p>
                        </div>
                    </div>
                    {error && <div className="bg-red-100 text-red-700 text-center p-3 rounded-md mb-4">{error}</div>}
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="mb-4">
                            <label className="mb-1.5 block text-xs font-semibold text-slate-700">Email or username</label>
                            <input className="input-class" autoComplete="username" {...register('username')} />
                        </div>
                        <div className="mb-6">
                            <label className="mb-1.5 block text-xs font-semibold text-slate-700">Password</label>
                            <input className="input-class" type="password" autoComplete="current-password" {...register('password')} />
                        </div>
                        {recaptchaEnabled && <div className="my-4 flex justify-center">
                           <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey="6LfjloorAAAAAIliYx8r0qFzrYk1n70I9aMgLBEC"
                           />
                        </div>}
                        <button className="btn-primary w-full" type="submit">Sign in to console</button>
                        <div className="mt-4 text-center">
    <p className="text-sm text-gray-600">Don't have an account? <Link to="/register" className="text-blue-600 hover:text-blue-800 font-semibold">Sign up</Link></p>
        <p><Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-800">Forgot Password?</Link></p>
</div>

    

                    </form>
                </div>
            </div>
        </div>
    );
};


const RegisterPage = () => {
    const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm();
    const navigate = useNavigate();
    const { user } = useAuthStore(); // Removed 'login' since we don't auto-login anymore
    const [error, setError] = useState('');
    const recaptchaRef = useRef(null);
    const recaptchaEnabled = useRecaptchaEnabled();
    const password = watch("password");

    useEffect(() => { if (user) navigate('/'); }, [user, navigate]);

    const onSubmit = async (data) => {
        const token = recaptchaEnabled ? recaptchaRef.current?.getValue() : null;
        if (recaptchaEnabled && !token) { setError('Please complete the reCAPTCHA verification.'); return; }
        setError('');
        
        try {
            // Only call register, do NOT call login()
            const response = await authApi.register(data.username, data.password, token);
            
            // Show success message and redirect to login
            toast.success(response.message || "Registration successful! Check your email.");
            navigate('/login');
        } catch (err) {
            setError(err.response?.data?.message || 'Registration failed.');
            recaptchaRef.current?.reset();
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
            <div className="w-full max-w-sm bg-white shadow-2xl rounded-lg px-8 pt-6 pb-6 mb-4 border border-gray-200">
                <h1 className="text-center text-2xl font-bold text-gray-800 mb-4">Create Account</h1>
                {error && <div className="bg-red-100 text-red-700 text-center p-3 rounded-md mb-4 text-sm">{error}</div>}
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Email Address</label>
                        <input 
                            type="email" // Enforce email input type
                            className={`bg-gray-50 border ${errors.username ? 'border-red-500' : 'border-gray-300'} text-gray-900 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500`} 
                            {...register('username', { 
                                required: "Email is required", 
                                pattern: { value: /^\S+@\S+$/i, message: "Invalid email address" }
                            })} 
                        />
                        {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username.message}</p>}
                    </div>
                    {/* ... Password & Confirm Password fields remain the same ... */}
                    {/* ... ReCAPTCHA & Submit Button remain the same ... */}
                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Password</label>
                        <input type="password" className="bg-gray-50 border border-gray-300 text-gray-900 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500" {...register('password', { required: true, minLength: 6 })} />
                    </div>
                    <div className="mb-6">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Confirm Password</label>
                        <input type="password" className="bg-gray-50 border border-gray-300 text-gray-900 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500" {...register('confirmPassword', { required: true, validate: val => val === password || "Passwords do not match" })} />
                    </div>
                    {recaptchaEnabled && <div className="my-4 flex justify-center"><ReCAPTCHA ref={recaptchaRef} sitekey="6LfjloorAAAAAIliYx8r0qFzrYk1n70I9aMgLBEC" /></div>}
                    <button className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded w-full" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Creating...' : 'Sign Up'}</button>
                </form>
                <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">Already have an account? <Link to="/login" className="text-blue-600 hover:text-blue-800 font-semibold">Login here</Link></p>
                </div>
            </div>
        </div>
    );
};
// --- ADMIN PAGES ---
// App.js

const AdminDashboardPage = () => {
    const [ownersData, setOwnersData] = useState({
        owners: [],
        totalPages: 0,
        globalStats: { totalOwners: 0, activeDevices: 0, loggedOnOwners: 0, onlineOwners: [] },
    });
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [analytics, setAnalytics] = useState({
        summary: {
            totalOwners: 0,
            totalMikrotiks: 0,
            onlineNow: 0,
            activeToday: 0,
            activeThisWeek: 0,
            activeThisMonth: 0,
            pendingVerifications: 0,
        },
        onlineOwners: [],
        activityTrend: { timezone: 'Asia/Manila', trackingStartedAt: null, points: [] },
        topOwners: [],
        abuseCandidates: [],
    });
    const [analyticsLoading, setAnalyticsLoading] = useState(true);
    const [analyticsError, setAnalyticsError] = useState('');
    const [trendDays, setTrendDays] = useState(30);
    const [leaderboardRange, setLeaderboardRange] = useState('30d');
    const [isOnlineOwnersModalOpen, setOnlineOwnersModalOpen] = useState(false);
    const [selectedOwnerId, setSelectedOwnerId] = useState(null);
    const [ownerToReview, setOwnerToReview] = useState('');
    const [abusePage, setAbusePage] = useState(1);
    const abusePageSize = 10;
    const abuseTotalPages = Math.max(1, Math.ceil(analytics.abuseCandidates.length / abusePageSize));
    const currentAbusePage = Math.min(abusePage, abuseTotalPages);
    const abusePageStart = (currentAbusePage - 1) * abusePageSize;
    const visibleAbuseCandidates = analytics.abuseCandidates.slice(abusePageStart, abusePageStart + abusePageSize);

    useEffect(() => {
        setAbusePage((page) => Math.min(page, abuseTotalPages));
    }, [abuseTotalPages]);

    const navigate = useNavigate();
    const { user, token, impersonate } = useAuthStore();

    // Modal Visibility States
    const [isOwnerModalOpen, setOwnerModalOpen] = useState(false);
    const [editingOwner, setEditingOwner] = useState(null);
    const [isDeviceModalOpen, setDeviceModalOpen] = useState(false);
    const [selectedOwnerForDevice, setSelectedOwnerForDevice] = useState(null);
    const [editingDevice, setEditingDevice] = useState(null);
    const [isScriptModalOpen, setScriptModalOpen] = useState(false);
    const [selectedDeviceForScript, setSelectedDeviceForScript] = useState(null);
    const [isLogoModalOpen, setLogoModalOpen] = useState(false);
    const [selectedDeviceForLogo, setSelectedDeviceForLogo] = useState(null);

    // React Hook Form initializations
    const { register: registerDevice, handleSubmit: handleDeviceSubmit, reset: resetDeviceForm } = useForm();
    const { register: registerScript, handleSubmit: handleScriptSubmit, reset: resetScriptForm } = useForm();
    const { register: registerLogo, handleSubmit: handleLogoSubmit, reset: resetLogoForm, watch: watchLogo } = useForm();
    const logo1File = watchLogo("logo1");
    const logo2File = watchLogo("logo2");

    // Search and Pagination states
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);


    const handleImpersonate = async (owner) => {
        if (window.confirm(`Are you sure you want to impersonate the account for ${owner.username}?`)) {
            try {
                const response = await adminApi.impersonateOwner(owner.id);
                // Trigger the Zustand action to swap tokens
                impersonate(response.token, response.user, token, user);
                // Redirect to the owner dashboard
                navigate('/owner/dashboard');
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to impersonate account.");
            }
        }
    };

    // Fetch Hierarchy Data
    const fetchHierarchy = useCallback(async () => {
        setIsLoading(true);
        setLoadError('');
        try {
            const data = await adminApi.getOwnersHierarchy(debouncedSearchTerm, currentPage);
            const owners = Array.isArray(data?.owners)
                ? data.owners.map(owner => ({
                    ...owner,
                    MikrotikDevices: owner.MikrotikDevices || owner.mikrotikdevices || [],
                }))
                : [];

            setOwnersData({
                owners,
                totalPages: Number(data?.totalPages || 0),
                globalStats: {
                    totalOwners: Number(data?.globalStats?.totalOwners || 0),
                    activeDevices: Number(data?.globalStats?.activeDevices || 0),
                    loggedOnOwners: Number(data?.globalStats?.loggedOnOwners || 0),
                    onlineOwners: Array.isArray(data?.globalStats?.onlineOwners) ? data.globalStats.onlineOwners : [],
                },
            });
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to load owner and device list.';
            setLoadError(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    }, [debouncedSearchTerm, currentPage]);

    const fetchAnalytics = useCallback(async () => {
        setAnalyticsLoading(true);
        setAnalyticsError('');
        try {
            const data = await adminApi.getDashboardAnalytics(trendDays, leaderboardRange);
            setAnalytics((previous) => ({
                summary: { ...previous.summary, ...(data?.summary || {}) },
                onlineOwners: Array.isArray(data?.onlineOwners) ? data.onlineOwners : [],
                activityTrend: data?.activityTrend || { timezone: 'Asia/Manila', trackingStartedAt: null, points: [] },
                topOwners: Array.isArray(data?.topOwners) ? data.topOwners : [],
                abuseCandidates: Array.isArray(data?.abuseCandidates) ? data.abuseCandidates : [],
            }));
        } catch (error) {
            setAnalyticsError(error.response?.data?.message || 'Failed to load dashboard analytics.');
        } finally {
            setAnalyticsLoading(false);
        }
    }, [trendDays, leaderboardRange]);

    useEffect(() => { fetchHierarchy(); }, [fetchHierarchy]);
    useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

    useEffect(() => { setCurrentPage(1); }, [debouncedSearchTerm]);

    useEffect(() => {
        if (!ownerToReview) return;
        const match = ownersData.owners.find((owner) => owner.username === ownerToReview);
        if (match) {
            setSelectedOwnerId(match.id);
            setOwnerToReview('');
        }
    }, [ownerToReview, ownersData.owners]);

    const reviewOwner = (username) => {
        setSelectedOwnerId(null);
        setOwnerToReview(username);
        setSearchTerm(username);
        setCurrentPage(1);
        window.requestAnimationFrame(() => document.getElementById('owner-directory')?.scrollIntoView({ behavior: 'smooth' }));
    };

    // --- OWNER HANDLERS ---
    const handleToggleVerify = async (id, username, isVerified) => {
        if (window.confirm(`Are you sure you want to ${isVerified ? 'unverify' : 'verify'} owner "${username}"?`)) {
            toast.promise(adminApi.toggleVerifyOwner(id), {
                loading: 'Updating verification status...',
                success: () => { fetchHierarchy(); fetchAnalytics(); return 'User verification updated!'; },
                error: 'Failed to update verification.'
            });
        }
    };

    const handleOpenOwnerModal = (owner = null) => {
        setEditingOwner(owner);
        setOwnerModalOpen(true);
    };

    const handleCloseOwnerModal = () => { setOwnerModalOpen(false); setEditingOwner(null); };

    const onOwnerSubmit = async (data) => {
        toast.promise(
            editingOwner ? adminApi.updateOwner(editingOwner.id, data) : adminApi.createOwner(data),
            {
                loading: 'Saving owner...',
                success: () => { fetchHierarchy(); fetchAnalytics(); handleCloseOwnerModal(); return `Owner ${editingOwner ? 'updated' : 'created'} successfully!`; },
                error: (err) => err.response?.data?.message || 'Failed to save owner.',
            }
        );
    };
    
    const handleDeleteOwner = async (ownerId, ownerName) => {
        if (window.confirm(`Are you sure you want to PERMANENTLY delete owner '${ownerName}'?`)) {
            await toast.promise(adminApi.deleteOwner(ownerId), {
                loading: `Deleting owner '${ownerName}'...`,
                success: (res) => { fetchHierarchy(); fetchAnalytics(); return res.message; },
                error: (err) => err.response?.data?.message || 'Failed to delete owner.',
            });
        }
    };

    const handleToggleBan = async (id, username, isBanned) => {
        if (window.confirm(`Are you sure you want to ${isBanned ? 'unban' : 'ban'} owner "${username}"?`)) {
            toast.promise(adminApi.toggleBanOwner(id), {
                loading: 'Updating status...',
                success: () => { fetchHierarchy(); fetchAnalytics(); return 'Owner status updated!'; },
                error: 'Failed to update status.'
            });
        }
    };

    // --- DEVICE HANDLERS ---
    const handleDeleteDevice = async (deviceId, deviceName) => {
        if (window.confirm(`Are you sure you want to delete device "${deviceName}"?`)) {
            toast.promise(adminApi.deleteMikrotik(deviceId), {
                loading: 'Deleting device...',
                success: () => { fetchHierarchy(); fetchAnalytics(); return 'Device deleted successfully!'; },
                error: (err) => err.response?.data?.message || 'Failed to delete device.',
            });
        }
    };

    const handleOpenDeviceModal = (owner) => {
        setSelectedOwnerForDevice(owner);
        setEditingDevice(null);
        resetDeviceForm({ deviceName: '', mikrotikIP: '', mikrotikUser: '', mikrotikPass: '' });
        setDeviceModalOpen(true);
    };
    
    const handleOpenEditDeviceModal = async (device, owner) => {
        try {
            const editableDevice = await adminApi.getMikrotikDevice(device.id);
            setSelectedOwnerForDevice(owner);
            setEditingDevice(editableDevice);
            resetDeviceForm({ ...editableDevice, mikrotikPass: '' });
            setDeviceModalOpen(true);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load device details.');
        }
    };

    const handleCloseDeviceModal = () => {
        setDeviceModalOpen(false);
        setSelectedOwnerForDevice(null);
        setEditingDevice(null);
    };

    const onDeviceSubmit = async (data) => {
        const toastId = toast.loading('Saving device...');
        try {
            const promise = editingDevice
                ? adminApi.updateMikrotikDevice(editingDevice.id, data)
                : adminApi.addMikrotikForOwner(selectedOwnerForDevice.id, data);
            
            const savedDevice = await promise;
            toast.loading('Checking device status...', { id: toastId });
            const deviceIdToCheck = editingDevice ? editingDevice.id : savedDevice.id;
            await pingerApi.pingNow({ type: 'mikrotik', id: deviceIdToCheck });

            toast.success('Device saved and status updated!', { id: toastId });
            handleCloseDeviceModal();
            fetchHierarchy();
            fetchAnalytics();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save device.', { id: toastId });
        }
    };

    // --- SCRIPT & LOGO HANDLERS ---
    const handleOpenScriptModal = (device) => {
        setSelectedDeviceForScript(device);
        resetScriptForm({ model: '' });
        setScriptModalOpen(true);
    };

    const handleCloseScriptModal = () => {
        setScriptModalOpen(false);
        setSelectedDeviceForScript(null);
    };

    const onScriptPush = async (data) => {
        if (!selectedDeviceForScript) return;
        toast.promise(
            adminApi.pushMikrotikScripts(selectedDeviceForScript.id, data.model, data.scriptVariant),
            {
                loading: 'Pushing scripts to MikroTik...',
                success: 'Scripts pushed successfully!',
                error: (err) => err.response?.data?.message || 'Failed to push scripts.',
            }
        ).finally(() => {
            handleCloseScriptModal();
            fetchHierarchy();
            fetchAnalytics();
        });
    };
    
    const handleOpenLogoModal = (device) => {
        setSelectedDeviceForLogo(device);
        resetLogoForm({ model: '' });
        setLogoModalOpen(true);
    };

    const handleCloseLogoModal = () => {
        setLogoModalOpen(false);
        setSelectedDeviceForLogo(null);
    };

    const onLogoUpload = async (data) => {
        if (!selectedDeviceForLogo) return;
        const formData = new FormData();
        formData.append('model', data.model);

        if (data.logo1 && data.logo1[0]) {
            if (data.logo1[0].size > 100 * 1024) { toast.error("Logo 1 exceeds 100KB."); return; }
            formData.append('logo1', data.logo1[0]);
        }
        if (data.logo2 && data.logo2[0]) {
            if (data.logo2[0].size > 100 * 1024) { toast.error("Logo 2 exceeds 100KB."); return; }
            formData.append('logo2', data.logo2[0]);
        }
        if (!formData.has('logo1') && !formData.has('logo2')) { toast.error("Select a logo."); return; }

        toast.promise(adminApi.uploadLogos(selectedDeviceForLogo.id, formData), {
            loading: 'Uploading logos...',
            success: (res) => { handleCloseLogoModal(); return res.message || 'Success!'; },
            error: (err) => err.response?.data?.message || 'Failed.',
        });
    };

    if (isLoading) return <LoadingDisplay message="Loading System Hierarchy..." />;

    const trendPoints = analytics.activityTrend.points || [];
    const activityChartData = {
        labels: trendPoints.map((point) => new Intl.DateTimeFormat('en-PH', {
            timeZone: 'UTC', month: 'short', day: 'numeric',
        }).format(new Date(`${point.date}T00:00:00Z`))),
        datasets: [{
            label: 'Active owners',
            data: trendPoints.map((point) => point.activeOwners),
            borderColor: '#2563eb',
            backgroundColor: 'rgba(37, 99, 235, 0.12)',
            pointBackgroundColor: '#2563eb',
            pointRadius: trendDays <= 30 ? 3 : 0,
            pointHoverRadius: 5,
            borderWidth: 2,
            tension: 0.32,
            fill: true,
            spanGaps: false,
        }],
    };
    const activityChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        plugins: { legend: { display: false } },
        scales: {
            x: { grid: { display: false }, ticks: { maxTicksLimit: trendDays === 90 ? 9 : 10 } },
            y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: '#eef2f7' } },
        },
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Enterprise Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-200 pb-5">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">System Administration</h1>
                    <p className="text-sm text-gray-500">Manage owner accounts and global device compliance.</p>
                </div>
                <div className="flex items-center gap-3">
                    {analytics.summary.pendingVerifications > 0 && (
                        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">{analytics.summary.pendingVerifications} pending verification</span>
                    )}
                    <button onClick={() => handleOpenOwnerModal()} className="btn-primary py-2 text-sm">
                        <AddIcon className="w-4 h-4" /> Create Owner
                    </button>
                </div>
            </div>
            {loadError && (
                <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm font-semibold text-amber-900">Administration data is temporarily unavailable</p>
                        <p className="mt-0.5 text-xs text-amber-700">{loadError}</p>
                    </div>
                    <button type="button" onClick={fetchHierarchy} className="btn-secondary shrink-0">
                        <RefreshIcon /> Retry
                    </button>
                </div>
            )}
            {analyticsError && (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                    <p className="text-sm text-amber-800">{analyticsError}</p>
                    <button type="button" onClick={fetchAnalytics} className="btn-secondary shrink-0"><RefreshIcon /> Retry</button>
                </div>
            )}
            <div className={`grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6 ${analyticsLoading ? 'animate-pulse opacity-60' : ''}`}>
                <StatCard 
                    title="Total Registered Owners" 
                    value={analytics.summary.totalOwners}
                    icon={<UsersIcon/>} 
                    color="border-blue-500" 
                />
                <StatCard 
                    title="Registered MikroTiks"
                    value={analytics.summary.totalMikrotiks}
                    icon={<MonitorIcon/>} 
                    color="border-purple-500"
                />
                <StatCard 
                    title="Owners Currently Online" 
                    value={analytics.summary.onlineNow}
                    icon={<MonitorIcon/>} 
                    color="border-green-500" 
                    onClick={() => setOnlineOwnersModalOpen(true)}
                    actionLabel="View owners currently online"
                />
                <StatCard title="Active Today" value={analytics.summary.activeToday} icon={<UsersIcon/>} color="border-blue-500" />
                <StatCard title="Active This Week" value={analytics.summary.activeThisWeek} icon={<UsersIcon/>} color="border-green-500" />
                <StatCard title="Active This Month" value={analytics.summary.activeThisMonth} icon={<UsersIcon/>} color="border-yellow-500" />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:col-span-2">
                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <h2 className="text-sm font-bold text-slate-900">Owner activity trend</h2>
                            <p className="mt-0.5 text-xs text-slate-500">Unique authenticated owners per Manila calendar day</p>
                        </div>
                        <div className="flex rounded-lg bg-slate-100 p-1">
                            {[7, 30, 90].map((days) => (
                                <button key={days} type="button" onClick={() => setTrendDays(days)} className={`rounded-md px-3 py-1 text-xs font-semibold ${trendDays === days ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
                                    {days}D
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="h-64">
                        <Line data={activityChartData} options={activityChartOptions} />
                    </div>
                    <p className="mt-2 text-[11px] text-slate-400">
                        {analytics.activityTrend.trackingStartedAt
                            ? `Tracking began ${new Date(analytics.activityTrend.trackingStartedAt).toLocaleString('en-PH', { timeZone: 'Asia/Manila', dateStyle: 'medium', timeStyle: 'short' })}.`
                            : 'Activity tracking begins after the analytics migration and deployment.'}
                    </p>
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                            <h2 className="text-sm font-bold text-slate-900">Top voucher generators</h2>
                            <p className="mt-0.5 text-xs text-slate-500">Successful generation events</p>
                        </div>
                        <select value={leaderboardRange} onChange={(event) => setLeaderboardRange(event.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700">
                            <option value="today">Today</option>
                            <option value="7d">7 days</option>
                            <option value="30d">30 days</option>
                            <option value="all">All time</option>
                        </select>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {analytics.topOwners.length > 0 ? analytics.topOwners.map((owner) => (
                            <button key={owner.ownerId} type="button" onClick={() => reviewOwner(owner.username)} className="flex w-full items-center gap-3 py-2 text-left hover:bg-slate-50">
                                <span className="w-6 text-center text-xs font-bold text-slate-400">#{owner.rank}</span>
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-xs font-semibold text-slate-800">{owner.username}</span>
                                    <span className="text-[10px] text-slate-400">{owner.mikrotiksRegistered} MikroTik{owner.mikrotiksRegistered === 1 ? '' : 's'}</span>
                                </span>
                                <span className="text-sm font-bold text-blue-700">{Number(owner.vouchersGenerated).toLocaleString()}</span>
                            </button>
                        )) : <p className="py-8 text-center text-xs text-slate-400">No voucher generation events in this period.</p>}
                    </div>
                </section>
            </div>

            <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h2 className="text-sm font-bold text-slate-900">Potential free-plan abuse</h2>
                        <p className="mt-0.5 text-xs text-slate-500">Review-only signals; accounts are never restricted automatically.</p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">{analytics.abuseCandidates.length} flagged</span>
                </div>
                {analytics.abuseCandidates.length > 0 ? (
                    <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                        {visibleAbuseCandidates.map((candidate) => (
                            <div key={candidate.ownerId} className="flex flex-col gap-2 rounded-lg border border-slate-200 p-3 sm:flex-row sm:items-center">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className="truncate text-xs font-bold text-slate-900">{candidate.username}</p>
                                        <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${candidate.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{candidate.severity}</span>
                                    </div>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {candidate.reasons.map((reason) => <span key={reason.code} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{reason.label}</span>)}
                                    </div>
                                </div>
                                <button type="button" onClick={() => reviewOwner(candidate.username)} className="btn-secondary shrink-0 px-3 py-1.5 text-xs">Review owner</button>
                            </div>
                        ))}
                    </div>
                ) : <div className="rounded-lg border border-dashed border-slate-200 py-6 text-center text-xs text-slate-400">No owners currently match the review rules.</div>}
                {analytics.abuseCandidates.length > 0 && (
                    <nav aria-label="Flagged owners pagination" className="mt-3 border-t border-slate-100 pt-3">
                        <p className="text-xs text-slate-500" aria-live="polite">
                            Showing {abusePageStart + 1}–{Math.min(abusePageStart + abusePageSize, analytics.abuseCandidates.length)} of {analytics.abuseCandidates.length} flagged owners
                        </p>
                        <PaginationControls
                            currentPage={currentAbusePage}
                            totalPages={abuseTotalPages}
                            onPageChange={setAbusePage}
                        />
                    </nav>
                )}
            </section>
            {/* Filter Bar */}
            <div id="owner-directory" className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row items-center gap-4">
                <div className="relative w-full max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        <UsersIcon className="w-4 h-4" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search owners or devices..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="input-class pl-10 h-10 border-gray-200 focus:ring-blue-500 rounded-lg w-full"
                    />
                </div>
                <div className="flex-grow"></div>
                <div className="text-xs text-gray-400 font-medium">Showing {ownersData.owners.length} accounts</div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="hidden grid-cols-[minmax(0,2fr)_auto_auto_auto] gap-4 border-b border-slate-100 bg-slate-50 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 md:grid">
                    <span>Owner</span><span>MikroTiks</span><span>Vendos</span><span>Last activity</span>
                </div>
                <div className="divide-y divide-slate-100">
                    {ownersData.owners.map((owner) => {
                        const selected = String(selectedOwnerId) === String(owner.id);
                        const deviceCount = Number(owner.deviceCount ?? owner.MikrotikDevices.length);
                        const vendoCount = Number(owner.vendoCount || 0);
                        return (
                            <div key={owner.id} className={selected ? 'bg-blue-50/40' : 'bg-white'}>
                                <button type="button" onClick={() => setSelectedOwnerId(selected ? null : owner.id)} aria-expanded={selected} className="grid w-full grid-cols-1 items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50 md:grid-cols-[minmax(0,2fr)_auto_auto_auto] md:gap-4">
                                    <span className="flex min-w-0 items-center gap-2.5">
                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[10px] font-bold text-blue-700">{owner.username.substring(0, 2).toUpperCase()}</span>
                                        <span className="min-w-0">
                                            <span className="block truncate text-xs font-bold text-slate-900">{owner.username}</span>
                                            <span className="mt-0.5 flex flex-wrap gap-1">
                                                <span className={`rounded-full px-1.5 py-0.5 text-[8px] font-bold ${owner.isBanned ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{owner.isBanned ? 'SUSPENDED' : 'ACTIVE'}</span>
                                                <span className={`rounded-full px-1.5 py-0.5 text-[8px] font-bold ${owner.isVerified ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{owner.isVerified ? 'VERIFIED' : 'PENDING'}</span>
                                            </span>
                                        </span>
                                    </span>
                                    <span className="text-xs font-semibold text-slate-700"><span className="md:hidden text-slate-400">MikroTiks: </span>{deviceCount}/{owner.maxMikrotiks}</span>
                                    <span className="text-xs font-semibold text-slate-700"><span className="md:hidden text-slate-400">Vendos: </span>{vendoCount}/{owner.maxVendos}</span>
                                    <span className="whitespace-nowrap text-[11px] text-slate-500">{owner.lastSeen ? new Date(owner.lastSeen).toLocaleString('en-PH', { timeZone: 'Asia/Manila', dateStyle: 'medium', timeStyle: 'short' }) : 'Never'}</span>
                                </button>

                                {selected && (
                                    <div className="border-t border-blue-100 px-3 pb-3">
                                        <div className="flex flex-wrap gap-1.5 py-2.5">
                                            <button onClick={() => handleImpersonate(owner)} className="btn-secondary px-2.5 py-1.5 text-xs"><ProfileIcon className="w-3.5 h-3.5" /> Impersonate</button>
                                            <button onClick={() => handleOpenDeviceModal(owner)} className="btn-secondary px-2.5 py-1.5 text-xs"><AddIcon className="w-3.5 h-3.5" /> Device</button>
                                            <button onClick={() => handleOpenOwnerModal(owner)} className="btn-secondary px-2.5 py-1.5 text-xs"><EditIcon className="w-3.5 h-3.5" /> Edit</button>
                                            <button onClick={() => handleToggleVerify(owner.id, owner.username, owner.isVerified)} className="btn-secondary px-2.5 py-1.5 text-xs"><CheckCircleIcon className="w-3.5 h-3.5" /> {owner.isVerified ? 'Unverify' : 'Verify'}</button>
                                            <button onClick={() => handleToggleBan(owner.id, owner.username, owner.isBanned)} className="btn-secondary px-2.5 py-1.5 text-xs">{owner.isBanned ? <CheckCircleIcon className="w-3.5 h-3.5" /> : <BlockIcon className="w-3.5 h-3.5" />} {owner.isBanned ? 'Restore' : 'Suspend'}</button>
                                            <button onClick={() => handleDeleteOwner(owner.id, owner.username)} className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"><DeleteIcon className="w-3.5 h-3.5" /> Delete</button>
                                        </div>
                                        <div className="space-y-1.5">
                                            {owner.MikrotikDevices.length > 0 ? owner.MikrotikDevices.map((device) => (
                                                <div key={device.id} className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 sm:flex-row sm:items-center">
                                                    <span className={`h-2 w-2 shrink-0 rounded-full ${device.isOnline ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                                    <span className="min-w-0 flex-1">
                                                        <span className="block truncate text-xs font-semibold text-slate-800">{device.deviceName}</span>
                                                        <span className="font-mono text-[10px] text-slate-400">{device.mikrotikIP}</span>
                                                    </span>
                                                    <span className={`text-[9px] font-bold ${device.isOnline ? 'text-emerald-600' : 'text-slate-400'}`}>{device.isOnline ? 'ONLINE' : 'OFFLINE'}</span>
                                                    <div className="flex items-center gap-1">
                                                        <button onClick={() => navigate(`/manage/device/${device.id}`)} className="rounded bg-blue-50 px-2 py-1 text-[10px] font-bold text-blue-700">Manage</button>
                                                        <button title="Logos" onClick={() => handleOpenLogoModal(device)} className="p-1.5 text-slate-400 hover:text-slate-700"><ImageIcon className="w-3.5 h-3.5" /></button>
                                                        <button title="Provision" onClick={() => handleOpenScriptModal(device)} className="p-1.5 text-slate-400 hover:text-slate-700"><UploadIcon className="w-3.5 h-3.5" /></button>
                                                        <button title="Edit" onClick={() => handleOpenEditDeviceModal(device, owner)} className="p-1.5 text-indigo-400 hover:text-indigo-700"><EditIcon className="w-3.5 h-3.5" /></button>
                                                        <button title="Delete" onClick={() => handleDeleteDevice(device.id, device.deviceName)} className="p-1.5 text-red-400 hover:text-red-700"><DeleteIcon className="w-3.5 h-3.5" /></button>
                                                    </div>
                                                </div>
                                            )) : <div className="rounded-lg border border-dashed border-slate-200 py-4 text-center text-xs text-slate-400">No hardware assets linked to this owner.</div>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
            
            <PaginationControls
                currentPage={currentPage}
                totalPages={ownersData.totalPages}
                onPageChange={setCurrentPage}
            />

            {/* --- MODALS --- */}
            <Modal isOpen={isOnlineOwnersModalOpen} onClose={() => setOnlineOwnersModalOpen(false)} title="Owners Currently Online">
                <div className="space-y-3">
                    <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                        Owners shown here made an authenticated request within the last five minutes.
                    </p>
                    {analytics.onlineOwners.length > 0 ? (
                        <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
                            {analytics.onlineOwners.map(owner => (
                                <div key={owner.id} className="flex items-center gap-3 bg-white px-3 py-2.5">
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-xs font-bold text-emerald-700">
                                        {owner.username?.substring(0, 2).toUpperCase() || '—'}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-semibold text-slate-900">{owner.username}</p>
                                        <p className="text-[11px] text-slate-500">
                                            Last active {owner.lastSeen ? new Intl.DateTimeFormat('en-PH', {
                                                timeZone: 'Asia/Manila',
                                                dateStyle: 'medium',
                                                timeStyle: 'short',
                                            }).format(new Date(owner.lastSeen)) : 'just now'}
                                        </p>
                                    </div>
                                    <span className="badge badge-success"><span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />Online</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
                            No owners are currently online.
                        </div>
                    )}
                </div>
            </Modal>
            <Modal isOpen={isOwnerModalOpen} onClose={handleCloseOwnerModal} title={editingOwner ? 'Update Profile' : 'New Account'}>
                <OwnerForm editingOwner={editingOwner} onSubmit={onOwnerSubmit} onCancel={handleCloseOwnerModal} />
            </Modal>

            <Modal isOpen={isDeviceModalOpen} onClose={handleCloseDeviceModal} title={editingDevice ? `Modify ${editingDevice.deviceName}` : `Link Asset to ${selectedOwnerForDevice?.username}`}>
                <form onSubmit={handleDeviceSubmit(onDeviceSubmit)} className="space-y-4">
                    <p className="text-xs text-gray-400 italic">Leave password blank to retain current secure credentials.</p>
                    <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Asset Name</label><input className="input-class" {...registerDevice('deviceName', { required: true })} /></div>
                    <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Network Host (IP)</label><input className="input-class font-mono" {...registerDevice('mikrotikIP', { required: true })} /></div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">API User</label><input className="input-class" {...registerDevice('mikrotikUser', { required: true })} /></div>
                        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">API Pass</label><input className="input-class" type="text" {...registerDevice('mikrotikPass', { required: !editingDevice })} /></div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={handleCloseDeviceModal} className="btn-secondary text-sm">Cancel</button><button type="submit" className="btn-primary text-sm px-6">Save Asset</button></div>
                </form>
            </Modal>

            <Modal isOpen={isLogoModalOpen} onClose={handleCloseLogoModal} title="Asset Identity (Logos)">
                <form onSubmit={handleLogoSubmit(onLogoUpload)} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Hardware Model</label>
                        <select className="input-class" {...registerLogo('model', { required: true })}>
                            <MikrotikModelOptions />
                        </select>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                        <div className="p-3 border rounded-lg bg-gray-50">
                            <label className="block text-xs font-bold text-gray-700 mb-2">Logo 1 (.jpg only)</label>
                            <input type="file" accept=".jpg,.jpeg" className="text-xs file:btn-secondary file:py-1" {...registerLogo('logo1')} />
                            {logo1File && logo1File[0] && <p className="text-[10px] text-blue-600 mt-1">Ready: {logo1File[0].name}</p>}
                        </div>
                        <div className="p-3 border rounded-lg bg-gray-50">
                            <label className="block text-xs font-bold text-gray-700 mb-2">Logo 2 (.png only)</label>
                            <input type="file" accept=".png" className="text-xs file:btn-secondary file:py-1" {...registerLogo('logo2')} />
                            {logo2File && logo2File[0] && <p className="text-[10px] text-blue-600 mt-1">Ready: {logo2File[0].name}</p>}
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={handleCloseLogoModal} className="btn-secondary text-sm">Close</button><button type="submit" className="btn-primary text-sm px-6">Upload Identity</button></div>
                </form>
            </Modal>

            <Modal isOpen={isScriptModalOpen} onClose={handleCloseScriptModal} title="System Provisioning (Scripts)">
                <form onSubmit={handleScriptSubmit(onScriptPush)} className="space-y-4">
                    <p className="text-xs text-gray-500">Deploy enterprise configurations directly to the hardware platform.</p>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Hardware Model</label>
                        <select className="input-class" {...registerScript('model', { required: true })}>
                            <MikrotikModelOptions />
                        </select>
                    </div>
                    {/* [NEW] Script Version Selection */}
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">RouterOS Version</label>
                        <select className="input-class" {...registerScript('scriptVariant')} defaultValue="">
                            <option value="">7.8 Above</option>
                            <option value="7.8">v7.8 Below</option>
                        </select>
                    </div>
                    <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={handleCloseScriptModal} className="btn-secondary text-sm">Cancel</button><button type="submit" className="btn-primary text-sm px-6 font-bold flex items-center gap-2"><UploadIcon className="w-4 h-4" /> PUSH CONFIG</button></div>
                </form>
            </Modal>
        </div>
    );
};

const GlobalMikrotikMonitor = () => {
    const [devices, setDevices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Independent pagination states for each group
    const [onlinePage, setOnlinePage] = useState(1);
    const [offlinePage, setOfflinePage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        adminApi.getAllMikrotiks()
            .then(setDevices)
            .catch(console.error)
            .finally(() => setIsLoading(false));
    }, []);

    if (isLoading) return <p className="p-8 text-center text-gray-500">Loading Global Device Monitor...</p>;

    // Filter devices into groups
    const onlineDevices = devices.filter(d => d.isOnline);
    const offlineDevices = devices.filter(d => !d.isOnline);

    // Calculate pagination for Online group
    const onlineTotalPages = Math.ceil(onlineDevices.length / itemsPerPage);
    const currentOnline = onlineDevices.slice((onlinePage - 1) * itemsPerPage, onlinePage * itemsPerPage);

    // Calculate pagination for Offline group
    const offlineTotalPages = Math.ceil(offlineDevices.length / itemsPerPage);
    const currentOffline = offlineDevices.slice((offlinePage - 1) * itemsPerPage, offlinePage * itemsPerPage);

    const DeviceTable = ({ title, deviceList, currentPage, totalPages, onPageChange, badgeColor }) => (
        <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
                <h2 className={`text-xl font-bold ${badgeColor === 'green' ? 'text-green-700' : 'text-red-700'}`}>
                    {title} ({deviceList.length})
                </h2>
            </div>
            <div className="bg-white rounded-lg shadow-md overflow-x-auto">
                <table className="min-w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MikroTik IP</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Seen</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {deviceList.length > 0 ? (
                            deviceList.map(device => (
                                <tr key={device.id}>
                                    <td className="px-4 py-4 whitespace-nowrap text-gray-900 font-medium">{device.deviceName}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-gray-600">{device.owner?.username || 'N/A'}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-gray-600 font-mono text-sm">{device.mikrotikIP}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-gray-600 text-sm">
                                        {device.lastSeen ? new Date(device.lastSeen).toLocaleString() : 'Never'}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="px-4 py-8 text-center text-gray-400">No {title.toLowerCase()} found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <PaginationControls 
                currentPage={currentPage} 
                totalPages={totalPages} 
                onPageChange={onPageChange} 
            />
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 border-b pb-4">Global Device Monitor</h1>
            
            {/* Online Group */}
            <DeviceTable 
                title="Online Devices" 
                deviceList={currentOnline} 
                currentPage={onlinePage}
                totalPages={onlineTotalPages}
                onPageChange={setOnlinePage}
                badgeColor="green"
            />

            {/* Offline Group */}
            <DeviceTable 
                title="Offline Devices" 
                deviceList={currentOffline} 
                currentPage={offlinePage}
                totalPages={offlineTotalPages}
                onPageChange={setOfflinePage}
                badgeColor="red"
            />
        </div>
    );
};
const AdminDeviceHierarchy = () => {
    const [owners, setOwners] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const { register, handleSubmit, reset } = useForm();
    const navigate = useNavigate();

    const fetchHierarchy = useCallback(() => {
        setIsLoading(true);
        adminApi.getOwnersHierarchy()
            .then(setOwners)
            .catch(err => toast.error("Failed to load owner and device list."))
            .finally(() => setIsLoading(false));
    }, []);
    useEffect(() => { fetchHierarchy(); }, [fetchHierarchy]);

    const handleDelete = async (deviceId, deviceName) => {
        if (window.confirm(`Are you sure you want to delete the device "${deviceName}"? This action is permanent and cannot be undone.`)) {
            toast.promise(
                adminApi.deleteMikrotik(deviceId),
                {
                    loading: 'Deleting device...',
                    success: () => { fetchHierarchy(); return 'Device deleted successfully!'; },
                    error: (err) => err.response?.data?.message || 'Failed to delete device.',
                }
            );
        }
    };

    const handleOpenAddModal = (owner) => {
        setSelectedOwner(owner);
        reset({ deviceName: '', mikrotikIP: '', mikrotikUser: '', mikrotikPass: '' });
        setAddModalOpen(true);
    };
    const handleCloseAddModal = () => {
        setAddModalOpen(false);
        setSelectedOwner(null);
    };
    const onSubmitAddDevice = async (data) => {
        if (!selectedOwner) return;
        toast.promise(
            adminApi.addMikrotikForOwner(selectedOwner.id, data),
            {
                loading: `Adding device to ${selectedOwner.username}...`,
                success: () => {
                    handleCloseAddModal();
                    fetchHierarchy();
                    return 'Device added successfully!';
                },
                error: (err) => err.response?.data?.message || 'Failed to add device.',
            }
        );
    };

    if (isLoading) return <p>Loading Owners and Devices...</p>;

    return (
        <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Owner & Device Hierarchy</h1>
            <div className="space-y-4">
                {owners.map(owner => (
                    <details key={owner.id} className="bg-white rounded-lg shadow-md open:ring-2 open:ring-blue-500 transition-all">
                        <summary className="p-4 font-semibold text-lg cursor-pointer flex justify-between items-center text-gray-800">
                            <div className="flex items-center gap-4">
                                <span>Owner: {owner.username} ({owner.MikrotikDevices.length} devices)</span>
                                <button
                                    onClick={(e) => { e.preventDefault(); handleOpenAddModal(owner); }}
                                    className="flex items-center gap-1 text-sm px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-md"
                                >
                                    <AddIcon /> Add Device
                                </button>
                            </div>
                        </summary>
                        <div className="p-4 border-t border-gray-200">
                            {owner.MikrotikDevices.length > 0 ? (
                                <div className="space-y-3">
                                    {owner.MikrotikDevices.map(device => (
                                        <div key={device.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 bg-gray-50 rounded-md gap-4">
                                            <div>
                                                <p className="font-semibold text-gray-900">{device.deviceName}</p>
                                                <p className="text-sm text-gray-500">{device.mikrotikIP}</p>
                                            </div>
                                            <div className="flex flex-row items-center justify-start gap-4">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${device.isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{device.isOnline ? 'Online' : 'Offline'}</span>
                                                <button onClick={() => navigate(`/manage/device/${device.id}`)} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md">Manage</button>
                                                <button onClick={() => handleDelete(device.id, device.deviceName)} className="p-2 text-red-500 hover:text-red-700 rounded-md flex items-center justify-center"><DeleteIcon /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : <p className="text-gray-500">This owner has no MikroTik devices.</p>}
                        </div>
                    </details>
                ))}
                <Modal isOpen={isAddModalOpen} onClose={handleCloseAddModal} title={`Add Device to ${selectedOwner?.username}`}>
                    <form onSubmit={handleSubmit(onSubmitAddDevice)}>
                        <div className="mb-4"><label className="block text-gray-700 text-sm font-bold mb-2">Device Name</label><input className="bg-gray-50 border border-gray-300 text-gray-900 rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline" {...register('deviceName', { required: true })} /></div>
                        <div className="mb-4"><label className="block text-gray-700 text-sm font-bold mb-2">MikroTik IP Address</label><input className="bg-gray-50 border border-gray-300 text-gray-900 rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline" {...register('mikrotikIP', { required: true })} /></div>
                        <div className="mb-4"><label className="block text-gray-700 text-sm font-bold mb-2">MikroTik Username</label><input className="bg-gray-50 border border-gray-300 text-gray-900 rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline" {...register('mikrotikUser', { required: true })} /></div>
                        <div className="mb-6"><label className="block text-gray-700 text-sm font-bold mb-2">MikroTik Password</label><input className="bg-gray-50 border border-gray-300 text-gray-900 rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline" type="password" {...register('mikrotikPass', { required: true })} /></div>
                        <div className="flex justify-end gap-4"><button type="button" onClick={handleCloseAddModal} className="px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-md">Cancel</button><button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">Add Device</button></div>
                    </form>
                </Modal>
            </div>
        </div>
    );
};


// --- OWNER PAGES ---
const OwnerDashboard = () => {
    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Enterprise Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-200 pb-5">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">My Network Assets</h1>
                    <p className="text-sm text-gray-500">Monitor and manage your connected MikroTik hardware and compliance status.</p>
                </div>
            </div>
            <MikrotikList />
        </div>
    );
};

const ProfilePage = () => {
    const { user } = useAuthStore();
    const [isChangePasswordModalOpen, setChangePasswordModalOpen] = useState(false);

    // Select the correct API and display role based on the logged-in user
    const api = user.role === 'admin' ? adminApi : ownerApi;
    const displayRole = user.role === 'admin' ? 'Administrator' : 'Vendo Owner';

    return (
        <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">My Profile</h1>
            <div className="bg-white p-6 rounded-lg shadow-md max-w-md">
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-500">Username</label>
                    <p className="text-lg text-gray-800">{user.username}</p>
                </div>
                
                <button
                    onClick={() => setChangePasswordModalOpen(true)}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md"
                >
                    Change Password
                </button>
            </div>
            <ChangePasswordModal 
                isOpen={isChangePasswordModalOpen} 
                onClose={() => setChangePasswordModalOpen(false)} 
                api={api} 
            />
        </div>
    );
};

const ChangePasswordModal = ({ isOpen, onClose, api }) => {
    const { register, handleSubmit, reset, watch, formState: { errors } } = useForm();
    const newPassword = watch("newPassword");

    const onSubmit = async (data) => {
        toast.promise(
            api.changePassword({ oldPassword: data.oldPassword, newPassword: data.newPassword }),
            {
                loading: 'Changing password...',
                success: (res) => {
                    reset();
                    onClose();
                    return res.message || 'Password changed successfully!';
                },
                error: (err) => err.response?.data?.message || 'Failed to change password.',
            }
        );
    };
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Change Password">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-4"><label className="block text-gray-700 text-sm font-bold mb-2">Old Password</label><input className="bg-gray-50 border border-gray-300 text-gray-900 appearance-none rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline" type="password" {...register('oldPassword', { required: true })} /></div>
                <div className="mb-4"><label className="block text-gray-700 text-sm font-bold mb-2">New Password</label><input className="bg-gray-50 border border-gray-300 text-gray-900 appearance-none rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline" type="password" {...register('newPassword', { required: true, minLength: 6 })} /></div>
                <div className="mb-6"><label className="block text-gray-700 text-sm font-bold mb-2">Confirm New Password</label><input className="bg-gray-50 border border-gray-300 text-gray-900 appearance-none rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline" type="password" {...register('confirmPassword', { required: true, validate: value => value === newPassword || "Passwords do not match" })} />{errors.confirmPassword && <p className="text-red-500 text-xs italic">{errors.confirmPassword.message}</p>}</div>
                <div className="flex justify-end gap-4"><button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-md">Cancel</button><button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">Save Changes</button></div>
            </form>
        </Modal>
    );
};
const AdminChangePasswordModal = ({ isOpen, onClose }) => {
    const { register, handleSubmit, reset, watch, formState: { errors } } = useForm();
    const newPassword = watch("newPassword");

    const onSubmit = async (data) => {
        toast.promise(
            adminApi.changePassword({ oldPassword: data.oldPassword, newPassword: data.newPassword }),
            {
                loading: 'Changing password...',
                success: (res) => {
                    reset();
                    onClose();
                    return res.message || 'Password changed successfully!';
                },
                error: (err) => err.response?.data?.message || 'Failed to change password.',
            }
        );
    };
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Change Password">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-4"><label className="block text-gray-700 text-sm font-bold mb-2">Old Password</label><input className="bg-gray-50 border border-gray-300 text-gray-900 appearance-none rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline" type="password" {...register('oldPassword', { required: true })} /></div>
                <div className="mb-4"><label className="block text-gray-700 text-sm font-bold mb-2">New Password</label><input className="bg-gray-50 border border-gray-300 text-gray-900 appearance-none rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline" type="password" {...register('newPassword', { required: true, minLength: 6 })} /></div>
                <div className="mb-6"><label className="block text-gray-700 text-sm font-bold mb-2">Confirm New Password</label><input className="bg-gray-50 border border-gray-300 text-gray-900 appearance-none rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline" type="password" {...register('confirmPassword', { required: true, validate: value => value === newPassword || "Passwords do not match" })} />{errors.confirmPassword && <p className="text-red-500 text-xs italic">{errors.confirmPassword.message}</p>}</div>
                <div className="flex justify-end gap-4"><button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-md">Cancel</button><button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">Save Changes</button></div>
            </form>
        </Modal>
    );
};

const AdminProfilePage = () => {
    const { user } = useAuthStore();
    const [isChangePasswordModalOpen, setChangePasswordModalOpen] = useState(false);

    return (
        <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">My Profile</h1>
            <div className="bg-white p-6 rounded-lg shadow-md max-w-md">
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-500">Username</label>
                    <p className="text-lg text-gray-800">{user.username}</p>
                </div>
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-500">Role</label>
                    <p className="text-lg text-gray-800">Administrator</p>
                </div>
                <button
                    onClick={() => setChangePasswordModalOpen(true)}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md"
                >
                    Change Password
                </button>
            </div>
            <AdminChangePasswordModal isOpen={isChangePasswordModalOpen} onClose={() => setChangePasswordModalOpen(false)} />
        </div>
    );
};




const MikrotikList = () => {
    const [mikrotiks, setMikrotiks] = useState([]);
    const [isModalOpen, setModalOpen] = useState(false);
    const [editingDevice, setEditingDevice] = useState(null);
    const [statuses, setStatuses] = useState({});
    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);
    const { register, handleSubmit, reset } = useForm();
    const navigate = useNavigate();

    const [isScriptModalOpen, setScriptModalOpen] = useState(false);
    const [selectedDeviceForScript, setSelectedDeviceForScript] = useState(null);
    const { register: registerScript, handleSubmit: handleScriptSubmit, reset: resetScriptForm } = useForm();
    
    const [isLogoModalOpen, setLogoModalOpen] = useState(false);
    const [selectedDeviceForLogo, setSelectedDeviceForLogo] = useState(null);
    const { register: registerLogo, handleSubmit: handleLogoSubmit, reset: resetLogoForm, watch: watchLogo } = useForm();

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Original fetch logic preserved for compliance and online status
    const fetchMikrotiksAndCompliance = useCallback(() => {
        ownerApi.getMikrotiks()
            .then(async (initialDevices) => {
                if (!initialDevices || initialDevices.length === 0) {
                    setMikrotiks([]);
                    return;
                }
                setMikrotiks(initialDevices.map(device => ({
                    ...device,
                    isCompliant: 'checking', 
                    isOnline: false 
                })));

                initialDevices.forEach(device => {
                    Promise.all([
                        ownerApi.getDeviceCompliance(device.id).catch(() => ({ isCompliant: false })),
                        pingerApi.pingNow({ type: 'mikrotik', id: device.id }).catch(() => ({ ...device, isOnline: false }))
                    ]).then(([complianceResult, pingResult]) => {
                        setMikrotiks(prevDevices => prevDevices.map(d => 
                            d.id === device.id 
                                ? { ...d, isCompliant: complianceResult.isCompliant, isOnline: pingResult.isOnline } 
                                : d
                        ));
                    });
                });
            })
            .catch(err => toast.error("Failed to load devices."));
    }, []);

    useEffect(() => { fetchMikrotiksAndCompliance(); }, [fetchMikrotiksAndCompliance]);

    // Background polling for RouterOS version and uptime
    useEffect(() => {
        const fetchAllStatuses = async () => {
            const onlineDevices = mikrotiks.filter(d => d.isOnline);
            if (onlineDevices.length === 0) return;

            const statusPromises = onlineDevices.map(d => 
                mikrotikApi.getStatus(d.id)
                    .then(s => ({ id: d.id, status: s }))
                    .catch(() => ({ id: d.id, status: { error: 'Failed' } }))
            );
            const results = await Promise.all(statusPromises);
            const newStatuses = results.reduce((acc, r) => { acc[r.id] = r.status; return acc; }, {});
            setStatuses(prev => ({ ...prev, ...newStatuses }));
        };
        const statusInterval = setInterval(fetchAllStatuses, 5000); 
        return () => clearInterval(statusInterval);
    }, [mikrotiks]);

    // Modal and Menu Handlers
    const handleCloseModal = () => setModalOpen(false);
    const handleCloseScriptModal = () => { setScriptModalOpen(false); setSelectedDeviceForScript(null); resetScriptForm(); };
    const handleCloseLogoModal = () => { setLogoModalOpen(false); setSelectedDeviceForLogo(null); resetLogoForm(); };

    const handleMenuToggle = (e, deviceId) => {
        e.stopPropagation();
        if (openMenuId === deviceId) {
            setOpenMenuId(null);
        } else {
            setAnchorRect(e.currentTarget.getBoundingClientRect());
            setOpenMenuId(deviceId);
        }
    };

    const handleOpenModal = (device = null) => {
        setEditingDevice(device);
        reset(device || { deviceName: '', mikrotikIP: '', mikrotikUser: '', mikrotikPass: '' });
        setModalOpen(true);
    };

    const onSubmit = async (data) => {
        const toastId = toast.loading('Saving device...');
        try {
            const promise = editingDevice ? ownerApi.updateMikrotik(editingDevice.id, data) : ownerApi.addMikrotik(data);
            await promise;
            toast.success('Device saved successfully!', { id: toastId });
            setModalOpen(false);
            fetchMikrotiksAndCompliance(); 
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to save device.", { id: toastId });
        }
    };

    const handleDelete = async (deviceId, deviceName) => {
        if (window.confirm(`Are you sure you want to delete device "${deviceName}"?`)) {
            toast.promise(ownerApi.deleteMikrotik(deviceId), {
                loading: 'Deleting...', success: () => { fetchMikrotiksAndCompliance(); return 'Deleted!'; }, error: 'Failed.'
            });
        }
    };

    const onScriptPush = async (data) => {
        if (!selectedDeviceForScript) return;

        handleCloseScriptModal(); // Close the modal immediately
        let isComplete = false;

        // 1. Start the toast with initial message
        const toastId = toast.loading('Initializing connection to device...');

        // 2. Define steps that mirror the backend operations
        const steps = [
            { message: 'Uploading core scripts (Login/Logout)...', delay: 2000 },
            { message: 'Configuring system services (SSH/FTP)...', delay: 2000 },
            { message: 'Applying firewall security rules...', delay: 1500 },
            { message: 'Setting up sales tracking & schedules...', delay: 1500 },
            { message: 'Installing offline sync engine...', delay: 2000 },
            { message: 'Updating SSL certificates...', delay: 2500 },
            { message: 'Finalizing configuration...', delay: 1000 }
        ];

        // 3. Run a progress loop in parallel with the API call
        const progressLoop = async () => {
            for (const step of steps) {
                if (isComplete) break;
                toast.loading(step.message, { id: toastId });
                await new Promise(resolve => setTimeout(resolve, step.delay));
            }
        };
        const progressPromise = progressLoop();

        try {
            // 4. Await the actual backend operation
            await ownerApi.pushMikrotikScripts(selectedDeviceForScript.id, data.model, data.scriptVariant);
            isComplete = true; // Stop the loop

            toast.success('Compliance scripts pushed successfully!', { id: toastId });
            fetchMikrotiksAndCompliance();

        } catch (err) {
            isComplete = true; // Stop the loop on error
            toast.error(err.response?.data?.message || 'Failed.', { id: toastId });
        }
    };

    const onLogoUpload = async (data) => {
        if (!selectedDeviceForLogo) return;
        const formData = new FormData();
        formData.append('model', data.model);
        if (data.logo1?.[0]) formData.append('logo1', data.logo1[0]);
        if (data.logo2?.[0]) formData.append('logo2', data.logo2[0]);

        toast.promise(ownerApi.uploadLogos(selectedDeviceForLogo.id, formData), {
            loading: 'Uploading...', success: (res) => { handleCloseLogoModal(); return 'Uploaded!'; }, error: 'Failed.'
        });
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                <button onClick={fetchMikrotiksAndCompliance} className="btn-secondary py-2 text-sm flex items-center gap-2">
                    <RefreshIcon className="w-4 h-4" /> Refresh
                </button>
                <button onClick={() => handleOpenModal()} className="btn-primary py-2 text-sm">
                    <AddIcon className="w-4 h-4" /> Add Device
                </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-visible">
                <div className="overflow-x-auto scrollbar-hide">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Device Asset</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Compliance</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Hardware</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-20 shadow-[-5px_0_5px_-5px_rgba(0,0,0,0.1)]">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {mikrotiks.map(device => (
                                <tr key={device.id} 
                                    className="hover:bg-gray-50 transition-colors cursor-pointer group"
                                    onClick={() => device.isOnline && navigate(`/manage/device/${device.id}`)}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-start gap-3">
                                            <div className={`h-10 w-10 rounded-xl flex items-center justify-center font-bold text-xs shrink-0 ${device.isOnline ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                                                <WifiIcon className="w-5 h-5" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-bold text-gray-900 truncate text-sm sm:text-base">{device.deviceName}</div>
                                                <div className="text-[10px] font-mono text-gray-400">{device.mikrotikIP}</div>
                                                
                                                {/* Stacked minimalistic status for mobile views */}
                                                <div className="flex flex-wrap items-center gap-2 mt-1.5 sm:hidden">
                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border ${device.isOnline ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                                                        {device.isOnline ? 'ONLINE' : 'OFFLINE'}
                                                    </span>
                                                    <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded border ${device.isCompliant === 'checking' ? 'bg-blue-50 text-blue-700 animate-pulse' : device.isCompliant ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`}>
                                                        {device.isCompliant === 'checking' ? 'AUDIT' : device.isCompliant ? 'SECURE' : 'NON-COMPLIANT'}
                                                    </span>
                                                    {device.isOnline && statuses[device.id] && !statuses[device.id].error && (
                                                        <span className="text-[9px] text-gray-400 font-mono">v{statuses[device.id].version}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    
                                    <td className="table-cell hidden sm:table-cell whitespace-nowrap"><span className={`badge ${device.isOnline ? 'badge-success' : 'badge-error'}`}>{device.isOnline ? 'ONLINE' : 'OFFLINE'}</span></td>
                                    <td className="table-cell hidden md:table-cell whitespace-nowrap"><span className={`badge ${device.isCompliant === 'checking' ? 'badge-info animate-pulse' : device.isCompliant ? 'badge-success' : 'badge-warning'}`}>{device.isCompliant === 'checking' ? 'AUDITING...' : device.isCompliant ? 'SECURE' : 'NON-COMPLIANT'}</span></td>
                                    <td className="table-cell hidden lg:table-cell whitespace-nowrap">
                                        {device.isOnline && statuses[device.id] && !statuses[device.id].error ? (
                                            <div className="text-[10px] leading-tight text-gray-500">
                                                <div className="font-semibold">v{statuses[device.id].version}</div>
                                                <div className="italic">Up: {statuses[device.id].uptime}</div>
                                            </div>
                                        ) : <span className="text-gray-300">--</span>}
                                    </td>

                                    <td className="px-4 py-3 whitespace-nowrap text-right sticky right-0 bg-white group-hover:bg-gray-50 z-20 shadow-[-5px_0_5px_-5px_rgba(0,0,0,0.1)]" onClick={(e) => e.stopPropagation()}>
                                        {/* Desktop direct action buttons */}
                                        <div className="hidden xl:flex justify-end gap-1.5">
                                            <button onClick={() => navigate(`/manage/device/${device.id}`)} disabled={!device.isOnline} title="Manage" className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-30"><WifiIcon className="w-4 h-4"/></button>
                                            <button onClick={() => { setSelectedDeviceForLogo(device); setLogoModalOpen(true); }} disabled={!device.isOnline} title="Logos" className="p-1.5 hover:bg-gray-100 text-gray-400 rounded-lg border border-gray-100"><ImageIcon className="w-4 h-4"/></button>
                                            <button onClick={() => { setSelectedDeviceForScript(device); setScriptModalOpen(true); }} disabled={!device.isOnline} title="Provision" className="p-1.5 hover:bg-gray-100 text-gray-400 rounded-lg border border-gray-100"><UploadIcon className="w-4 h-4"/></button>
                                            <button onClick={() => handleOpenModal(device)} title="Edit" className="p-1.5 hover:bg-gray-100 text-indigo-400 rounded-lg border border-gray-100"><EditIcon className="w-4 h-4"/></button>
                                            <button onClick={() => handleDelete(device.id, device.deviceName)} title="Delete" className="p-1.5 hover:bg-red-50 text-red-400 rounded-lg border border-red-50"><DeleteIcon className="w-4 h-4"/></button>
                                        </div>

                                        {/* Mobile action portal menu */}
                                        <div className="xl:hidden">
                                            <button onClick={(e) => handleMenuToggle(e, device.id)} className="p-2 hover:bg-gray-200 rounded-full text-gray-500"><MenuIcon className="w-5 h-5" /></button>
                                            <ActionPortalMenu isOpen={openMenuId === device.id} anchorRect={anchorRect} onClose={() => setOpenMenuId(null)}>
                                                <button onClick={() => navigate(`/manage/device/${device.id}`)} disabled={!device.isOnline} className="flex w-full items-center px-4 py-2 text-xs font-bold text-blue-600 hover:bg-blue-50 disabled:opacity-30"><WifiIcon className="mr-2 w-4 h-4"/> MANAGE</button>
                                                <button onClick={() => { setSelectedDeviceForLogo(device); setLogoModalOpen(true); setOpenMenuId(null); }} disabled={!device.isOnline} className="flex w-full items-center px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-30 text-left"><ImageIcon className="mr-2 w-4 h-4"/> LOGOS</button>
                                                <button onClick={() => { setSelectedDeviceForScript(device); setScriptModalOpen(true); setOpenMenuId(null); }} disabled={!device.isOnline} className="flex w-full items-center px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-30 text-left"><UploadIcon className="mr-2 w-4 h-4"/> PUSH SCRIPTS</button>
                                                <div className="border-t border-gray-100 my-1"></div>
                                                <button onClick={() => { handleOpenModal(device); setOpenMenuId(null); }} className="flex w-full items-center px-4 py-2 text-xs text-indigo-600 hover:bg-indigo-50 font-semibold text-left"><EditIcon className="mr-2 w-4 h-4"/> EDIT</button>
                                                <button onClick={() => { handleDelete(device.id, device.deviceName); setOpenMenuId(null); }} className="flex w-full items-center px-4 py-2 text-xs text-red-600 hover:bg-red-50 font-semibold text-left"><DeleteIcon className="mr-2 w-4 h-4"/> DELETE</button>
                                            </ActionPortalMenu>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Components for Device, Script, and Logo Identity */}
            <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingDevice?.id ? 'Update Device' : 'Register Asset'}>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div><label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Name</label><input className="input-class" {...register('deviceName', { required: true })} /></div>
                    <div><label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Host (IP)</label><input className="input-class font-mono" {...register('mikrotikIP', { required: true })} /></div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-xs font-bold text-gray-500 mb-1 uppercase">User</label><input className="input-class" {...register('mikrotikUser', { required: true })} /></div>
                        <div><label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Pass</label><input className="input-class" type="password" {...register('mikrotikPass', { required: !editingDevice?.id })} /></div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={handleCloseModal} className="btn-secondary text-xs">Cancel</button><button type="submit" className="btn-primary text-xs px-6 uppercase font-bold">Save</button></div>
                </form>
            </Modal>
            
           <Modal isOpen={isScriptModalOpen} onClose={handleCloseScriptModal} title="System Configuration">
                <form onSubmit={handleScriptSubmit(onScriptPush)} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Model</label>
                        <select className="input-class" {...registerScript('model', { required: true })}>
                            <MikrotikModelOptions />
                        </select>
                    </div>
                    {/* [NEW] Script Version Selection */}
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">RouterOS Version</label>
                        <select className="input-class" {...registerScript('scriptVariant')} defaultValue="">
                            <option value="">7.8 Above</option>
                            <option value="7.8">v7.8 Below</option>
                        </select>
                    </div>
                    <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={handleCloseScriptModal} className="btn-secondary text-sm">Cancel</button><button type="submit" className="btn-primary text-sm px-6 font-bold">Push Script</button></div>
                </form>
            </Modal>

            <Modal isOpen={isLogoModalOpen} onClose={handleCloseLogoModal} title="Asset Identity">
                <form onSubmit={handleLogoSubmit(onLogoUpload)} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Model</label>
                        <select className="input-class" {...registerLogo('model', { required: true })}>
                            <MikrotikModelOptions />
                        </select>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                        <div className="p-3 border rounded-lg bg-gray-50">
                            <label className="block text-xs font-bold text-gray-700 mb-2">Logo 1 (.jpg)</label>
                            <input type="file" accept=".jpg,.jpeg" className="text-xs" {...registerLogo('logo1')} />
                        </div>
                        <div className="p-3 border rounded-lg bg-gray-50">
                            <label className="block text-xs font-bold text-gray-700 mb-2">Logo 2 (.png)</label>
                            <input type="file" accept=".png" className="text-xs" {...registerLogo('logo2')} />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={handleCloseLogoModal} className="btn-secondary text-sm">Close</button><button type="submit" className="btn-primary text-sm px-6 uppercase">Upload</button></div>
                </form>
            </Modal>
        </div>
    );
};



// --- DEVICE MANAGEMENT (SHARED) ---
const MikrotikManagement = () => {
    const { mikrotikId } = useParams();
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const [device, setDevice] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const vouchersTabRef = useRef();
    const { useMemo } = React; // Ensure React is available for useMemo

    // Correctly select the API and back URL based on the user's role.
    const initialFetchApi = user.role === 'admin' ? adminApi : ownerApi;
    const backUrl = user.role === 'admin' ? '/admin/dashboard' : '/owner/dashboard';

    // Create a stable, memoized API object for the child tabs.
    // This prevents infinite re-rendering loops and selects the correct API (admin vs. owner).
    const deviceDataManager = useMemo(() => {
        const roleSpecificApis = user.role === 'admin' ? adminApi : ownerApi;
        // Merge the common mikrotik APIs with the role-specific ones.
        return { ...mikrotikApi, ...vendoApi, ...roleSpecificApis };
    }, [user.role]);

    useEffect(() => {
        setIsLoading(true);
        initialFetchApi.getMikrotikDevice(mikrotikId)
            .then(setDevice)
            .catch(err => {
                toast.error("Could not load device data.");
                navigate(backUrl);
            })
            .finally(() => setIsLoading(false));
    }, [mikrotikId, navigate, initialFetchApi, backUrl]);

    if (isLoading) return <p className="text-center text-gray-500">Loading Device Information...</p>;
    if (!device) return null;

    return (
        <DeviceContext.Provider value={{ device, api: deviceDataManager, vouchersTabRef }}>
            <div className="text-gray-800">
                <button onClick={() => navigate(backUrl)} className="mb-4 text-blue-600 hover:text-blue-800 font-semibold">&larr; Back to List</button>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Managing: {device.deviceName}</h1>
                    <button onClick={async () => {
                        toast.promise(mikrotikApi.syncVouchers(mikrotikId), {
                            loading: 'Syncing vouchers with MikroTik...',
                            success: (res) => {
                                if (vouchersTabRef.current?.fetchVouchers) {
                                    vouchersTabRef.current.fetchVouchers();
                                }
                                return `Sync complete: Added ${res.added}, Updated ${res.updated}, Deleted ${res.deleted}.`;
                            },
                            error: (err) => `Sync failed: ${err.response?.data?.message || err.message}`,
                        });
                    }} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181-3.181m0-4.992v4.992m0 0h-4.992m4.993 0-3.181 3.181m2.985-2.985h-4.992m0 0v4.992m0-4.992 3.181 3.181" /></svg>
                        Run Sync Now
                    </button>
                </div>
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                    <Outlet />
                </div>
            </div>
        </DeviceContext.Provider>
    );
};

// --- NESTED MANAGEMENT COMPONENTS ---
const ImagePreviewModal = ({ isOpen, onClose, imageUrl, themeName }) => {
    const [previewFailed, setPreviewFailed] = useState(false);

    useEffect(() => {
        setPreviewFailed(false);
    }, [imageUrl, isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex justify-center items-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center border-b p-4">
                    <h2 className="text-lg font-semibold text-gray-900">Preview: {themeName}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <CloseIcon />
                    </button>
                </div>
                <div className="p-4 bg-gray-200">
                    {previewFailed ? (
                        <div className="flex min-h-48 items-center justify-center rounded-md border border-dashed border-gray-400 bg-white px-6 text-center text-sm text-gray-600">
                            Preview image unavailable. Upload a preview image from Edit Theme and try again.
                        </div>
                    ) : (
                        <img
                            src={imageUrl}
                            alt={`Preview for ${themeName}`}
                            className="w-full h-auto object-contain rounded-md border"
                            onError={() => setPreviewFailed(true)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};


const ThemeManagementPage = () => {
    const { user } = useAuthStore();
    const isAdmin = user.role === 'admin';
    const api = isAdmin ? adminApi : ownerApi;

    const [themes, setThemes] = useState([]);
    const [mikrotiks, setMikrotiks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeployModalOpen, setDeployModalOpen] = useState(false);
    const [selectedTheme, setSelectedTheme] = useState(null);

    const { register: registerUpload, handleSubmit: handleUploadSubmit, reset: resetUploadForm, formState: { isSubmitting: isUploading } } = useForm();
    const { register: registerDeploy, handleSubmit: handleDeploySubmit, reset: resetDeployForm } = useForm();

    const fetchThemes = useCallback(() => {
        return api.getThemes().then(setThemes).catch(() => toast.error("Could not load themes."));
    }, [api]);

    useEffect(() => {
        setIsLoading(true);
        const promises = [fetchThemes()];
        const deviceApi = isAdmin ? adminApi.getAllMikrotiks : ownerApi.getMikrotiks;
        promises.push(deviceApi().then(setMikrotiks));
        
        Promise.all(promises).finally(() => setIsLoading(false));
    }, [isAdmin, fetchThemes]);

    // --- NEW: Helper function to determine card color from theme name ---
    const getThemeCardColor = (themeName) => {
        const name = themeName.toLowerCase();
        if (name.includes('red')) return 'bg-red-100 hover:bg-red-200';
        if (name.includes('gray') || name.includes('light')) return 'bg-slate-100 hover:bg-slate-200';
        if (name.includes('modern')) return 'bg-blue-100 hover:bg-blue-200';
        if (name.includes('dark')) return 'bg-gray-700 text-white hover:bg-gray-600';
        if (name.includes('green')) return 'bg-green-100 hover:bg-green-200';
        if (name.includes('yellow')) return 'bg-yellow-100 hover:bg-yellow-200';
        // Default color
        return 'bg-white hover:bg-gray-50';
    };

    const onUploadSubmit = async (data) => {
        const formData = new FormData();
        formData.append('name', data.name);
        formData.append('themeZip', data.themeZip[0]);

        await toast.promise(adminApi.uploadTheme(formData), {
            loading: 'Uploading theme...',
            success: (res) => {
                resetUploadForm();
                fetchThemes();
                return res.message;
            },
            error: (err) => err.response?.data?.message || 'Failed to upload theme.',
        });
    };

    const handleOpenDeployModal = (theme) => {
        setSelectedTheme(theme);
        resetDeployForm();
        setDeployModalOpen(true);
    };

    const handleDeleteTheme = async (theme) => {
        if (window.confirm(`Are you sure you want to permanently delete the theme "${theme.name}"?`)) {
            await toast.promise(adminApi.deleteTheme(theme.id), {
                loading: 'Deleting theme...',
                success: (res) => {
                    fetchThemes();
                    return res.message;
                },
                error: (err) => err.response?.data?.message || 'Failed to delete theme.',
            });
        }
    };

    const onDeploySubmit = async (data) => {
        if (!selectedTheme) return;
        const { mikrotikId, model } = data;
        await toast.promise(api.deployTheme(mikrotikId, { themeId: selectedTheme.id, model }), {
            loading: `Deploying '${selectedTheme.name}'...`,
            success: (res) => {
                setDeployModalOpen(false);
                return res.message;
            },
            error: (err) => err.response?.data?.message || 'Failed to deploy theme.',
        });
    };

    if (isLoading) return <LoadingDisplay message="Loading themes..." />;

    return (
        <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Hotspot Portal Themes</h1>

            {isAdmin && (
                <details className="bg-white p-4 rounded-lg shadow-md mb-8 open:ring-2 open:ring-blue-500 transition-shadow">
                    <summary className="font-semibold text-lg cursor-pointer">Upload New Theme</summary>
                    <div className="mt-4 border-t pt-4">
                        <form onSubmit={handleUploadSubmit(onUploadSubmit)} className="space-y-4 max-w-lg">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Theme Name</label>
                                <input {...registerUpload('name', { required: true })} placeholder="e.g., Modern Dark Theme" className="input-class w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Theme (.zip file)</label>
                                <input type="file" {...registerUpload('themeZip', { required: true })} accept=".zip" className="input-class w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                            </div>
                            <div className="text-right">
                                <button type="submit" className="btn-primary" disabled={isUploading}>{isUploading ? 'Uploading...' : 'Upload Theme'}</button>
                            </div>
                        </form>
                    </div>
                </details>
            )}

            <div>
                <h2 className="text-xl font-semibold mb-4 text-gray-800">Available Themes</h2>
                {themes.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {themes.map(theme => {
                            // --- MODIFICATION: Apply dynamic colors to each card ---
                            const cardColor = getThemeCardColor(theme.name);
                            return (
                                <div key={theme.id} className={`${cardColor} rounded-lg shadow-md flex flex-col justify-between overflow-hidden transition-colors duration-300`}>
                                    <div className="p-5">
                                        <h3 className="font-bold text-gray-900 text-lg">{theme.name}</h3>
                                    </div>
                                    <div className="bg-black bg-opacity-5 px-5 py-3 flex items-center justify-end gap-2">
                                        {isAdmin && (
                                            <button onClick={() => handleDeleteTheme(theme)} className="p-2 text-red-500 hover:text-red-700" title="Delete Theme">
                                                <DeleteIcon />
                                            </button>
                                        )}
                                        <button onClick={() => handleOpenDeployModal(theme)} className="btn-primary text-sm font-semibold">
                                            Deploy
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-10 bg-white rounded-lg shadow-md">
                        <p className="text-gray-500">No themes have been uploaded by the administrator.</p>
                    </div>
                )}
            </div>

            <Modal isOpen={isDeployModalOpen} onClose={() => setDeployModalOpen(false)} title={`Deploy Theme: ${selectedTheme?.name}`}>
                <form onSubmit={handleDeploySubmit(onDeploySubmit)}>
                    <p className="text-gray-600 mb-4">Select a device and its model to deploy this theme to. Protected files (logos, config.js, etc.) will not be overwritten.</p>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">MikroTik Device</label>
                            <select {...registerDeploy('mikrotikId', { required: true })} className="input-class w-full">
                                <option value="">-- Select a device --</option>
                                {mikrotiks.map(m => <option key={m.id} value={m.id}>{m.deviceName}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Device Model</label>
                            <select {...registerDeploy('model', { required: true })} className="input-class w-full">
                                <MikrotikModelOptions placeholder="-- Select model --" />
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-end gap-4 mt-6">
                        <button type="button" onClick={() => setDeployModalOpen(false)} className="btn-secondary">Cancel</button>
                        <button type="submit" className="btn-primary">Deploy Theme</button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

const VendoManagement = ({ vendo, onBack, api }) => {
    const [promos, setPromos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isPromoModalOpen, setPromoModalOpen] = useState(false);
    const [editingPromo, setEditingPromo] = useState(null);
    const { register: registerCreds, handleSubmit: handleCredsSubmit, reset: resetCredsForm } = useForm();
    const { register: registerPromo, handleSubmit: handlePromoSubmit, reset: resetPromo } = useForm();
    const MAX_PROMO_RATES = 7;
    
    const fetchSettings = useCallback(() => {
        setIsLoading(true);
        api.getFirmwareSettings(vendo.id)
            .then(settings => {
                setPromos(settings.promos || []);
                resetCredsForm({ adminUser: settings.adminUser, adminPass: '' });
            })
            .catch(err => toast.error("Could not fetch settings from firmware."))
            .finally(() => setIsLoading(false));
    }, [vendo.id, resetCredsForm, api]);
    useEffect(() => { fetchSettings(); }, [fetchSettings]);

    const handleOpenPromoModal = (promo = {}, index = -1) => {
        if (index === -1 && promos.length >= MAX_PROMO_RATES) {
            toast.error(`Maximum of ${MAX_PROMO_RATES} promo rates allowed.`);
            return;
        }
        setEditingPromo({ promo, index });
        resetPromo(promo);
        setPromoModalOpen(true);
    };
    const handleClosePromoModal = () => setPromoModalOpen(false);

    const onPromoSubmit = (data) => {
        const newPromos = [...promos];
        const formattedData = {
            name: data.name,
            price: parseInt(data.price, 10),
            minutes: parseInt(data.minutes, 10),
            validityDays: parseInt(data.validityDays, 10)
        };
        if (editingPromo.index > -1) { newPromos[editingPromo.index] = formattedData; }
        else { newPromos.push(formattedData); }
        setPromos(newPromos);
        handleClosePromoModal();
        toast.success("Promo rate updated locally. Click 'Save to Firmware' to apply changes.");
    };
    const handleDeletePromo = (indexToDelete) => {
        if (window.confirm('This will remove the promo. Click "Save to Firmware" to finalize.')) {
            setPromos(promos.filter((_, index) => index !== indexToDelete));
        }
    };

    const saveSettingsToFirmware = (credsData) => {
        const settingsToSave = {
            adminUser: credsData.adminUser,
            adminPass: credsData.adminPass,
            promos: promos
        };
        toast.promise(api.setFirmwareSettings(vendo.id, settingsToSave), {
            loading: 'Saving all settings to firmware...',
            success: 'Settings saved to firmware successfully!',
            error: (err) => err.response?.data?.message || 'Failed to save settings.',
        });
    };

    return (
        <div className="text-gray-800">
            <button onClick={onBack} className="mb-4 text-blue-600 hover:text-blue-800 font-semibold">&larr; Back to Vendo List</button>
            <h1 className="text-xl sm:text-2xl font-bold mb-6">Manage Firmware: {vendo.vendoName}</h1>
            <form onSubmit={handleCredsSubmit(saveSettingsToFirmware)}>
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                        <h2 className="text-xl font-semibold">Promo Rates</h2>
                        <button type="button" onClick={() => handleOpenPromoModal()} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm w-full sm:w-auto"><AddIcon /> Add Promo</button>
                    </div>
                    <div className="overflow-x-auto">
                        {isLoading ? <p className="p-4 text-gray-500">Loading settings...</p> : (<table className="min-w-full"><thead className="bg-gray-50"><tr><th className="px-4 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th><th className="px-4 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th><th className="px-4 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Minutes</th><th className="px-4 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Validity (Days)</th><th className="px-4 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th></tr></thead><tbody className="divide-y divide-gray-200">{promos.map((promo, index) => (<tr key={index}><td className="px-4 py-4 sm:px-6">{promo.name}</td><td className="px-4 py-4 sm:px-6">P{promo.price}</td><td className="px-4 py-4 sm:px-6">{promo.minutes}</td><td className="px-4 py-4 sm:px-6">{promo.validityDays}</td><td className="px-4 py-4 sm:px-6 flex items-center gap-4"><button type="button" onClick={() => handleOpenPromoModal(promo, index)} className="text-indigo-600 hover:text-indigo-800"><EditIcon /></button><button type="button" onClick={() => handleDeletePromo(index)} className="text-red-600 hover:text-red-800"><DeleteIcon /></button></td></tr>))}</tbody></table>)}
                    </div>
                </div>
                <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Firmware Admin Credentials</h2>
                    <div className="p-4 bg-white rounded-lg shadow-md max-w-md">
                        <div className="mb-4"><label className="block text-gray-700 text-sm font-bold mb-2">Admin Username</label><input className="bg-gray-50 border border-gray-300 text-gray-900 w-full p-2 rounded" {...registerCreds('adminUser')} /></div>
                        <div className="mb-6"><label className="block text-gray-700 text-sm font-bold mb-2">New Admin Password (optional)</label><input className="bg-gray-50 border border-gray-300 text-gray-900 w-full p-2 rounded" type="password" placeholder="Leave blank to keep unchanged" {...registerCreds('adminPass')} /></div>
                    </div>
                </div>
                <div className="mt-6 flex justify-end"><button type="submit" className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-semibold text-lg w-full sm:w-auto">Save All Changes to Firmware</button></div>
            </form>
            <Modal isOpen={isPromoModalOpen} onClose={handleClosePromoModal} title={editingPromo?.index > -1 ? 'Edit Promo' : 'Add New Promo'}>
                <form onSubmit={handlePromoSubmit(onPromoSubmit)}>
                    <div className="mb-4"><label className="block text-gray-700 text-sm font-bold mb-2">Name</label><input className="bg-gray-50 border border-gray-300 text-gray-900 w-full p-2 rounded" {...registerPromo('name', { required: true })} /></div>
                    <div className="mb-4"><label className="block text-gray-700 text-sm font-bold mb-2">Price</label><input className="bg-gray-50 border border-gray-300 text-gray-900 w-full p-2 rounded" type="number" {...registerPromo('price', { required: true })} /></div>
                    <div className="mb-4"><label className="block text-gray-700 text-sm font-bold mb-2">Minutes</label><input className="bg-gray-50 border border-gray-300 text-gray-900 w-full p-2 rounded" type="number" {...registerPromo('minutes', { required: true })} /></div>
                    <div className="mb-6"><label className="block text-gray-700 text-sm font-bold mb-2">Validity (Days)</label><input className="bg-gray-50 border border-gray-300 text-gray-900 w-full p-2 rounded" type="number" {...registerPromo('validityDays', { required: true })} /></div>
                    <div className="flex justify-end gap-4"><button type="button" onClick={handleClosePromoModal} className="px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-md">Cancel</button><button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">Confirm</button></div>
                </form>
            </Modal>
        </div>
    );
};
const ProgressBar = ({ value, color }) => {
    const colorClass = color || 'bg-blue-500';
    return (
        <div className="w-full bg-gray-600 rounded-full h-3">
            <div className={`${colorClass} h-3 rounded-full`} style={{ width: `${value}%` }}></div>
        </div>
    );
};

const SystemStatsPanel = ({ stats }) => {
    const formatBytes = (bytes = 0, decimals = 2) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    const freeMemoryPercent = stats.totalMemory ? (stats.freeMemory / stats.totalMemory) * 100 : 0;
    const freeHddPercent = stats.totalHdd ? (stats.freeHdd / stats.totalHdd) * 100 : 0;
    const tempPercent = Math.min(stats.temperature || 0, 100);

    return (
        <div className="bg-gray-800 text-white p-6 rounded-lg shadow-md mt-6">
            <h3 className="text-lg font-semibold text-center mb-4">{stats.boardName} - {stats.version}</h3>
            <div className="space-y-4 text-sm">
                <div>
                    <div className="flex justify-between mb-1">
                        <span className="font-medium text-gray-400">CPU Load</span>
                        <span>{stats.cpuLoad}%</span>
                    </div>
                    <ProgressBar value={stats.cpuLoad} color="bg-blue-500" />
                </div>
                <div>
                    <div className="flex justify-between mb-1">
                        <span className="font-medium text-gray-400">Free Memory</span>
                        <span>{formatBytes(stats.freeMemory)} / {formatBytes(stats.totalMemory)}</span>
                    </div>
                    <ProgressBar value={freeMemoryPercent} color="bg-red-500" />
                </div>
                <div>
                    <div className="flex justify-between mb-1">
                        <span className="font-medium text-gray-400">Free HDD</span>
                        <span>{formatBytes(stats.freeHdd)} / {formatBytes(stats.totalHdd)}</span>
                    </div>
                    <ProgressBar value={freeHddPercent} color="bg-green-500" />
                </div>
                {stats.temperature && (
                    <div>
                        <div className="flex justify-between mb-1">
                            <span className="font-medium text-gray-400">Temperature</span>
                            <span>{stats.temperature}°C</span>
                        </div>
                        <ProgressBar value={tempPercent} color="bg-yellow-500" />
                    </div>
                )}
                <div>
                    <span className="font-medium text-gray-400">Uptime: </span>
                    <span>{stats.uptime}</span>
                </div>
            </div>
        </div>
    );
};
// App.js

const VendoMachinesTab = () => {
    const { device, api } = useDevice();
    const [vendos, setVendos] = useState([]);
    const [isModalOpen, setModalOpen] = useState(false);
    const [editingVendo, setEditingVendo] = useState(null);
    const [managingVendo, setManagingVendo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [uptimes, setUptimes] = useState({});
    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);
    
    const [newVendoDetails, setNewVendoDetails] = useState({ vpnIp: null, availableAssignments: [] });
    const { register, handleSubmit, reset, setValue } = useForm();
    const navigate = useNavigate();

    const fetchVendos = useCallback(() => {
        api.getVendoMachines(device.id)
            .then(setVendos)
            .catch(console.error)
            .finally(() => setIsLoading(false));
    }, [device.id, api]);

    useEffect(() => {
        fetchVendos();
        const interval = setInterval(fetchVendos, 10000);
        return () => clearInterval(interval);
    }, [fetchVendos]);

    useEffect(() => {
        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        if (vendos.length > 0) {
            vendos.forEach(async (vendo) => {
                if (vendo.isOnline) {
                    try {
                        const data = await api.getUptime(vendo.id);
                        setUptimes(prev => ({ ...prev, [vendo.id]: data.uptime }));
                    } catch (error) {
                        setUptimes(prev => ({ ...prev, [vendo.id]: 'Error' }));
                    }
                }
            });
        }
    }, [vendos, api]);

    const handleMenuToggle = (e, vendoId) => {
        e.stopPropagation();
        if (openMenuId === vendoId) {
            setOpenMenuId(null);
        } else {
            setAnchorRect(e.currentTarget.getBoundingClientRect());
            setOpenMenuId(vendoId);
        }
    };

    const handleOpenModal = async (vendo = null) => {
        setEditingVendo(vendo);
        reset(vendo || { vendoName: 'New Vendo', firmwareIP: '', apiKey: '' });
        if (!vendo) {
            await toast.promise(api.getNewVendoDetails(device.id), {
                loading: 'Fetching network details...',
                success: (data) => { setNewVendoDetails(data); setModalOpen(true); return 'Ready!'; },
                error: (err) => err.response?.data?.message || 'Could not fetch details.',
            });
        } else {
            setModalOpen(true);
        }
    };

    const onSubmit = async (data) => {
        const toastId = toast.loading('Saving asset...');
        try {
            if (editingVendo) {
                await api.updateVendoMachine(editingVendo.id, data);
                await pingerApi.pingNow({ type: 'vendo', id: editingVendo.id });
            } else {
                const newVendo = await api.addVendoMachine(device.id, data);
                await api.updateVendoNameFromFirmware(newVendo.id);
            }
            toast.success('Asset synchronized!', { id: toastId });
            fetchVendos();
            setModalOpen(false);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed.', { id: toastId });
        }
    };

    const handleDelete = (vendo) => {
        if (window.confirm(`Delete Vendo '${vendo.vendoName}'?`)) {
            toast.promise(api.deleteVendoMachine(vendo.id), {
                loading: "Deleting...", success: () => { fetchVendos(); return "Deleted!"; }, error: "Failed."
            });
        }
    };

    const handleAssignmentChange = (e) => {
        const num = parseInt(e.target.value, 10);
        if (num && newVendoDetails.vpnIp) {
            setValue('firmwareIP', `${newVendoDetails.vpnIp}:${1109 + num}`);
        }
    };

    if (managingVendo) return <VendoManagement vendo={managingVendo} onBack={() => setManagingVendo(null)} api={api} />;

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                <h2 className="text-lg font-bold text-gray-900 hidden sm:block">Vendo Assets</h2>
                <button onClick={() => handleOpenModal()} className="btn-primary py-2 text-sm">
                    <AddIcon className="w-4 h-4" /> Add Vendo
                </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-visible">
                <div className="overflow-x-auto scrollbar-hide">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vendo Asset</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Uptime</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-20 shadow-[-5px_0_5px_-5px_rgba(0,0,0,0.1)]">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {vendos.map(vendo => (
                                <tr key={vendo.id} className="hover:bg-gray-50 transition-colors group">
                                    <td className="px-4 py-3">
                                        <div className="flex items-start gap-3">
                                            <div className={`h-10 w-10 rounded-xl flex items-center justify-center font-bold text-xs shrink-0 ${vendo.isOnline ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-400'}`}>
                                                <VendoIcon className="w-5 h-5" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-bold text-gray-900 truncate text-sm sm:text-base">{vendo.vendoName}</div>
                                                <div className="text-[10px] font-mono text-gray-400">{vendo.firmwareIP}</div>
                                                
                                                {/* Mobile Status Stack */}
                                                <div className="flex flex-wrap items-center gap-2 mt-1.5 sm:hidden">
                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border ${vendo.isOnline ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                                                        {vendo.isOnline ? 'ONLINE' : 'OFFLINE'}
                                                    </span>
                                                    {vendo.isOnline && (
                                                        <span className="text-[9px] text-gray-500 font-medium">Up: {uptimes[vendo.id] || '--'}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="table-cell hidden sm:table-cell whitespace-nowrap"><span className={`badge ${vendo.isOnline ? 'badge-success' : 'badge-error'}`}>{vendo.isOnline ? 'ONLINE' : 'OFFLINE'}</span></td>
                                    <td className="table-cell hidden md:table-cell whitespace-nowrap text-[10px] text-gray-500">{vendo.isOnline ? (uptimes[vendo.id] || 'Loading...') : 'N/A'}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-right sticky right-0 bg-white group-hover:bg-gray-50 z-20 shadow-[-5px_0_5px_-5px_rgba(0,0,0,0.1)]">
                                        <div className="hidden xl:flex justify-end gap-1.5">
                                            <button onClick={() => setManagingVendo(vendo)} className="px-3 py-1.5 bg-blue-600 text-white text-[10px] font-bold rounded-lg uppercase">Manage</button>
                                            <button onClick={() => handleOpenModal(vendo)} title="Edit" className="p-1.5 bg-gray-100 text-indigo-400 rounded-lg border border-gray-100"><EditIcon className="w-4 h-4"/></button>
                                            <button onClick={() => handleDelete(vendo)} title="Delete" className="p-1.5 bg-red-50 text-red-400 rounded-lg border border-red-50"><DeleteIcon className="w-4 h-4"/></button>
                                        </div>
                                        <div className="xl:hidden">
                                            <button onClick={(e) => handleMenuToggle(e, vendo.id)} className="p-2 hover:bg-gray-200 rounded-full text-gray-500"><MenuIcon className="w-5 h-5" /></button>
                                            <ActionPortalMenu isOpen={openMenuId === vendo.id} anchorRect={anchorRect} onClose={() => setOpenMenuId(null)}>
                                                <button onClick={() => setManagingVendo(vendo)} className="flex w-full items-center px-4 py-2 text-xs font-bold text-blue-600 hover:bg-blue-50"><SettingsIcon className="mr-2 w-4 h-4"/> MANAGE FIRMWARE</button>
                                                <div className="border-t border-gray-100 my-1"></div>
                                                <button onClick={() => { handleOpenModal(vendo); setOpenMenuId(null); }} className="flex w-full items-center px-4 py-2 text-xs text-indigo-600 hover:bg-indigo-50 font-semibold"><EditIcon className="mr-2 w-4 h-4"/> EDIT VENDO</button>
                                                <button onClick={() => { handleDelete(vendo); setOpenMenuId(null); }} className="flex w-full items-center px-4 py-2 text-xs text-red-600 hover:bg-red-50 font-semibold text-left"><DeleteIcon className="mr-2 w-4 h-4"/> DELETE VENDO</button>
                                            </ActionPortalMenu>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)} title={editingVendo?.id ? 'Update Vendo' : 'Register Vendo'}>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Name</label><input className="input-class" {...register('vendoName', { required: true })} /></div>
                    {!editingVendo && (
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Assignment</label>
                            <select className="input-class" onChange={handleAssignmentChange} defaultValue="">
                                <option value="" disabled>Select Assignment</option>
                                {newVendoDetails.availableAssignments.map(num => <option key={num} value={num}>{num}{num === 1 ? 'st' : num === 2 ? 'nd' : 'rd'} Vendo</option>)}
                            </select>
                        </div>
                    )}
                    <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">IP Host</label><input className="input-class" {...register('firmwareIP')} /></div>
                    <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">API Key</label><input className="input-class" {...register('apiKey')} /></div>
                    <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setModalOpen(false)} className="btn-secondary text-xs">Cancel</button><button type="submit" className="btn-primary text-xs px-6 uppercase font-bold">Save</button></div>
                </form>
            </Modal>
        </div>
    );
};

// Add this helper function before the component or inside App.js
const formatDuration = (minutes) => {
    const mins = parseInt(minutes, 10);
    if (isNaN(mins)) return '-';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
};

const VoucherPrintComponent = React.forwardRef(({ vouchers, deviceName }, ref) => {
    const voucherStyle = { 
        border: '1px solid #000', 
        padding: '1.5mm', 
        width: '36mm', 
        height: '21mm', 
        boxSizing: 'border-box', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'space-between', 
        backgroundColor: '#fff', 
        fontFamily: "'Arial', sans-serif", 
        color: '#000', 
        textAlign: 'center',
        overflow: 'hidden',
        margin: '0',
        position: 'relative' 
    };
    
    // Sequence Number Style - Matched to Header Size
    const sequenceStyle = {
        position: 'absolute',
        top: '1mm', 
        left: '1.5mm',
        fontSize: '6pt', // Adjusted to match brandStyle size
        fontWeight: 'bold',
        color: '#000',   
        lineHeight: '1',
        zIndex: 10
    };
    
    const brandStyle = { 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '2px', 
        fontWeight: 'bold', 
        fontSize: '7pt', // Same size as sequence number
        textTransform: 'uppercase',
        lineHeight: '1',
        marginBottom: '0.5mm',
        marginTop: '1mm' 
    };
    
    const codeStyle = { 
        fontWeight: '800', 
        fontSize: '12pt', 
        fontFamily: "'Verdana', sans-serif", 
        display: 'block', 
        margin: '0',
        letterSpacing: '1px',
        lineHeight: '1.1' 
    };
    
    const infoContainerStyle = { 
        display: 'flex', 
        justifyContent: 'space-between', 
        borderTop: '1px solid #ccc', 
        paddingTop: '1mm',
        marginTop: '0.5mm'
    };
    
    const infoBlockStyle = { textAlign: 'center', flex: 1 };
    const infoLabelStyle = { display: 'block', fontSize: '4.5pt', color: '#555', textTransform: 'uppercase', lineHeight: '1' };
    const infoValueStyle = { display: 'block', fontSize: '7pt', fontWeight: 'bold', lineHeight: '1.1' };

    return (
        <div ref={ref}>
            {vouchers.map((v, index) => (
                <div key={v.id} id={`voucher-${v.id}`} style={voucherStyle}>
                    {/* Sequence Number */}
                    <span style={sequenceStyle}>[{index + 1}]</span>
                    
                    <div style={brandStyle}>
                        <span>{deviceName.length > 12 ? deviceName.substring(0, 12) + '..' : deviceName}</span>
                    </div>
                    <div>
                        <span style={codeStyle}>{v.code}</span>
                    </div>
                    <div style={infoContainerStyle}>
                        <div style={infoBlockStyle}>
                            <span style={infoLabelStyle}>Price</span>
                            <span style={infoValueStyle}>P{v.price}</span>
                        </div>
                        <div style={infoBlockStyle}>
                            <span style={infoLabelStyle}>Time</span>
                            <span style={infoValueStyle}>{formatDuration(v.duration)}</span>
                        </div>
                        <div style={infoBlockStyle}>
                            <span style={infoLabelStyle}>Valid</span>
                            <span style={infoValueStyle}>{v.generatedValidityDays || '-'}d</span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
});


// Helper Form Component for Profiles
const UserProfileForm = ({ onSubmit, defaultValues }) => {
    const {
        register,
        handleSubmit,
        reset,
        formState: { isSubmitting }
    } = useForm({
        defaultValues: {
            name: "",
            uploadRate: "",
            downloadRate: "",
            sharedUsers: 1,
            ...defaultValues
        }
    });

    // 🔁 Reset form when switching between Create / Edit
    useEffect(() => {
        reset({
            name: defaultValues?.name || "",
            uploadRate: defaultValues?.uploadRate || "",
            downloadRate: defaultValues?.downloadRate || "",
            sharedUsers: defaultValues?.sharedUsers ?? 1
        });
    }, [defaultValues, reset]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

            {/* Profile Name */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profile Name
                </label>
                <input
                    type="text"
                    placeholder="e.g. VIP-10Mbps"
                    className="input-class w-full"
                    {...register("name", { required: true })}
                />
            </div>

            {/* Speed Limits */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Upload (Mbps)
                    </label>
                    <input
                        type="number"
                        min="1"
                        className="input-class w-full"
                        {...register("uploadRate", {
                            required: true,
                            valueAsNumber: true,
                            min: 1
                        })}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Download (Mbps)
                    </label>
                    <input
                        type="number"
                        min="1"
                        className="input-class w-full"
                        {...register("downloadRate", {
                            required: true,
                            valueAsNumber: true,
                            min: 1
                        })}
                    />
                </div>
            </div>

            {/* Shared Users */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shared Users
                </label>
                <input
                    type="number"
                    min="1"
                    max="100"
                    className="input-class w-full"
                    {...register("sharedUsers", {
                        required: true,
                        valueAsNumber: true,
                        min: 1
                    })}
                />
            </div>

            {/* Submit */}
            <div className="pt-4 text-right">
                <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "Saving..." : "Save Profile"}
                </button>
            </div>
        </form>
    );
};

const VouchersTab = () => {
    const { device, api, vouchersTabRef } = useDevice();
    const [vouchers, setVouchers] = useState([]);
    const [profiles, setProfiles] = useState([]); 
    const [vouchersToPrint, setVouchersToPrint] = useState([]);
    const [selectedVouchers, setSelectedVouchers] = useState(new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDeletingBatch, setIsDeletingBatch] = useState(false);
    const [isPdfGenerating, setIsPdfGenerating] = useState(false);
    const [isProfileModalOpen, setProfileModalOpen] = useState(false); 
    const [isSyncingProfiles, setIsSyncingProfiles] = useState(false);
    const [printLabel, setPrintLabel] = useState(''); // [NEW] State for custom print label

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [batches, setBatches] = useState([]);
    const [selectedBatch, setSelectedBatch] = useState('');
    const [editingProfile, setEditingProfile] = useState(null);
    const { register, handleSubmit, reset } = useForm();
    const printComponentRef = useRef();

    const handleSyncProfilesToDevice = async () => {
        if (!device?.id) return;

        const proceed = window.confirm(
            "Sync database profiles to MikroTik?\n\nOK = Continue\nCancel = Stop"
        );
        if (!proceed) return;

        const strict = window.confirm(
            "STRICT SYNC?\n\nOK = Also DELETE MikroTik profiles not in database (MoziFi-managed only).\nCancel = Only create/update (safe)."
        );

        await toast.promise(api.syncUserProfiles(device.id, strict), {
            loading: strict ? "Strict syncing profiles..." : "Syncing profiles...",
            success: (r) => r.message || "Sync complete!",
            error: (err) => err.response?.data?.message || "Sync failed."
        });

        fetchProfiles();
    };

    const fetchVouchers = useCallback(() => {
        setIsLoading(true);
        api.getVouchers(device.id, currentPage, selectedBatch)
            .then(data => {
                setVouchers(data.vouchers || []);
                setTotalPages(data.totalPages || 0);
                setSelectedVouchers(new Set());
            })
            .catch(console.error)
            .finally(() => setIsLoading(false));
    }, [device.id, api, currentPage, selectedBatch]);

    const fetchProfiles = useCallback(() => {
        api.getUserProfiles(device.id)
            .then(data => {
                setProfiles(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                toast.error("Failed to load user profiles.");
                setProfiles([]); 
            });
    }, [device.id, api]);

    const fetchBatches = useCallback(() => {
        api.getVoucherBatches(device.id)
            .then(data => setBatches(Array.isArray(data) ? data.filter(Boolean) : []))
            .catch(error => {
                console.error(error);
                setBatches([]);
            });
    }, [device.id, api]);
    
    useEffect(() => { 
        fetchVouchers(); 
        fetchBatches(); 
        fetchProfiles(); 
    }, [fetchVouchers, fetchBatches, fetchProfiles]);

    React.useImperativeHandle(vouchersTabRef, () => ({ fetchVouchers }));

    // [UPDATED] PDF Generation Logic with Prompt
    const handleDownloadPdf = async () => {
        if (isPdfGenerating) {
            toast.error("A PDF is already being generated. Please wait.");
            return;
        }

        // 1. Ask for the voucher label (default to device name)
        const customLabel = window.prompt("Enter voucher label (12 Chars Max):", device.deviceName);
        
        // 2. If user cancels (returns null), stop the process
        if (customLabel === null) return;

        setIsPdfGenerating(true);
        setPrintLabel(customLabel); // Store the custom label

        try {
            let printableData = [];
            if (selectedVouchers.size > 0) {
                printableData = vouchers.filter(v => selectedVouchers.has(v.id));
                if (printableData.length === 0) {
                    toast.error("Could not find data for selected vouchers.");
                    setIsPdfGenerating(false);
                    return;
                }
            } else {
                if (!selectedBatch) {
                    toast.error("Please select a specific Batch or check boxes to print.");
                    setIsPdfGenerating(false);
                    return;
                }
                printableData = await api.getPrintableVouchers(device.id, selectedBatch);
            }

            if (!printableData || printableData.length === 0) {
                toast.error('No unused vouchers found to print.');
                setIsPdfGenerating(false);
                return;
            }

            setVouchersToPrint(printableData);

            setTimeout(async () => {
                const toastId = toast.loading(`Preparing ${printableData.length} vouchers...`);
                try {
                    const pdf = new jsPDF('portrait', 'mm', 'a4');
                    
                    const VOUCHER_WIDTH_MM = 36;
                    const VOUCHER_HEIGHT_MM = 21;
                    const PAGE_WIDTH_MM = 210;
                    const PAGE_HEIGHT_MM = 297;
                    const MARGIN_MM = 10;
                    const GAP_MM = 2; 

                    const availWidth = PAGE_WIDTH_MM - (MARGIN_MM * 2);
                    const availHeight = PAGE_HEIGHT_MM - (MARGIN_MM * 2);

                    const VOUCHERS_PER_ROW = Math.floor((availWidth + GAP_MM) / (VOUCHER_WIDTH_MM + GAP_MM));
                    const VOUCHERS_PER_COL = Math.floor((availHeight + GAP_MM) / (VOUCHER_HEIGHT_MM + GAP_MM));
                    const VOUCHERS_PER_PAGE = VOUCHERS_PER_ROW * VOUCHERS_PER_COL;

                    const contentWidth = (VOUCHERS_PER_ROW * VOUCHER_WIDTH_MM) + ((VOUCHERS_PER_ROW - 1) * GAP_MM);
                    const contentHeight = (VOUCHERS_PER_COL * VOUCHER_HEIGHT_MM) + ((VOUCHERS_PER_COL - 1) * GAP_MM);
                    
                    const START_X = (PAGE_WIDTH_MM - contentWidth) / 2;
                    const START_Y = (PAGE_HEIGHT_MM - contentHeight) / 2;

                    let voucherCountOnPage = 0;

                    for (let i = 0; i < printableData.length; i++) {
                        if (i % 20 === 0) {
                            toast.loading(`Processing ${i + 1} of ${printableData.length}...`, { id: toastId });
                            await new Promise(resolve => setTimeout(resolve, 0));
                        }

                        const voucher = printableData[i];
                        const elementToCapture = document.getElementById(`voucher-${voucher.id}`);
                        if (!elementToCapture) continue;

                        if (voucherCountOnPage > 0 && voucherCountOnPage % VOUCHERS_PER_PAGE === 0) {
                            pdf.addPage();
                            voucherCountOnPage = 0;
                        }

                        const colIndex = voucherCountOnPage % VOUCHERS_PER_ROW;
                        const rowIndex = Math.floor(voucherCountOnPage / VOUCHERS_PER_ROW);
                        
                        const x = START_X + (colIndex * (VOUCHER_WIDTH_MM + GAP_MM));
                        const y = START_Y + (rowIndex * (VOUCHER_HEIGHT_MM + GAP_MM));

                        const canvas = await html2canvas(elementToCapture, { scale: 2, useCORS: true, logging: false });
                        const imgData = canvas.toDataURL('image/jpeg', 0.8);
                        pdf.addImage(imgData, 'JPEG', x, y, VOUCHER_WIDTH_MM, VOUCHER_HEIGHT_MM);
                        
                        voucherCountOnPage++;
                    }

                    // Use custom label in filename if available, else device name
                    const filenameLabel = customLabel.replace(/[^a-z0-9]/gi, '_'); 
                    const fileName = `${filenameLabel}-${selectedVouchers.size > 0 ? 'Selected' : selectedBatch}-vouchers.pdf`;
                    
                    if (Capacitor.isNativePlatform()) {
                        const pdfData = pdf.output('datauristring').split(',')[1];
                        await Filesystem.writeFile({ path: fileName, data: pdfData, directory: Directory.Documents });
                        toast.success(`PDF saved to Documents!`, { id: toastId });
                    } else {
                        pdf.save(fileName);
                        toast.success('PDF downloaded!', { id: toastId });
                    }
                } catch (innerError) {
                    toast.error(`Generation failed: ${innerError.message}`, { id: toastId });
                } finally {
                    setVouchersToPrint([]);
                    setIsPdfGenerating(false);
                }
            }, 2000); 
        } catch (error) {
            toast.error('Failed to retrieve vouchers.');
            setIsPdfGenerating(false);
        }
    };

    const handleSelectOne = (voucherId) => {
        const newSelection = new Set(selectedVouchers);
        if (newSelection.has(voucherId)) newSelection.delete(voucherId);
        else newSelection.add(voucherId);
        setSelectedVouchers(newSelection);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedVouchers(new Set(vouchers.map(v => v.id)));
        else setSelectedVouchers(new Set());
    };

    const onSubmit = async (data) => {
        if (!data.price || !data.count || parseInt(data.count, 10) <= 0) {
            toast.error("Please select a promo and quantity.");
            return;
        }
        setIsGenerating(true);
        try {
            await toast.promise(
                api.generateVouchers({ ...data, mikrotikDeviceId: device.id }), 
                { 
                    loading: `Generating ${data.count} vouchers...`, 
                    success: (res) => { 
                        fetchVouchers(); 
                        fetchBatches(); 
                        reset({ count: "10", validityDays: "7", price: "", duration: "", batchName: "", userProfile: "default" }); 
                        return res.message; 
                    }, 
                    error: (err) => err.response?.data?.message || "Failed to generate vouchers." 
                }
            );
        } finally {
            setIsGenerating(false);
        }
    };

    const deleteHotspotUserProfile = async (profileId) => {
        if (window.confirm("Delete this user profile? This will also remove it from the MikroTik hardware.")) {
            toast.promise(api.deleteUserProfile(profileId), {
                loading: 'Deleting profile...',
                success: () => { fetchProfiles(); return 'Profile deleted successfully.'; },
                error: (err) => err.response?.data?.message || 'Failed to delete profile.'
            });
        }
    };

    const handleBatchDelete = async () => {
        const voucherIds = Array.from(selectedVouchers);
        if (window.confirm(`Permanently delete ${voucherIds.length} selected vouchers?`)) {
            setIsDeleting(true);
            toast.promise(api.batchDeleteVouchers(voucherIds), {
                loading: 'Deleting vouchers...',
                success: () => { fetchVouchers(); fetchBatches(); return `${voucherIds.length} vouchers deleted.`; },
                error: 'Failed to delete selected vouchers.'
            }).finally(() => setIsDeleting(false));
        }
    };

    const handleDeleteEntireBatch = async () => {
        if (!selectedBatch || isDeletingBatch) return;

        const batchToDelete = selectedBatch;
        const confirmed = window.confirm(
            `Permanently delete every voucher in batch "${batchToDelete}" from the MikroTik and database?\n\nThis cannot be undone.`
        );
        if (!confirmed) return;

        setIsDeletingBatch(true);
        try {
            const result = await api.deleteVoucherBatch(device.id, batchToDelete);
            toast.success(result.message || `Batch "${batchToDelete}" deleted.`);
            setSelectedBatch('');
            setCurrentPage(1);
            setSelectedVouchers(new Set());
            fetchBatches();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete the voucher batch.');
        } finally {
            setIsDeletingBatch(false);
        }
    };

    const getVoucherStatus = (voucher) => {
        if (voucher.status === 'used') {
            if (voucher.validUntil && new Date(voucher.validUntil) < new Date()) {
                return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Expired</span>;
            }
            return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">Used</span>;
        }
        return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Unused</span>;
    };

    return (
        <div className="text-gray-800">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Hotspot User Profiles</h2>
                <button
                    onClick={handleSyncProfilesToDevice}
                    className="btn-secondary text-sm flex items-center gap-2"
                    disabled={isSyncingProfiles}
                    title="Sync DB profiles to router"
                >
                    <RefreshIcon className="w-4 h-4" />
                    {isSyncingProfiles ? "Syncing..." : "Sync to Device"}
                </button>

                <button
                    onClick={() => setProfileModalOpen(true)}
                    className="btn-primary text-sm flex items-center gap-2"
                >
                    <AddIcon className="w-4 h-4" /> Create Profile
                </button>
            </div>
            <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left font-semibold text-gray-500 uppercase">Profile Name</th>
                            <th className="px-6 py-3 text-left font-semibold text-gray-500 uppercase">Speed Limit</th>
                            <th className="px-6 py-3 text-left font-semibold text-gray-500 uppercase">Shared</th>
                            <th className="px-6 py-3 text-center font-semibold text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {Array.isArray(profiles) && profiles.map(p => (
                            <tr key={p.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 font-bold text-blue-600">{p.name}</td>
                                <td className="px-6 py-4">{p.uploadRate}M / {p.downloadRate}M</td>
                                <td className="px-6 py-4 font-medium">{p.sharedUsers}</td>
                                <td className="px-6 py-4 text-center space-x-2">
                                    <button
                                        onClick={() => { setEditingProfile(p); setProfileModalOpen(true); }}
                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Edit"
                                    >
                                        <EditIcon className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => deleteHotspotUserProfile(p.id)}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Delete"
                                    >
                                        <DeleteIcon className="w-5 h-5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {profiles.length === 0 && <tr><td colSpan="4" className="px-6 py-10 text-center text-gray-400 italic">No custom user profiles created.</td></tr>}
                    </tbody>
                </table>
            </div>
            <br></br>

            <h2 className="text-xl font-semibold mb-4">Generate Vouchers</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 items-end mb-8 p-4 bg-gray-50 rounded-lg">
                <div className="flex flex-col">
                    <label htmlFor="price" className="mb-1 text-sm font-medium text-gray-600">Price (₱)</label>
                    <input id="price" type="number" min="0" className="bg-white border border-gray-300 text-gray-900 p-2 rounded-md" {...register('price', { required: true, valueAsNumber: true })} />
                </div>
                <div className="flex flex-col">
                    <label htmlFor="duration" className="mb-1 text-sm font-medium text-gray-600">Duration (min)</label>
                    <input id="duration" type="number" min="1" className="bg-white border border-gray-300 text-gray-900 p-2 rounded-md" {...register('duration', { required: true, valueAsNumber: true, min: 1 })} />
                </div>
                <div className="flex flex-col">
                    <label htmlFor="validityDays" className="mb-1 text-sm font-medium text-gray-600">Validity (days)</label>
                    <input id="validityDays" type="number" defaultValue="7" className="bg-white border border-gray-300 text-gray-900 p-2 rounded-md" {...register('validityDays', { required: true, valueAsNumber: true })} />
                </div>
                
                <div className="flex flex-col">
                    <label htmlFor="userProfile" className="mb-1 text-sm font-medium text-gray-600">User Profile</label>
                    <select id="userProfile" className="bg-white border border-gray-300 text-gray-900 p-2 rounded-md" {...register('userProfile')}>
                        <option value="default">Default Profile</option>
                        {Array.isArray(profiles) && profiles.map(p => (
                            <option key={p.id} value={p.name}>{p.name}</option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col">
                    <label htmlFor="batchName" className="mb-1 text-sm font-medium text-gray-600">Batch Name</label>
                    <input id="batchName" type="text" placeholder="Optional" className="bg-white border border-gray-300 text-gray-900 p-2 rounded-md" {...register('batchName')} />
                </div>
                <div className="flex flex-col">
                    <label htmlFor="count" className="mb-1 text-sm font-medium text-gray-600">Count</label>
                    <input id="count" type="number" defaultValue="10" className="bg-white border border-gray-300 text-gray-900 p-2 rounded-md" {...register('count', { required: true, max: 100, valueAsNumber: true })} />
                </div>
                
                <button type="submit" className="btn-primary h-10 lg:col-span-6 mt-4 shadow-sm" disabled={isGenerating}>
                    {isGenerating ? 'Generating...' : 'Generate Vouchers'}
                </button>
            </form>

            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
                <h2 className="text-xl font-semibold">Voucher List</h2>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    <select
                        value={selectedBatch}
                        onChange={(e) => {
                            setSelectedBatch(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="bg-white border border-gray-300 text-gray-900 text-sm rounded-md p-2 h-10 min-w-[150px]"
                    >
                        <option value="">-- All Batches --</option>
                        {batches.map(batch => (<option key={batch} value={batch}>{batch}</option>))}
                    </select>
                    {selectedBatch && (
                        <button
                            type="button"
                            onClick={handleDeleteEntireBatch}
                            className="btn-secondary text-red-600 border-red-200 hover:bg-red-50 h-10 px-3 flex items-center gap-2"
                            disabled={isDeletingBatch || isDeleting}
                            title={`Delete every voucher in batch ${selectedBatch}`}
                        >
                            <DeleteIcon className="w-4 h-4" /> {isDeletingBatch ? 'Deleting...' : 'Delete Batch'}
                        </button>
                    )}
                    <button onClick={handleDownloadPdf} className="btn-secondary h-10 flex items-center gap-2 px-3" disabled={isPdfGenerating}>
                        <PrintIcon className="w-4 h-4" /> {isPdfGenerating ? '...' : 'PDF'}
                    </button>
                    {selectedVouchers.size > 0 && (
                        <button onClick={handleBatchDelete} className="btn-secondary text-red-600 border-red-200 hover:bg-red-50 h-10 px-3 flex items-center gap-2" disabled={isDeleting}>
                            <DeleteIcon className="w-4 h-4" /> ({selectedVouchers.size})
                        </button>
                    )}
                    <button onClick={() => { fetchVouchers(); fetchBatches(); }} className="btn-secondary h-10 p-2"><RefreshIcon className="w-4 h-4"/></button>
                </div>
            </div>

            <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm mb-12">
                {isLoading ? (
                    <div className="p-8 text-center text-gray-400">Loading vouchers...</div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left w-10">
                                    <input type="checkbox" onChange={handleSelectAll} checked={vouchers.length > 0 && selectedVouchers.size === vouchers.length} />
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Code</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Batch</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Profile</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Details</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {vouchers.map(v => (
                                <tr key={v.id} className={selectedVouchers.has(v.id) ? 'bg-blue-50/30' : 'hover:bg-gray-50'}>
                                    <td className="px-4 py-3"><input type="checkbox" checked={selectedVouchers.has(v.id)} onChange={() => handleSelectOne(v.id)} /></td>
                                    <td className="px-4 py-3 font-mono font-bold text-sm">{v.code}</td>
                                    <td className="px-4 py-3">{getVoucherStatus(v)}</td>
                                    <td className="px-4 py-3 text-xs text-gray-400">{v.batchName || '-'}</td>
                                    <td className="px-4 py-3 text-xs text-gray-600">{v.profile || 'default'}</td>
                                    <td className="px-4 py-3 text-xs text-gray-600">{`₱${v.price} for ${v.duration}min`}</td>
                                </tr>
                            ))}
                            {vouchers.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-gray-400">No vouchers found.</td></tr>}
                        </tbody>
                    </table>
                )}
            </div>
            
            <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

            <Modal
                isOpen={isProfileModalOpen}
                onClose={() => { setProfileModalOpen(false); setEditingProfile(null); }}
                title={editingProfile ? "Edit User Profile" : "Add User Profile"}
            >
                <UserProfileForm
                    defaultValues={editingProfile || undefined}
                    onSubmit={async (data) => {
                        if (editingProfile) {
                            await toast.promise(api.updateUserProfile(editingProfile.id, data), {
                                loading: 'Updating profile on Router...',
                                success: 'Profile updated!',
                                error: (err) => err.response?.data?.message || 'Failed.'
                            });
                        } else {
                            await toast.promise(api.createUserProfile(device.id, data), {
                                loading: 'Applying profile to Router...',
                                success: 'Profile created!',
                                error: (err) => err.response?.data?.message || 'Failed.'
                            });
                        }

                        fetchProfiles();
                        setProfileModalOpen(false);
                        setEditingProfile(null);
                    }}
                />
            </Modal>

            <div style={{ position: 'absolute', left: '-9999px', top: '0', zIndex: -1 }}>
                <VoucherPrintComponent ref={printComponentRef} vouchers={vouchersToPrint} deviceName={printLabel || device.deviceName} />
            </div>
        </div>
    );
};

const OwnerStatsDashboardPage = () => (
    <MainDashboard />
);

const UnauthenticatedHostsTable = ({ mikrotikId, api }) => {
    const [unauthHosts, setUnauthHosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchUnauthHosts = useCallback(async () => {
        try {
            const response = await api.getUnauthenticatedHosts(mikrotikId);
            setUnauthHosts(response);
        } catch (error) {
            console.error("Error fetching unauth hosts:", error);
        } finally {
            setIsLoading(false);
        }
    }, [mikrotikId, api]);

    const handleBypassMac = async (mac) => {
        if (window.confirm(`Bypass device with MAC: ${mac}?`)) {
            await toast.promise(
                api.bypassHostByMac(mikrotikId, mac),
                {
                    loading: 'Bypassing host...',
                    success: () => {
                        fetchUnauthHosts();
                        return `Host ${mac} bypassed successfully!`;
                    },
                    error: (err) => err.response?.data?.message || "Failed to bypass device."
                }
            );
        }
    };

    useEffect(() => {
        fetchUnauthHosts();
        const interval = setInterval(fetchUnauthHosts, 10000); // Refresh every 10s
        return () => clearInterval(interval);
    }, [fetchUnauthHosts]);

    if (isLoading) return <div className="p-4 text-center text-gray-500 text-sm">Loading pending hosts...</div>;

    return (
        <div className="mt-8 border-t pt-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Unauthenticated Hosts (Waiting to Login)</h2>
            <div className="overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-100">
                <table className="min-w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">IP Address</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">MAC Address</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Bridge</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {unauthHosts.length > 0 ? unauthHosts.map((host, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{host.address}</td>
                                <td className="px-4 py-4 whitespace-nowrap font-mono text-xs text-gray-600">{host['mac-address']}</td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{host.interface}</td>
                                <td className="px-4 py-4 whitespace-nowrap text-center">
                                    <button 
                                        onClick={() => handleBypassMac(host['mac-address'])}
                                        className="bg-green-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-green-700 transition-colors"
                                    >
                                        Bypass Device
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="4" className="p-8 text-center text-gray-400 text-sm italic">No pending hosts currently connected.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const HotspotManagerTab = () => {
    const { device, api } = useDevice();
    const [users, setUsers] = useState([]);
    const [bannedUsers, setBannedUsers] = useState([]);
    const [bypassedUsers, setBypassedUsers] = useState([]);
    const [unauthHosts, setUnauthHosts] = useState([]);
    const [failedLists, setFailedLists] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false); // Track refresh status

    const fetchUsers = useCallback(() => {
        // Show full loading spinner only if the list is currently empty
        if (users.length === 0) setIsLoading(true);
        setIsRefreshing(true); // Enable refreshing state for the button and table

        Promise.allSettled([
            api.getActiveUsers(device.id),
            api.getBannedUsers(device.id),
            api.getBypassedUsers(device.id),
            api.getUnauthenticatedHosts(device.id)
        ]).then(results => {
            const names = ['Active sessions', 'Banned users', 'Bypassed users', 'Unauthenticated hosts'];
            setFailedLists(names.filter((_, index) => results[index].status === 'rejected'));
            const [activeData, bannedData, bypassedData, unauthData] = results.map(result => result.value);
            // Normalize Binding entries (Banned/Bypassed)
            const normalizeBinding = (b) => ({
                ...b,
                bindingId: b['.id'] || b.id || b._id || b.bindingId || null,
                macAddress: (b['mac-address'] || b.macAddress || b.address || null),
            });

            const normalizedBanned = Array.isArray(bannedData) ? bannedData.map(normalizeBinding) : [];
            const normalizedBypassed = Array.isArray(bypassedData) ? bypassedData.map(normalizeBinding) : [];

            // Filter out MACs already in Bindings to keep lists clean
            const boundMacs = new Set([
                ...normalizedBanned.map(b => b.macAddress?.toUpperCase()),
                ...normalizedBypassed.map(b => b.macAddress?.toUpperCase())
            ]);

            const vendoIpPattern = /^10\.10\.\d+\.249$/;

            // Normalize and Filter Unauthenticated Hosts
            const normalizedUnauth = Array.isArray(unauthData) 
                ? unauthData
                    .map(h => ({
                        ...h,
                        macAddress: h['mac-address'] || h.macAddress || 'N/A',
                        bridgeName: h['interface'] || h.interface || 'N/A'
                    }))
                    .filter(h => {
                        const mac = h.macAddress?.toUpperCase();
                        const ipAddr = h.address || '';
                        return !boundMacs.has(mac) && !vendoIpPattern.test(ipAddr);
                    })
                : [];

            // Normalize Active Users using REST API structured data
            const normalizeActive = (u) => ({
                ...u,
                userId: u.userId || u['.id'] || u.id || u._id || null,
                macAddress: (u['mac-address'] || u.macAddress || u.address || null),
                // RESTORED: Capture sessionTimeLeft directly from the MikroTik REST response
                sessionTimeLeft: u['session-time-left'] || u.sessionTimeLeft || "Unlimited"
            });

            setUsers(Array.isArray(activeData) ? activeData.map(normalizeActive) : []);
            setBannedUsers(normalizedBanned);
            setBypassedUsers(normalizedBypassed);
            setUnauthHosts(normalizedUnauth);
        }).catch(err => {
            toast.error("Failed to load user lists.");
            console.error(err);
        }).finally(() => {
            setIsLoading(false);
            setIsRefreshing(false); // Reset refreshing status
        });
    }, [device.id, api, users.length]);

    useEffect(() => {
        fetchUsers();
        const interval = setInterval(fetchUsers, 30000);
        return () => clearInterval(interval);
    }, [fetchUsers]);

    const handleKick = async (userId, userName) => {
        toast.promise(api.kickUser(device.id, userId), {
            loading: `Kicking user '${userName}'...`,
            success: (res) => { fetchUsers(); return res.message || `User '${userName}' kicked!`; },
            error: (err) => err.response?.data?.message || "Failed to kick user."
        });
    };

    const handleBypassMac = async (mac) => {
        if (window.confirm(`Bypass device with MAC: ${mac}?`)) {
            await toast.promise(
                api.bypassHostByMac(device.id, mac),
                {
                    loading: 'Bypassing host...',
                    success: () => { fetchUsers(); return `Host ${mac} bypassed successfully!`; },
                    error: (err) => err.response?.data?.message || "Failed to bypass device."
                }
            );
        }
    };

    const handleBypass = async (userId, userName, macAddress) => {
        const displayName = (macAddress && macAddress.trim() !== "") ? macAddress : (userName || `user ${userId}`);
        toast.promise(api.bypassUser(device.id, userId, macAddress), {
            loading: `Bypassing user '${displayName}'...`,
            success: (res) => { fetchUsers(); return res.message || `User bypassed!`; },
            error: (err) => err.response?.data?.message || "Failed to bypass user."
        });
    };

    const handleBan = async (userId, userName) => {
        if (window.confirm(`Are you sure you want to ban '${userName}'?`)) {
            toast.promise(api.banUser(device.id, userId), {
                loading: `Banning user '${userName}'...`,
                success: (res) => { fetchUsers(); return res.message || `User banned!`; },
                error: (err) => err.response?.data?.message || "Failed to ban user."
            });
        }
    };

    const handleUnbypass = async (bindingId, macAddress) => {
        const displayName = macAddress || `entry ${bindingId}`;
        toast.promise(api.unbypassUser(device.id, bindingId), {
            loading: `Removing bypass for '${displayName}'...`,
            success: (res) => { fetchUsers(); return res.message || `Bypass removed!`; },
            error: (err) => err.response?.data?.message || "Failed to remove bypass."
        });
    };

    const handleUnban = async (bindingId, macAddress) => {
        const displayName = (macAddress && macAddress.trim() !== "") ? macAddress : `entry ${bindingId}`;
        toast.promise(api.unbanUser(device.id, bindingId), {
            loading: `Unbanning user '${displayName}'...`,
            success: (res) => { fetchUsers(); return res.message || `User unbanned!`; },
            error: (err) => err.response?.data?.message || "Failed to unban user."
        });
    };

    return (
        <div className="text-gray-800">
            {/* --- ACTIVE USERS TABLE --- */}
            {failedLists.length > 0 && (
                <p role="status" className="mb-4 rounded bg-amber-50 p-3 text-sm text-amber-900">
                    Could not load: {failedLists.join(', ')}. Check the router connection and refresh.
                </p>
            )}
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Active Hotspot Users</h2>
                <button 
                    onClick={() => fetchUsers()} 
                    disabled={isRefreshing}
                    className={`btn-secondary text-sm p-2 flex items-center gap-2 ${isRefreshing ? 'opacity-50' : ''}`}
                >
                    <RefreshIcon className={isRefreshing ? 'animate-spin' : ''} />
                    {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
                </button>
            </div>
            <div className="overflow-x-auto mb-8 bg-white rounded shadow min-h-[150px] relative">
                <table className="min-w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MAC Address</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uptime</th>
                            {/* RESTORED: Session Time Left Header */}
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session Time Left</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {isRefreshing && users.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-4 py-10 text-center text-gray-500 italic">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                                        Connecting to MikroTik REST API...
                                    </div>
                                </td>
                            </tr>
                        ) : users.length > 0 ? (
                            users.map(user => (
                                <tr key={user['.id']} className={isRefreshing ? 'opacity-40' : ''}>
                                    <td className="px-4 py-4 text-sm font-medium">{user.user}</td>
                                    <td className="px-4 py-4 text-sm">{user.address}</td>
                                    <td className="px-4 py-4 text-sm font-mono text-xs">{user.macAddress}</td>
                                    <td className="px-4 py-4 text-sm">{user.uptime}</td>
                                    {/* RESTORED: Display direct Session Time Left */}
                                    <td className="px-4 py-4 text-sm font-bold text-blue-600">{user.sessionTimeLeft}</td>
                                    <td className="px-4 py-4 text-center">
                                        <div className="flex justify-center gap-2">
                                            <button onClick={() => handleKick(user['.id'], user.user)} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Kick</button>
                                            <button onClick={() => handleBypass(user.userId || user['.id'], user.user, user.macAddress)} className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600">Bypass</button>
                                            <button onClick={() => handleBan(user['.id'], user.user)} className="px-2 py-1 text-xs bg-gray-700 text-white rounded hover:bg-gray-800">Ban</button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan="6" className="p-8 text-center text-gray-400">{failedLists.includes('Active sessions') ? 'Active sessions unavailable.' : 'No active sessions found.'}</td></tr>
                        )}
                    </tbody>
                </table>
                {isRefreshing && users.length > 0 && (
                    <div className="absolute inset-0 bg-white bg-opacity-10 flex items-center justify-center pointer-events-none">
                         <span className="bg-white px-3 py-1 rounded-full shadow-sm text-xs font-bold text-blue-600 animate-pulse border">Refreshing Data...</span>
                    </div>
                )}
            </div>

            {/* --- UNAUTHENTICATED HOSTS TABLE --- */}
            <h2 className="text-xl font-semibold mb-4 border-t pt-6 text-gray-800">Unauthenticated Hosts (Waiting to Login)</h2>
            <div className="overflow-x-auto mb-8 bg-white rounded shadow">
                <table className="min-w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MAC Address</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bridge</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {unauthHosts.length > 0 ? unauthHosts.map((host, idx) => (
                            <tr key={idx}>
                                <td className="px-4 py-4 text-sm">{host.address}</td>
                                <td className="px-4 py-4 text-sm font-mono text-xs">{host.macAddress}</td>
                                <td className="px-4 py-4 text-sm text-gray-500">{host.bridgeName}</td>
                                <td className="px-4 py-4 text-center">
                                    <button 
                                        onClick={() => handleBypassMac(host.macAddress)}
                                        className="bg-green-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-green-700 transition"
                                    >
                                        Bypass Device
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan="4" className="p-8 text-center text-gray-400">{failedLists.includes('Unauthenticated hosts') ? 'Pending hosts unavailable.' : 'No pending hosts found.'}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* --- BYPASSED USERS TABLE --- */}
            <h2 className="text-xl font-semibold mb-4 border-t pt-6">Bypassed Users</h2>
            <div className="overflow-x-auto mb-8 bg-white rounded shadow">
                <table className="min-w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MAC Address</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {bypassedUsers.map(user => (
                            <tr key={user.bindingId}>
                                <td className="px-4 py-4 text-sm font-mono">{user.macAddress}</td>
                                <td className="px-4 py-4 text-sm text-gray-600">{user.comment}</td>
                                <td className="px-4 py-4 text-center">
                                    <button onClick={() => handleUnbypass(user.bindingId, user.macAddress)} className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600">Remove Bypass</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- BANNED USERS TABLE --- */}
            <h2 className="text-xl font-semibold mb-4 border-t pt-6">Banned Users</h2>
            <div className="overflow-x-auto bg-white rounded shadow">
                <table className="min-w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MAC Address</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {bannedUsers.map(user => (
                            <tr key={user.bindingId}>
                                <td className="px-4 py-4 text-sm font-mono">{user.macAddress || 'N/A'}</td>
                                <td className="px-4 py-4 text-sm text-gray-600">{user.comment}</td>
                                <td className="px-4 py-4 text-center">
                                    <button onClick={() => handleUnban(user.bindingId, user.macAddress)} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Unban</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const HotspotUsersTab = () => {
    const { device, api } = useDevice();
    const [allUsers, setAllUsers] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; 

    // Fetch all users only once for fast local pagination
    const fetchAllUsers = useCallback(() => {
        setIsLoading(true);
        api.getHotspotUsers(device.id)
            .then(data => {
                const userList = data.users || [];
                const mappedUsers = userList.map(u => ({
                    ...u,
                    uptimeUsed: u.uptime,      // Mapping Consumed Uptime
                    timeLeft: u.limitUptime    // Mapping Total Limit
                }));
                setAllUsers(mappedUsers); 
            })
            .catch(err => toast.error("Failed to load hotspot users."))
            .finally(() => setIsLoading(false));
    }, [device.id, api]);

    useEffect(() => { 
        fetchAllUsers(); 
    }, [fetchAllUsers]);

    // Optimistic Delete Function
    const handleDelete = async (userName) => {
        if (!userName) return;
        if (window.confirm(`Are you sure you want to delete user: ${userName}?`)) {
            toast.promise(
                api.deleteHotspotUser(device.id, userName), 
                { 
                    loading: "Deleting user...", 
                    success: () => {
                        // Immediately update local state without a re-fetch
                        setAllUsers(prev => prev.filter(u => u.name !== userName));
                        return `User '${userName}' deleted!`;
                    }, 
                    error: (err) => err.response?.data?.message || "Failed to delete user."
                }
            );
        }
    };

    // Client-side pagination logic
    const totalPages = Math.ceil(allUsers.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentUsersPage = allUsers.slice(indexOfFirstItem, indexOfLastItem);

    return (
        <div className="text-gray-800">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">All Hotspot Users</h2>
                <button onClick={fetchAllUsers} className="btn-secondary text-sm p-2" title="Sync Refresh">
                    <RefreshIcon className="w-4 h-4" />
                </button>
            </div>

            <div className="overflow-x-auto">
                {isLoading ? (
                    <div className="p-8 text-center text-gray-500">Loading...</div>
                ) : (
                    <table className="min-w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Username</th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Profile</th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Uptime Used</th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Time Left</th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {currentUsersPage.map(user => (
                                <tr key={user.id || user.name} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-4 sm:px-6 whitespace-nowrap font-medium">{user.name}</td>
                                    <td className="px-4 py-4 sm:px-6 whitespace-nowrap">{user.profile}</td>
                                    <td className="px-4 py-4 sm:px-6 whitespace-nowrap">{user.uptimeUsed}</td>
                                    <td className="px-4 py-4 sm:px-6 whitespace-nowrap">{user.timeLeft}</td>
                                    <td className="px-4 py-4 sm:px-6 whitespace-nowrap">
                                        <button onClick={() => handleDelete(user.name)} className="text-red-600 hover:text-red-800 p-1">
                                            <DeleteIcon />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />
        </div>
    );
};



const DeviceWrapper = () => {
    const { user } = useAuthStore();
    const api = user.role === "admin" ? adminApi : ownerApi;
    const { mikrotikId } = useParams();

    return (
        <DeviceContext.Provider value={{ device: { id: mikrotikId }, api }}>
            <Outlet />
        </DeviceContext.Provider>
    );
};

const LiveSalesTab = () => {
    const { device, api } = useDevice();
    const [sales, setSales] = useState({ dailySales: 0, monthlySales: 0 });
    const [loading, setLoading] = useState(true);
    useEffect(() => { api.getSales(device.id).then(setSales).catch(console.error).finally(() => setLoading(false)); }, [device.id, api]);

    if (loading) return <p>Loading sales data...</p>;
    return (<div className="text-gray-800"><h2 className="text-xl font-semibold mb-4">Live Sales from MikroTik</h2><div className="grid grid-cols-1 md:grid-cols-2 gap-6"><div className="bg-gray-50 p-6 rounded-lg shadow-inner"><p className="text-gray-500">Today's Income</p><p className="text-3xl font-bold mt-2 text-gray-900">₱{sales.dailySales.toFixed(2)}</p></div><div className="bg-gray-50 p-6 rounded-lg shadow-inner"><p className="text-gray-500">This Month's Income</p><p className="text-3xl font-bold mt-2 text-gray-900">₱{sales.monthlySales.toFixed(2)}</p></div></div></div>);
};

// --- APP & RENDER ---
const App = () => {
    const { user } = useAuthStore();
return (
        <div className="mozifi-app">
            <Toaster position="top-right" toastOptions={{ style: { background: '#ffffff', color: '#101114', border: '1px solid #dfe3e8', borderRadius: '10px', boxShadow: '0 12px 30px rgba(16, 24, 40, 0.12)', fontSize: '13px' }, success: { iconTheme: { primary: '#10b981', secondary: 'white' } }, error: { iconTheme: { primary: '#ef4444', secondary: 'white' } }, }} />
            <Router>
                <Routes>
                
    {/* Public Login Route */}
                    <Route path="/login" element={<LoginPage />} />
                    {/* NEW Public Register Route */}
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
<Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                    {/* All Authenticated Routes Render Inside the MainLayout */}
                    <Route element={<MainLayout />}>
                  
      
                        {/* Admin-Only Routes */}
                        <Route element={<PrivateRoute allowedRoles={['admin']} />}>
                            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
        
                    <Route path="/admin/global-monitor" element={<GlobalMikrotikMonitor />} />
                            <Route path="/admin/promo-rates" element={<PromoManagementPage />} />
                            <Route path="/admin/pppoe" element={<PppoeManagementPage />} />
            <Route path="/admin/hotspot-coins-log" element={<HotspotLogsPage />} />
                <Route path="/admin/sales-log" element={<VendoSalesPage />} />
                            <Route path="/admin/sales-report" element={<SalesReportPage />} />
                            <Route path="/admin/backups" element={<BackupsPage />} />
                            <Route path="/admin/profile" element={<AdminProfilePage />} />
                
            <Route path="/admin/customization" element={<CustomizationPage />} />
            <Route path="/admin/enterprise-sales" element={<EnterpriseSalesReportPage />} />
    <Route path="/admin/enterprise-sales/:mikrotikId?" element={<EnterpriseSalesReportPage />} />
                        </Route>

                        {/* Owner-Only Routes */}
                        <Route element={<PrivateRoute allowedRoles={['vendo-owner']} />}>
      
                      <Route path="/owner/dashboard" element={<OwnerDashboard />} />
                      <Route path="/owner/enterprise-sales" element={<EnterpriseSalesReportPage />} />
    <Route path="/owner/enterprise-sales/:mikrotikId?" element={<EnterpriseSalesReportPage />} />
                            <Route path="/owner/stats-dashboard" element={<OwnerStatsDashboardPage />} />
                            <Route path="/owner/promo-rates" element={<PromoManagementPage />} />
          <Route path="/owner/hotspot-coins-log" element={<HotspotLogsPage />} />

                  <Route path="/owner/pppoe" element={<PppoeManagementPage />} />
                            <Route path="/owner/sales-log" element={<VendoSalesPage />} />
                            <Route path="/owner/sales-report" element={<SalesReportPage />} />
              <Route path="/owner/backups" element={<BackupsPage />} />
              <Route path="/owner/profile" element={<ProfilePage />} />
                            <Route path="/owner/customization" element={<CustomizationPage />} />
                        </Route>

                        {/* Shared 
Admin & Owner Routes */}
                        <Route element={<PrivateRoute allowedRoles={['admin', 'vendo-owner']} />}>
                            <Route path="/manage/device/:mikrotikId" element={<MikrotikManagement />}>
                                <Route index element={<Navigate to="vendos" replace 
/>} />
                                <Route path="vendos" element={<VendoMachinesTab />} />
                                <Route path="vouchers" element={<VouchersTab />} />
                           
     <Route path="active-users" element={<HotspotManagerTab />} />
                                <Route path="hotspot-users" element={<HotspotUsersTab />} />
                                <Route path="settings" element={<DeviceSettingsPage />} />
                               <Route path="hotspot-coins-log" element={<HotspotLogsPage />} />
             <Route path="telegram" element={<TelegramTab />} />
                                <Route path="auto-config" element={<AutoConfigTab />} />
                                <Route path="backups" element={<BackupsTab />} />
                            </Route>
                       
 </Route>

                    </Route>

                    {/* Redirects and Catch-All */}
                    <Route path="/" element={<HomeRedirect />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
     
           </Routes>
            </Router>
        </div>
    );
};

const rootElement = document.getElementById('root');
if (rootElement) {
    const root = ReactDOM.createRoot(rootElement);
    //root.render(<React.StrictMode><App /></React.StrictMode>);
}

export default App;
